/**
 * interior_archetypes.ts — THE FUNCTIONAL PROGRAMS (the interiors programme, slice B, 2026-10-01). PURE data + lookups.
 *
 * An ARCHETYPE says what a kind of building is FOR: per floor a PROGRAM of ZONES (stable semantic ids — never array
 * indices; a role; mandatory or optional; a minimum usable area; who it is for — a POLICY REQUEST, never authority;
 * what it should be next to and apart from; the enclosure it would like — `room` is recorded, not built, in wave 1),
 * the KITS each zone may take (interior_kits.ts), the ANCHORS it must end up with, and one or more ARRANGEMENTS — the
 * candidate ways to lay the program out, in a stable order. The planner (interior_planner.ts) tries the first K, keeps
 * the valid ones and ranks them; an archetype also names its SMALLER FALLBACK.
 *
 * SELECTION is by building KEY first (`deed:hotel` and `deed:cantina` are both `hospitality`; a museum, a bunker and
 * an outpost are all `civic`), purpose second. Re-authored in this wave: HOME (XS studio → L, distinct upper floors, a
 * spare room that is a rentable unit), CANTINA (one program — arrival / queue, social tables, counter + prep with a
 * staff side, a performance edge that drops first — in two construction traditions: The Radiant Cup's open axis, The
 * Frosthearth's sheltered bays), CLINIC (reception → waiting → treatment with a real bedside, supplies), SHOP (window
 * / browse / checkout / backstock), and the two LODGING archetypes the rentals slice needs (HOTEL, APARTMENT: units
 * upstairs). Every other purpose resolves to `legacy.<purpose>`: today's plan, slot for slot.
 *
 * AN ARRANGEMENT is data: `cuts` carve named rects out of the floor's base regions, `fills` seat kits at SITES (a
 * wall run, a free-standing row, a grid of islands, a spot) — each fill lists alternative kits (largest first) and
 * alternative sites (best first). Walls: N = the back wall (+Z), S = the door wall, E = +X, W = −X (the stairs' side);
 * `start` is the west / south end of a wall, `end` the east / north end. Base regions: `room` (the whole floor),
 * `main` (behind the door corridor's depth), `frontEast` / `frontWest` (the two pockets beside the door), `mainEast` /
 * `mainWest` (main, either side of the central axis), `east` / `west` (the whole depth either side of the axis), `unit`
 * (inside a unit program: the unit's own rect). A cut off `room` is cut off every one of them.
 *
 * Bump an archetype's `revision` on any change to its zones or arrangements: it is in the recipe and the digest.
 */
import type { InteriorPurpose } from './interior_purpose.js';
import type { AccessRole, AnchorKind, Enclosure } from './interior_descriptor.js';
import { kitReseatVersions } from './interior_kits.js';

export type Tradition = 'aurelian' | 'borean' | 'neutral';
/** XS < 30 m² usable · S 30–80 · M 80–200 · L above (report §2.2 — authoring defaults). */
export type SizeBand = 'XS' | 'S' | 'M' | 'L';
export const SIZE_BANDS: ReadonlyArray<readonly [SizeBand, number]> = [['XS', 30], ['S', 80], ['M', 200], ['L', Infinity]];
export function sizeBand(usableM2: number): SizeBand {
  for (const [b, max] of SIZE_BANDS) if (usableM2 < max) return b;
  return 'L';
}

export interface ZoneSpec {
  /** Stable semantic id (`sleep`, `counter`, `treatment`) — the first part of every slot id seated in it. */
  id: string;
  role: string;
  mandatory: boolean;
  /** Minimum usable area (m²) the floor must still have for the zone to be attempted. */
  minArea: number;
  adjacentTo: string[];
  separatedFrom: string[];
  access: AccessRole;
  enclosure: Enclosure;
  /** The anchors the zone must end up with (each reachable) to count as present. */
  requires: AnchorKind[];
  /** Emit / pack order: lower is seated first (pack 1 of a home is the bed and storage, not the plant). */
  priority: number;
  /** Optional zones drop highest first when the floor is short of area (the performance edge goes first). */
  dropOrder?: number;
}

export type WallName = 'N' | 'E' | 'S' | 'W';
export interface Site {
  /** A base region or a cut's `out` name. */
  in: string;
  /** wall = back on that wall of the region · row = free-standing, parallel to that wall, its back `off` m from it ·
   *  grid = islands across the region · spot = one kit at (u, v) of the region. */
  mode: 'wall' | 'row' | 'grid' | 'spot';
  wall?: WallName;
  align?: 'start' | 'center' | 'end';
  /** row: metres from the wall to the kit's back edge. */
  off?: number;
  /** grid / spot: quarter turns of the kit (0 faces +Z). */
  turn?: 0 | 1 | 2 | 3;
  u?: number; v?: number;
  /** grid: the aisle between islands (m) and the margin kept from the region's edges (m). */
  gap?: number; margin?: number;
  /** wall / row (slice F): the kit takes its exact aligned place or none — it never slides along the wall. */
  fixed?: boolean;
  /** wall / row (slice F): 'center' on a north / south wall means the DOOR's axis (x = 0), not the middle of the run. */
  onAxis?: boolean;
}
export interface Fill {
  zone: string;
  /** Alternatives, the preferred (usually the largest) first. */
  kits: string[];
  /** Alternatives, the best first. */
  sites: Site[];
  /** How many instances (a wall run or a grid repeats; default 1). */
  count?: number;
  /** Seat the WHOLE kit in a later pass: `secondary` (after the route is proven) or `detail` (lived-in, last). */
  pass?: 'secondary' | 'detail';
  /** Choose among `kits` by the pass's seed stream instead of taking the first that fits. */
  pick?: 'seed';
  /** Only on floors of these size bands (a bigger room earns MORE places, not a longer row of the same one). */
  bands?: SizeBand[];
  /** AREA-SCALED COUNT (slice F): one instance per `per` m² of usable floor — never fewer than `count`, never more
   *  than `max` (default 12). A big floor earns more bays, tables and desks; a small one keeps `count`. */
  per?: number; max?: number;
}
/** How many instances a fill asks for on a floor of `usableM2` (`count` when it names no `per`). */
export function fillCount(f: Fill, usableM2: number): number {
  const base = f.count ?? 1;
  if (!f.per || !(f.per > 0)) return base;
  return Math.max(base, Math.min(f.max ?? 12, Math.floor(usableM2 / f.per)));
}
export interface Cut { out: string; from: string; side: 'back' | 'front' | 'east' | 'west'; size: { min: number; max: number; frac: number } }
export interface Arrangement {
  id: string;
  /** Size bands it suits (all when absent). */
  bands?: SizeBand[];
  /** Traditions it belongs to (all when absent); `neutral` tries every one and lets the seed break ties. */
  traditions?: Tradition[];
  cuts?: Cut[];
  /** Zones that OWN cut rects (a zone not listed takes the bounds of what it seats). */
  zoneRects?: Record<string, string[]>;
  fills: Fill[];
  /** Only for rooms of this proportion: `wide` = at least 1.5× as wide as deep; `deep` = the reverse. */
  aspect?: 'wide' | 'deep';
}
/** RENTABLE UNITS along the east side of a floor, a corridor on the stairs' side (slice R). */
export interface UnitPlan {
  kind: 'room' | 'dwelling';
  /** A unit's depth along the wall (m): as many as fit at `min`, none deeper than `max`. */
  depth: { min: number; max: number };
  /** The corridor kept between the stairs and the units (m), and the widest a unit may be (m). */
  corridor: number; maxWidth: number;
  /** Fewer than this many units → no units on the floor (the program's own arrangements furnish it shared). */
  minUnits: number; maxUnits: number;
  zone: Omit<ZoneSpec, 'id' | 'priority'>;
  /** Fills seated INSIDE each unit (`in: 'unit'`). */
  fills: Fill[];
  /** (slice U, the storeys revision) Later-pass fills for the CORRIDOR the units leave (zone `landing`): a bench, a
   *  lamp, something green. Read only at layout ≥ STOREYS_LAYOUT_REVISION. */
  common?: Fill[];
}
export interface FloorProgram {
  id: string;
  zones: ZoneSpec[];
  arrangements: Arrangement[];
  units?: UnitPlan;
}
export interface InteriorArchetype {
  id: string; revision: number; label: string;
  ground: FloorProgram | null;
  /** `upper[i]` furnishes floor i + 1; the LAST entry repeats when `repeatUpper` (unit floors), else higher floors stay unprogrammed. */
  upper: FloorProgram[];
  repeatUpper?: boolean;
  /** How many floors it programs at most. */
  maxFloors: number;
  /** The smaller archetype tried when this one cannot be seated (`simpler` on the ladder). */
  fallback: string | null;
  /** A `legacy.<purpose>` archetype: the room is `purposePlan(purpose)` exactly. */
  legacy?: InteriorPurpose;
  /** The legacy plan the ladder's `legacy` rung falls to. */
  legacyPurpose: InteriorPurpose;
}

// ─── Small builders ──────────────────────────────────────────────────────────────────────────────────────────────────

const zone = (id: string, role: string, priority: number, o: Partial<ZoneSpec> = {}): ZoneSpec => ({
  id, role, priority, mandatory: false, minArea: 4, adjacentTo: [], separatedFrom: [], access: 'public', enclosure: 'zone', requires: [], ...o,
});
const wall = (inn: string, w: WallName, align: 'start' | 'center' | 'end' = 'start'): Site => ({ in: inn, mode: 'wall', wall: w, align });
const row = (inn: string, w: WallName, off: number, align: 'start' | 'center' | 'end' = 'center'): Site => ({ in: inn, mode: 'row', wall: w, off, align });
const grid = (inn: string, turn: 0 | 1 = 0, gap = 1.1, margin = 0.3): Site => ({ in: inn, mode: 'grid', turn, gap, margin });
const spot = (inn: string, u: number, v: number, turn: 0 | 1 | 2 | 3 = 0): Site => ({ in: inn, mode: 'spot', u, v, turn });
const fill = (z: string, kits: string[], sites: Site[], o: Partial<Fill> = {}): Fill => ({ zone: z, kits, sites, ...o });

// ─── HOME ────────────────────────────────────────────────────────────────────────────────────────────────────────────

const HOME_ZONES: ZoneSpec[] = [
  zone('sleep', 'sleeping', 0, { mandatory: true, minArea: 5, access: 'owner', requires: ['bed'], separatedFrom: ['arrival'], enclosure: 'screen' }),
  zone('store', 'personal storage', 1, { minArea: 1.5, access: 'owner', requires: ['storage'], adjacentTo: ['sleep'], dropOrder: 1 }),
  zone('cook', 'cooking', 2, { mandatory: true, minArea: 4, requires: ['work'], adjacentTo: ['eat'] }),
  zone('living', 'sitting', 3, { minArea: 2.5, requires: ['seat'], adjacentTo: ['arrival'], dropOrder: 2 }),
  zone('eat', 'eating', 4, { minArea: 9, requires: ['seat'], adjacentTo: ['cook'], dropOrder: 4 }),
  zone('study', 'study', 5, { minArea: 6, access: 'owner', requires: ['work'], dropOrder: 5 }),
  zone('arrival', 'arrival', 6, { minArea: 0 }),
];
const HOME_DETAIL: Fill[] = [
  fill('living', ['detail.hearth'], [wall('room', 'E', 'center'), wall('room', 'N', 'center')], { pass: 'detail' }),
  fill('arrival', ['detail.guestbook'], [wall('frontEast', 'S', 'start'), wall('frontWest', 'S', 'end')], { pass: 'detail' }),
  fill('living', ['detail.lamp', 'detail.plant'], [wall('room', 'S', 'end'), wall('room', 'E', 'start'), wall('room', 'N', 'start')], { pass: 'detail', pick: 'seed' }),
  fill('study', ['store.bookcase', 'detail.sidecase'], [wall('room', 'N', 'center'), wall('room', 'E', 'center')], { pass: 'detail', pick: 'seed' }),
  fill('arrival', ['detail.plant', 'detail.column'], [wall('room', 'S', 'start'), wall('room', 'W', 'end')], { pass: 'detail', pick: 'seed' }),
  // A BIGGER HOUSE HAS MORE PLACES (M / L): a reading corner, a bookcase, a sideboard by the table, green in the corners
  fill('living', ['seat.reading', 'seat.chair'], [wall('room', 'W', 'center'), wall('room', 'E', 'center'), wall('room', 'N', 'center'), wall('room', 'S', 'start')], { pass: 'secondary', bands: ['M', 'L'] }),
  fill('store', ['store.bookcase', 'store.cabinet'], [wall('room', 'N', 'center'), wall('room', 'W', 'start'), wall('room', 'E', 'center')], { pass: 'secondary', bands: ['M', 'L'] }),
  fill('eat', ['store.cabinet', 'store.shelf'], [wall('room', 'W', 'center'), wall('room', 'E', 'center'), wall('room', 'N', 'center')], { pass: 'secondary', bands: ['M', 'L'] }),
  fill('living', ['detail.plant'], [wall('room', 'W', 'end'), wall('room', 'E', 'start'), wall('room', 'N', 'end'), wall('room', 'W', 'start')], { pass: 'detail', count: 2, bands: ['M', 'L'] }),
  fill('sleep', ['detail.mirror', 'detail.lamp'], [wall('room', 'N', 'center'), wall('room', 'E', 'center'), wall('room', 'S', 'end')], { pass: 'detail', pick: 'seed', bands: ['M', 'L'] }),
];
/** (slice F, revision 2) A BIG single room stops reading empty: a second place to sit in the middle of the floor, a
 *  screen at the foot of the bed (the room divided, never sealed), more storage and books on the long walls, lived-in
 *  clusters off the routes. Medium and large floors only, every one a LATER pass — the primary pieces and their anchors
 *  never move for them, and a small home is the room it was. */
const homeMore = (bedWall: WallName, bedAlign: 'start' | 'end'): Fill[] => [
  fill('sleep', ['screen.single'], [row('room', bedWall, 4.3, bedAlign)], { pass: 'secondary', bands: ['L'] }),
  fill('living', ['lounge.island'], [spot('main', 0.5, 0.74), spot('main', 0.34, 0.72), spot('main', 0.66, 0.72)], { pass: 'secondary', bands: ['L'] }),
  fill('living', ['seat.reading'], [wall('room', 'W', 'start'), wall('room', 'E', 'center'), wall('room', 'N', 'center')], { pass: 'secondary', bands: ['M', 'L'], count: 1, per: 160, max: 3 }),
  fill('eat', ['table.pair'], [grid('mainEast', 1, 1.8, 1.4), grid('mainWest', 1, 1.8, 1.4)], { pass: 'secondary', bands: ['L'], count: 1, per: 220, max: 2 }),
  fill('study', ['store.bookcase'], [wall('room', 'W', 'end'), wall('room', 'E', 'center'), wall('room', 'N', 'center')], { pass: 'secondary', bands: ['M', 'L'], count: 1, per: 110, max: 4 }),
  fill('store', ['store.cabinet', 'store.chest'], [wall('room', 'W', 'start'), wall('room', 'E', 'start'), wall('room', 'N', 'start')], { pass: 'secondary', bands: ['M', 'L'], count: 1, per: 150, max: 3 }),
  fill('living', ['detail.plant', 'detail.lamp', 'detail.sidecase'], [wall('room', 'W', 'center'), wall('room', 'S', 'start'), wall('room', 'N', 'center'), wall('room', 'E', 'end')], { pass: 'detail', pick: 'seed', bands: ['M', 'L'], count: 2, per: 90, max: 5 }),
  fill('eat', ['detail.rug'], [spot('main', 0.5, 0.42)], { pass: 'detail', bands: ['L'] }),
];
const HOME_GROUND: FloorProgram = {
  id: 'home.ground', zones: HOME_ZONES,
  arrangements: [
    { id: 'west-bed', fills: [ // the bed by the door wall's west end, the galley down the east wall, sitting at the back
      fill('sleep', ['bed.double', 'bed.single'], [wall('room', 'S', 'start'), wall('room', 'N', 'end'), wall('room', 'S', 'end')]),
      fill('store', ['store.wardrobe', 'store.chest'], [wall('room', 'N', 'start'), wall('room', 'W', 'end'), wall('room', 'E', 'end')]),
      fill('cook', ['kitchen.galley'], [wall('room', 'E', 'start'), wall('room', 'N', 'end'), wall('room', 'N', 'start')]),
      fill('living', ['lounge.set', 'seat.reading', 'seat.chair'], [wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'S', 'end')]),
      fill('eat', ['table.dining', 'table.pair'], [spot('main', 0.5, 0.42), spot('main', 0.68, 0.5, 1), spot('main', 0.3, 0.5, 1)]),
      fill('study', ['desk.study'], [wall('room', 'S', 'end'), wall('room', 'E', 'center')], { pass: 'secondary' }),
      ...HOME_DETAIL,
      ...homeMore('S', 'start'),
    ] },
    { id: 'back-bed', fills: [ // the bed in the back corner, the galley on the back wall, sitting by the door
      fill('sleep', ['bed.double', 'bed.single'], [wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'S', 'end')]),
      fill('store', ['store.wardrobe', 'store.chest'], [wall('room', 'E', 'end'), wall('room', 'N', 'center'), wall('room', 'E', 'center')]),
      fill('cook', ['kitchen.galley'], [wall('room', 'N', 'start'), wall('room', 'E', 'start')]),
      fill('living', ['lounge.set', 'seat.reading', 'seat.chair'], [wall('room', 'S', 'end'), wall('room', 'E', 'start'), wall('room', 'S', 'start')]),
      fill('eat', ['table.dining', 'table.pair'], [spot('main', 0.42, 0.4), spot('main', 0.5, 0.5, 1)]),
      fill('study', ['desk.study'], [wall('room', 'S', 'start'), wall('room', 'E', 'center')], { pass: 'secondary' }),
      ...HOME_DETAIL,
      ...homeMore('N', 'end'),
    ] },
    { id: 'east-bed', fills: [ // the bed by the door wall's east end, the galley at the back, sitting on the west
      fill('sleep', ['bed.double', 'bed.single'], [wall('room', 'S', 'end'), wall('room', 'E', 'start'), wall('room', 'N', 'end')]),
      fill('store', ['store.wardrobe', 'store.chest'], [wall('room', 'E', 'center'), wall('room', 'N', 'end')]),
      fill('cook', ['kitchen.galley'], [wall('room', 'N', 'end'), wall('room', 'N', 'start'), wall('room', 'E', 'end')]),
      fill('living', ['lounge.set', 'seat.reading', 'seat.chair'], [wall('room', 'N', 'start'), wall('room', 'S', 'start'), wall('room', 'W', 'end'), wall('room', 'E', 'end')]),
      fill('eat', ['table.dining', 'table.pair'], [spot('main', 0.5, 0.45), spot('main', 0.35, 0.5, 1)]),
      fill('study', ['desk.study'], [wall('room', 'E', 'end'), wall('room', 'S', 'start')], { pass: 'secondary' }),
      ...HOME_DETAIL,
      ...homeMore('S', 'end'),
    ] },
  ],
};
/** Floor 1: the bedrooms — the main bed, and a SPARE ROOM that is a rentable unit (slice R). */
/** (slice F, revision 2) A big sleeping floor: a sitting group in the open floor, a screen between the two beds, a
 *  dressing wall that grows with the floor. Later passes, medium and large floors only. */
const SLEEP_MORE: Fill[] = [
  fill('nook', ['lounge.island'], [spot('main', 0.5, 0.5), spot('main', 0.4, 0.6)], { pass: 'secondary', bands: ['L'] }),
  fill('nook', ['lounge.set', 'seat.reading'], [wall('room', 'S', 'start'), wall('room', 'E', 'center'), wall('room', 'N', 'center')], { pass: 'secondary', bands: ['M', 'L'], count: 1, per: 140, max: 3 }),
  fill('dress', ['store.wardrobe', 'store.cabinet'], [wall('room', 'N', 'center'), wall('room', 'E', 'center'), wall('room', 'N', 'start')], { pass: 'secondary', bands: ['M', 'L'], count: 1, per: 100, max: 4 }),
  fill('spare', ['screen.divider', 'screen.single'], [row('room', 'S', 4.6, 'end'), row('room', 'E', 4.6, 'start')], { pass: 'secondary', bands: ['L'] }),
  fill('nook', ['desk.study'], [wall('room', 'E', 'start'), wall('room', 'S', 'start')], { pass: 'secondary', bands: ['L'] }),
  fill('nook', ['detail.plant', 'detail.lamp', 'detail.sidecase'], [wall('room', 'S', 'end'), wall('room', 'N', 'end'), wall('room', 'E', 'end')], { pass: 'detail', pick: 'seed', bands: ['M', 'L'], count: 2, per: 100, max: 4 }),
];
const HOME_SLEEP: FloorProgram = {
  id: 'home.sleeping',
  zones: [
    zone('bedroom', 'main bedroom', 0, { mandatory: true, minArea: 6, access: 'owner', requires: ['bed'], enclosure: 'room' }),
    zone('dress', 'dressing', 1, { access: 'owner', requires: ['storage'], adjacentTo: ['bedroom'], dropOrder: 2 }),
    zone('spare', 'spare room', 2, { minArea: 14, access: 'guarded', requires: ['bed'], separatedFrom: ['bedroom'], enclosure: 'room', dropOrder: 3 }),
    zone('nook', 'reading nook', 3, { requires: ['seat'], dropOrder: 1 }),
  ],
  arrangements: [
    { id: 'north-bed', fills: [
      fill('bedroom', ['bed.double', 'bed.single'], [wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'N', 'center')]),
      fill('dress', ['store.wardrobe', 'store.chest'], [wall('room', 'N', 'start'), wall('room', 'E', 'center'), wall('room', 'N', 'center')]),
      fill('spare', ['bed.single'], [wall('room', 'S', 'end'), wall('room', 'E', 'start')]),
      fill('spare', ['store.chest'], [wall('room', 'E', 'start'), wall('room', 'S', 'end')], { pass: 'secondary' }),
      fill('nook', ['seat.reading', 'seat.chair'], [wall('room', 'E', 'center'), wall('room', 'N', 'center'), wall('room', 'S', 'start')]),
      fill('dress', ['detail.mirror'], [wall('room', 'N', 'center'), wall('room', 'E', 'center')], { pass: 'detail' }),
      fill('bedroom', ['detail.rug'], [spot('main', 0.6, 0.55)], { pass: 'detail' }),
      fill('nook', ['detail.lamp', 'detail.plant'], [wall('room', 'S', 'start'), wall('room', 'E', 'start')], { pass: 'detail', pick: 'seed' }),
      ...SLEEP_MORE,
    ] },
    { id: 'east-bed', fills: [
      fill('bedroom', ['bed.double', 'bed.single'], [wall('room', 'E', 'end'), wall('room', 'N', 'end')]),
      fill('dress', ['store.wardrobe', 'store.chest'], [wall('room', 'N', 'center'), wall('room', 'N', 'start')]),
      fill('spare', ['bed.single'], [wall('room', 'S', 'end'), wall('room', 'E', 'start')]),
      fill('spare', ['store.chest'], [wall('room', 'E', 'start'), wall('room', 'S', 'end')], { pass: 'secondary' }),
      fill('nook', ['seat.reading', 'seat.chair'], [wall('room', 'N', 'start'), wall('room', 'S', 'start')]),
      fill('dress', ['detail.mirror'], [wall('room', 'N', 'start'), wall('room', 'E', 'center')], { pass: 'detail' }),
      fill('bedroom', ['detail.rug'], [spot('main', 0.55, 0.5)], { pass: 'detail' }),
      fill('nook', ['detail.lamp', 'detail.plant'], [wall('room', 'S', 'start'), wall('room', 'N', 'center')], { pass: 'detail', pick: 'seed' }),
      ...SLEEP_MORE,
    ] },
  ],
};
/** Floor 2: the study — work, books, a quiet seat. */
/** (slice F, revision 2) A big study: the library runs on, a table to work at in the open floor. */
const STUDY_MORE: Fill[] = [
  fill('library', ['store.bookcase'], [wall('room', 'N', 'start'), wall('room', 'E', 'center'), wall('room', 'S', 'end')], { pass: 'secondary', bands: ['M', 'L'], count: 1, per: 70, max: 6 }),
  fill('den', ['table.pair', 'lounge.island'], [spot('main', 0.5, 0.5, 1), spot('main', 0.4, 0.6)], { pass: 'secondary', bands: ['L'] }),
];
const HOME_STUDY: FloorProgram = {
  id: 'home.study',
  zones: [
    zone('desk', 'desk', 0, { mandatory: true, access: 'owner', requires: ['work'] }),
    zone('library', 'library', 1, { requires: ['storage'], adjacentTo: ['desk'], dropOrder: 2 }),
    zone('den', 'den', 2, { requires: ['seat'], dropOrder: 1 }),
  ],
  arrangements: [
    { id: 'north-desk', fills: [
      fill('desk', ['desk.study'], [wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'N', 'center')]),
      fill('library', ['store.bookcase'], [wall('room', 'E', 'center'), wall('room', 'N', 'start')], { count: 2 }),
      fill('den', ['lounge.set', 'seat.reading'], [wall('room', 'S', 'end'), wall('room', 'E', 'start'), wall('room', 'N', 'start')]),
      fill('library', ['detail.sidecase'], [wall('room', 'N', 'start'), wall('room', 'E', 'start')], { pass: 'detail' }),
      fill('den', ['detail.plant', 'detail.lamp'], [wall('room', 'S', 'start'), wall('room', 'E', 'end')], { pass: 'detail', pick: 'seed' }),
      ...STUDY_MORE,
    ] },
    { id: 'east-desk', fills: [
      fill('desk', ['desk.study'], [wall('room', 'E', 'end'), wall('room', 'N', 'end')]),
      fill('library', ['store.bookcase'], [wall('room', 'N', 'center'), wall('room', 'N', 'start')], { count: 2 }),
      fill('den', ['lounge.set', 'seat.reading'], [wall('room', 'E', 'start'), wall('room', 'S', 'end')]),
      fill('library', ['detail.sidecase'], [wall('room', 'N', 'start'), wall('room', 'S', 'start')], { pass: 'detail' }),
      fill('den', ['detail.plant', 'detail.lamp'], [wall('room', 'S', 'start'), wall('room', 'N', 'end')], { pass: 'detail', pick: 'seed' }),
      ...STUDY_MORE,
    ] },
  ],
};
/** The smaller home: one room, the bed and the galley, a seat if there is ground for it. */
const HOME_COMPACT: FloorProgram = {
  id: 'home.compact',
  zones: [
    zone('sleep', 'sleeping', 0, { mandatory: true, minArea: 3, access: 'owner', requires: ['bed'] }),
    zone('store', 'personal storage', 1, { minArea: 1.5, access: 'owner', requires: ['storage'], dropOrder: 2 }),
    zone('cook', 'cooking', 2, { minArea: 3, requires: ['work'], dropOrder: 3 }),
    zone('living', 'sitting', 3, { minArea: 2, requires: ['seat'], dropOrder: 1 }),
  ],
  arrangements: [
    { id: 'studio', fills: [
      fill('sleep', ['bed.single'], [wall('room', 'S', 'start'), wall('room', 'S', 'end'), wall('room', 'N', 'end'), wall('room', 'E', 'end')]),
      fill('store', ['store.chest'], [wall('room', 'N', 'start'), wall('room', 'E', 'end'), wall('room', 'N', 'end')]),
      fill('cook', ['kitchen.galley'], [wall('room', 'E', 'start'), wall('room', 'N', 'end'), wall('room', 'N', 'start')]),
      fill('living', ['seat.chair'], [wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'S', 'end')]),
    ] },
    { id: 'studio-b', fills: [
      fill('sleep', ['bed.single'], [wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'S', 'end')]),
      fill('store', ['store.chest'], [wall('room', 'E', 'start'), wall('room', 'N', 'start')]),
      fill('cook', ['kitchen.galley'], [wall('room', 'N', 'start'), wall('room', 'E', 'start')]),
      fill('living', ['seat.chair'], [wall('room', 'S', 'end'), wall('room', 'E', 'start')]),
    ] },
  ],
};

// ─── CANTINA — one program, two traditions ───────────────────────────────────────────────────────────────────────────

const CANTINA_ZONES: ZoneSpec[] = [
  zone('counter', 'service counter', 0, { mandatory: true, minArea: 10, requires: ['service-staff', 'service-customer'], adjacentTo: ['prep', 'social'] }),
  zone('prep', 'prep and back bar', 1, { mandatory: true, minArea: 4, access: 'employee', requires: ['work'], adjacentTo: ['counter'], separatedFrom: ['arrival'], enclosure: 'screen' }),
  // the performance edge takes its corner BEFORE the tables are laid — and is the first to go in a small room
  zone('performance', 'performance edge', 2, { minArea: 30, requires: ['perform'], adjacentTo: ['social'], dropOrder: 9 }),
  zone('social', 'social tables', 3, { mandatory: true, minArea: 10, requires: ['seat'], adjacentTo: ['counter'] }),
  zone('arrival', 'arrival and queue', 4, { minArea: 0, adjacentTo: ['counter'] }),
];
const CANTINA_GROUND: FloorProgram = {
  id: 'cantina.ground', zones: CANTINA_ZONES,
  arrangements: [
    // THE RADIANT CUP: an open axis from the door to a counter centred on it, tables in two files either side
    { id: 'axis', traditions: ['aurelian'],
      cuts: [{ out: 'service', from: 'room', side: 'back', size: { min: 4.3, max: 4.9, frac: 0.36 } }],
      zoneRects: { counter: ['service'], social: ['east', 'west'] },
      fills: [
        fill('prep', ['prep.backbar'], [wall('service', 'N', 'end'), wall('service', 'N', 'start')]),
        fill('counter', ['counter.service.3', 'counter.service.2'], [row('service', 'N', 2.75, 'center'), row('service', 'N', 3.2, 'center')]),
        fill('social', ['table.dining', 'table.pair'], [grid('east', 1, 0.9, 0.15)], { count: 2 }),
        fill('social', ['table.dining', 'table.pair'], [grid('west', 1, 0.9, 0.15)], { count: 2 }),
        fill('social', ['table.stools'], [grid('east', 0, 0.9, 0.15)], { count: 2 }),
        fill('social', ['table.stools'], [grid('west', 0, 0.9, 0.15)], { count: 2 }),
        fill('performance', ['perform.corner'], [wall('room', 'E', 'start'), wall('room', 'S', 'end')]),
        fill('social', ['seat.waiting'], [wall('room', 'W', 'center'), wall('room', 'E', 'center')], { pass: 'secondary' }),
        fill('arrival', ['sign.screen'], [wall('frontEast', 'S', 'start'), wall('frontWest', 'S', 'end')], { pass: 'secondary' }),
        fill('counter', ['detail.column'], [wall('service', 'E', 'start'), wall('service', 'W', 'start')], { pass: 'detail', count: 2 }),
        fill('social', ['detail.plant'], [wall('room', 'S', 'start'), wall('room', 'S', 'end')], { pass: 'detail', count: 2 }),
        fill('social', ['detail.plant', 'detail.lamp'], [wall('room', 'E', 'center'), wall('room', 'W', 'end')], { pass: 'detail', pick: 'seed' }),
      ] },
    // THE FROSTHEARTH: sheltered bays down the side walls, the counter off the axis, the hearth at the heart
    { id: 'bays', traditions: ['borean'],
      cuts: [{ out: 'service', from: 'room', side: 'back', size: { min: 4.3, max: 4.9, frac: 0.36 } }],
      zoneRects: { counter: ['service'], social: ['east', 'west'] },
      fills: [
        fill('prep', ['prep.backbar'], [wall('service', 'N', 'start'), wall('service', 'N', 'end')]),
        fill('counter', ['counter.service.2', 'counter.service.3'], [row('service', 'N', 2.75, 'start'), row('service', 'N', 3.2, 'start'), row('service', 'N', 2.75, 'center')]),
        fill('social', ['booth.bay'], [wall('east', 'E', 'end')], { count: 3 }),
        fill('social', ['booth.bay'], [wall('west', 'W', 'end')], { count: 3 }),
        fill('social', ['table.stools', 'table.pair'], [spot('main', 0.5, 0.25), spot('main', 0.5, 0.5)]),
        // beside the door, not in a corner: the corners are bays
        fill('performance', ['perform.corner'], [wall('frontWest', 'S', 'end'), wall('frontEast', 'S', 'start'), wall('room', 'S', 'end'), wall('service', 'N', 'end')]),
        fill('arrival', ['sign.screen'], [wall('frontEast', 'S', 'start'), wall('frontWest', 'S', 'end')], { pass: 'secondary' }),
        fill('social', ['detail.hearth'], [wall('service', 'N', 'end'), wall('service', 'E', 'end'), wall('room', 'S', 'start')], { pass: 'secondary' }),
        fill('social', ['store.shelf', 'detail.sidecase'], [wall('room', 'S', 'start'), wall('service', 'E', 'start')], { pass: 'detail', pick: 'seed' }),
        fill('social', ['detail.lamp'], [wall('room', 'S', 'start'), wall('room', 'S', 'end')], { pass: 'detail', count: 2 }),
      ] },
  ],
};
const CANTINA_UPPER: FloorProgram = {
  id: 'cantina.lounge',
  zones: [
    zone('lounge', 'lounge', 0, { mandatory: true, requires: ['seat'] }),
    zone('snug', 'snug', 1, { requires: ['seat'], dropOrder: 1 }),
    zone('store', 'linen and stock', 2, { access: 'employee', requires: ['storage'], dropOrder: 2 }),
  ],
  arrangements: [
    { id: 'lounge', fills: [
      fill('lounge', ['lounge.set', 'seat.reading'], [wall('room', 'N', 'end'), wall('room', 'E', 'end')]),
      fill('snug', ['lounge.set', 'seat.reading'], [wall('room', 'E', 'start'), wall('room', 'S', 'end')]),
      fill('store', ['store.cabinet', 'store.chest'], [wall('room', 'N', 'start'), wall('room', 'N', 'center')]),
      fill('lounge', ['table.pair'], [spot('main', 0.5, 0.4)], { pass: 'secondary' }),
      fill('lounge', ['detail.lamp', 'detail.plant'], [wall('room', 'S', 'start'), wall('room', 'E', 'center')], { pass: 'detail', pick: 'seed' }),
    ] },
  ],
};
const CANTINA_COMPACT: FloorProgram = {
  id: 'cantina.compact',
  zones: [
    zone('counter', 'service counter', 0, { mandatory: true, minArea: 4, requires: ['service-staff', 'service-customer'] }),
    zone('prep', 'prep', 1, { access: 'employee', requires: ['work'], dropOrder: 2 }),
    zone('social', 'seats', 2, { requires: ['seat'], dropOrder: 1 }),
  ],
  arrangements: [
    { id: 'snug', fills: [
      fill('counter', ['counter.checkout'], [row('room', 'N', 1.25, 'end'), row('room', 'E', 1.25, 'center'), row('room', 'N', 1.25, 'center')]),
      fill('prep', ['store.shelf'], [wall('room', 'N', 'end'), wall('room', 'E', 'end')]),
      fill('social', ['table.pair', 'seat.stools', 'seat.waiting'], [spot('main', 0.3, 0.4, 1), wall('room', 'S', 'end'), wall('room', 'E', 'start')]),
    ] },
  ],
};

// ─── CLINIC ──────────────────────────────────────────────────────────────────────────────────────────────────────────

/** A bigger clinic (M / L): a real waiting area along the west side, a second store, green — more ROOM, not a longer row. */
const CLINIC_MORE: Fill[] = [
  fill('waiting', ['seat.waiting'], [wall('west', 'W', 'start'), wall('west', 'W', 'center'), wall('room', 'S', 'end')], { pass: 'secondary', count: 3, bands: ['M', 'L'] }),
  fill('supplies', ['store.cabinet', 'store.shelf'], [wall('room', 'N', 'center'), wall('west', 'W', 'end')], { pass: 'secondary', bands: ['M', 'L'] }),
  fill('waiting', ['detail.plant', 'detail.lamp'], [wall('west', 'W', 'end'), wall('room', 'S', 'start'), wall('room', 'N', 'start')], { pass: 'detail', count: 2, bands: ['M', 'L'] }),
  // (slice F, revision 2) a big clinic: screened bays down the west wall, rows to wait in on the open floor, a second
  // staff desk and a wash unit — later passes, large floors only
  fill('treatment', ['bed.treatment.screen', 'bed.treatment'], [wall('room', 'W', 'end')], { pass: 'secondary', bands: ['L'], count: 1, per: 140, max: 3 }),
  fill('waiting', ['seat.row'], [grid('mainWest', 0, 1.0, 1.6)], { pass: 'secondary', bands: ['L'], count: 1, per: 140, max: 4 }),
  fill('staff', ['desk.study'], [wall('room', 'N', 'start'), wall('room', 'W', 'start')], { pass: 'secondary', bands: ['L'] }),
  fill('supplies', ['service.wash'], [wall('room', 'N', 'center'), wall('room', 'E', 'center')], { pass: 'secondary', bands: ['M', 'L'] }),
];
const CLINIC_GROUND: FloorProgram = {
  id: 'clinic.ground',
  zones: [
    zone('reception', 'reception', 0, { mandatory: true, minArea: 6, requires: ['service-staff', 'service-customer'], adjacentTo: ['waiting'] }),
    zone('treatment', 'triage and treatment', 1, { mandatory: true, minArea: 9, access: 'guarded', requires: ['bed', 'bedside'], adjacentTo: ['supplies'], separatedFrom: ['waiting'], enclosure: 'screen' }),
    zone('waiting', 'waiting', 2, { mandatory: true, minArea: 4, requires: ['seat'], adjacentTo: ['reception'] }),
    zone('supplies', 'supplies', 3, { mandatory: true, minArea: 2, access: 'employee', requires: ['storage'], adjacentTo: ['treatment'] }),
    zone('staff', 'staff', 4, { minArea: 30, access: 'employee', requires: ['work'], separatedFrom: ['waiting'], enclosure: 'room', dropOrder: 5 }),
  ],
  arrangements: [
    { id: 'flow-east', fills: [ // the desk ahead-right of the door facing it, waiting by the door wall, cots at the back and down the east wall
      fill('reception', ['desk.reception'], [spot('main', 0.74, 0.1, 2), spot('main', 0.7, 0.2, 2), spot('main', 0.5, 0.3, 2)]),
      fill('treatment', ['bed.treatment'], [wall('room', 'N', 'end')], { count: 2 }),
      fill('treatment', ['bed.treatment'], [wall('room', 'E', 'end')], { count: 3 }),
      fill('waiting', ['seat.waiting'], [wall('room', 'S', 'start'), wall('frontEast', 'S', 'end'), wall('room', 'E', 'start')], { count: 2 }),
      fill('supplies', ['supply.wall', 'store.cabinet'], [wall('room', 'N', 'start'), wall('room', 'N', 'center'), wall('room', 'E', 'center')]),
      fill('staff', ['desk.study'], [wall('room', 'N', 'center'), wall('room', 'W', 'end')], { pass: 'secondary' }),
      fill('reception', ['sign.screen'], [wall('frontEast', 'S', 'start'), wall('frontWest', 'S', 'end')], { pass: 'secondary' }),
      fill('waiting', ['detail.plant'], [wall('room', 'S', 'start'), wall('room', 'S', 'end')], { pass: 'detail' }),
      ...CLINIC_MORE,
      fill('treatment', ['detail.column'], [wall('room', 'E', 'center'), wall('room', 'N', 'center')], { pass: 'detail' }),
    ] },
    { id: 'flow-west', fills: [ // mirrored: the desk ahead-left, waiting east of the door
      fill('reception', ['desk.reception'], [spot('main', 0.3, 0.1, 2), spot('main', 0.36, 0.2, 2), spot('main', 0.5, 0.3, 2)]),
      fill('treatment', ['bed.treatment'], [wall('room', 'N', 'end')], { count: 2 }),
      fill('treatment', ['bed.treatment'], [wall('room', 'E', 'end')], { count: 3 }),
      fill('waiting', ['seat.waiting'], [wall('frontEast', 'S', 'end'), wall('room', 'E', 'start'), wall('room', 'S', 'start')], { count: 2 }),
      fill('supplies', ['supply.wall', 'store.cabinet'], [wall('room', 'N', 'start'), wall('room', 'N', 'center')]),
      fill('staff', ['desk.study'], [wall('room', 'N', 'center'), wall('room', 'W', 'end')], { pass: 'secondary' }),
      fill('reception', ['sign.screen'], [wall('frontWest', 'S', 'end'), wall('frontEast', 'S', 'start')], { pass: 'secondary' }),
      fill('waiting', ['detail.plant'], [wall('room', 'S', 'end'), wall('room', 'S', 'start')], { pass: 'detail' }),
      ...CLINIC_MORE,
      fill('treatment', ['detail.column'], [wall('room', 'E', 'center'), wall('room', 'N', 'center')], { pass: 'detail' }),
    ] },
  ],
};
/** Upstairs: the ward and a staff corner — more beds are more ROOM, not a longer row. */
const CLINIC_WARD: FloorProgram = {
  id: 'clinic.ward',
  zones: [
    zone('ward', 'ward', 0, { mandatory: true, minArea: 9, access: 'guarded', requires: ['bed', 'bedside'], adjacentTo: ['supplies'] }),
    zone('supplies', 'supplies', 1, { access: 'employee', requires: ['storage'], dropOrder: 2 }),
    zone('staff', 'staff', 2, { access: 'employee', requires: ['work'], dropOrder: 1 }),
    zone('visitors', 'visitors', 3, { requires: ['seat'], dropOrder: 3 }),
  ],
  arrangements: [
    { id: 'ward', fills: [
      fill('ward', ['bed.treatment'], [wall('room', 'N', 'end')], { count: 3 }),
      fill('ward', ['bed.treatment'], [wall('room', 'E', 'start')], { count: 3 }),
      fill('supplies', ['supply.wall', 'store.cabinet'], [wall('room', 'N', 'start'), wall('room', 'S', 'end')]),
      fill('staff', ['desk.study'], [wall('room', 'S', 'end'), wall('room', 'S', 'start')]),
      fill('visitors', ['seat.waiting'], [wall('room', 'S', 'start'), wall('room', 'S', 'end')], { pass: 'secondary' }),
      fill('ward', ['detail.plant', 'detail.column'], [wall('room', 'S', 'end'), wall('room', 'N', 'center')], { pass: 'detail', pick: 'seed' }),
    ] },
  ],
};
const CLINIC_COMPACT: FloorProgram = {
  id: 'clinic.compact',
  zones: [ // a tiny shell: triage and treatment share one bay; capacity drops, nothing unreachable is drawn
    zone('treatment', 'treatment bay', 0, { mandatory: true, minArea: 5, access: 'guarded', requires: ['bed', 'bedside'] }),
    zone('supplies', 'supplies', 1, { access: 'employee', requires: ['storage'], dropOrder: 2 }),
    zone('waiting', 'waiting', 2, { requires: ['seat'], dropOrder: 1 }),
  ],
  arrangements: [
    { id: 'bay', fills: [
      fill('treatment', ['bed.treatment'], [wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'S', 'end'), wall('room', 'S', 'start')]),
      fill('supplies', ['store.cabinet', 'store.chest'], [wall('room', 'N', 'start'), wall('room', 'E', 'start'), wall('room', 'N', 'end')]),
      fill('waiting', ['seat.waiting', 'seat.chair'], [wall('room', 'S', 'start'), wall('room', 'S', 'end'), wall('room', 'E', 'start')]),
    ] },
  ],
};

// ─── SHOP ────────────────────────────────────────────────────────────────────────────────────────────────────────────

const SHOP_ZONES: ZoneSpec[] = [
  zone('checkout', 'checkout', 0, { mandatory: true, minArea: 6, requires: ['service-staff', 'service-customer'], adjacentTo: ['backstock'], separatedFrom: ['arrival'] }),
  zone('browse', 'browse', 1, { mandatory: true, minArea: 6, requires: ['display'], adjacentTo: ['window', 'checkout'] }),
  zone('backstock', 'back stock', 2, { minArea: 3, access: 'employee', requires: ['storage'], adjacentTo: ['checkout'], separatedFrom: ['arrival'], enclosure: 'screen', dropOrder: 2 }),
  zone('window', 'window and entry', 3, { minArea: 8, requires: ['display'], adjacentTo: ['browse'], dropOrder: 3 }),
];
const SHOP_GROUND: FloorProgram = {
  id: 'shop.ground', zones: SHOP_ZONES,
  arrangements: [
    { id: 'deep', fills: [ // a central aisle to a counter at the back, displays down both sides, stock behind the counter
      fill('backstock', ['stock.back', 'store.shelf'], [wall('room', 'N', 'end'), wall('room', 'N', 'start')]),
      fill('checkout', ['counter.checkout'], [row('room', 'N', 2.75, 'center'), row('room', 'N', 2.0, 'center'), row('room', 'N', 2.75, 'end')]),
      fill('browse', ['display.wall'], [wall('room', 'E', 'center')], { count: 4 }),
      fill('browse', ['display.wall'], [wall('room', 'W', 'center')], { count: 3 }),
      fill('browse', ['display.island'], [grid('mainEast', 1, 1.2)], { count: 2 }),
      fill('browse', ['display.island'], [grid('mainWest', 1, 1.2)], { count: 2 }),
      fill('window', ['display.hero'], [spot('frontEast', 0.3, 0.45), spot('frontEast', 0.5, 0.5)]),
      fill('window', ['display.hero'], [spot('frontWest', 0.7, 0.45)], { pass: 'secondary' }),
      fill('checkout', ['sign.screen'], [wall('room', 'N', 'center'), wall('room', 'E', 'end')], { pass: 'secondary' }),
      fill('browse', ['detail.column', 'detail.plant'], [wall('room', 'S', 'end'), wall('room', 'S', 'start')], { pass: 'detail', pick: 'seed' }),
      fill('window', ['detail.rug'], [spot('main', 0.5, 0.15, 1)], { pass: 'detail' }),
    ] },
    { id: 'wide', aspect: 'wide', fills: [ // a shallow, wide room: the counter at the east end, displays along the back wall
      fill('backstock', ['stock.back', 'store.shelf'], [wall('room', 'E', 'end'), wall('room', 'E', 'center')]),
      fill('checkout', ['counter.checkout'], [row('room', 'E', 2.0, 'center'), row('room', 'E', 1.25, 'center')]),
      fill('browse', ['display.wall'], [wall('room', 'N', 'start')], { count: 5 }),
      fill('browse', ['display.island'], [grid('mainWest', 0, 1.2), grid('frontWest', 0, 1.0)], { count: 2 }),
      fill('window', ['display.hero'], [spot('frontWest', 0.6, 0.45), spot('frontEast', 0.3, 0.45)]),
      fill('checkout', ['sign.screen'], [wall('room', 'N', 'end')], { pass: 'secondary' }),
      fill('browse', ['detail.column', 'detail.plant'], [wall('room', 'S', 'start'), wall('room', 'W', 'center')], { pass: 'detail', pick: 'seed' }),
    ] },
  ],
};
const SHOP_UPPER: FloorProgram = {
  id: 'shop.stockroom',
  zones: [
    zone('stock', 'stock room', 0, { mandatory: true, access: 'employee', requires: ['storage'] }),
    zone('office', 'office', 1, { access: 'employee', requires: ['work'], dropOrder: 1 }),
  ],
  arrangements: [
    { id: 'stock', fills: [
      fill('stock', ['stock.back', 'store.shelf'], [wall('room', 'N', 'end'), wall('room', 'E', 'end')]),
      fill('stock', ['store.shelf'], [wall('room', 'E', 'start')], { count: 3 }),
      fill('office', ['desk.study'], [wall('room', 'S', 'end'), wall('room', 'N', 'start')]),
      fill('stock', ['store.chest'], [wall('room', 'N', 'start'), wall('room', 'S', 'start')], { count: 2, pass: 'secondary' }),
      fill('office', ['detail.lamp'], [wall('room', 'S', 'start'), wall('room', 'E', 'center')], { pass: 'detail' }),
    ] },
  ],
};
const SHOP_COMPACT: FloorProgram = {
  id: 'shop.compact',
  zones: [
    zone('checkout', 'checkout', 0, { mandatory: true, minArea: 3, requires: ['service-staff', 'service-customer'] }),
    zone('browse', 'display', 1, { requires: ['display'], dropOrder: 1 }),
  ],
  arrangements: [
    { id: 'stall', fills: [
      fill('checkout', ['counter.checkout'], [row('room', 'N', 1.25, 'center'), row('room', 'E', 1.25, 'center'), row('room', 'N', 1.25, 'end')]),
      fill('browse', ['display.wall'], [wall('room', 'E', 'start'), wall('room', 'N', 'start'), wall('room', 'S', 'end')], { count: 2 }),
    ] },
  ],
};

// ─── LODGING — the units the rentals slice lets ─────────────────────────────────────────────────────────────────────

const UNIT_ZONE: Omit<ZoneSpec, 'id' | 'priority'> = { role: 'unit', mandatory: false, minArea: 9, adjacentTo: [], separatedFrom: [], access: 'guarded', enclosure: 'room', requires: ['bed'] };
const HOTEL_UNITS: FloorProgram = {
  id: 'lodging.rooms',
  zones: [zone('landing', 'landing', 9, { minArea: 0, requires: [] })],
  units: {
    kind: 'room', depth: { min: 3.3, max: 5.4 }, corridor: 1.5, maxWidth: 7.0, minUnits: 2, maxUnits: 8, zone: UNIT_ZONE,
    fills: [
      fill('unit', ['bed.double', 'bed.single'], [wall('unit', 'E', 'end'), wall('unit', 'E', 'start'), wall('unit', 'N', 'end')]),
      fill('unit', ['store.chest', 'store.cabinet'], [wall('unit', 'E', 'start'), wall('unit', 'N', 'start'), wall('unit', 'S', 'end')]),
      fill('unit', ['seat.chair'], [wall('unit', 'N', 'start'), wall('unit', 'S', 'start'), wall('unit', 'S', 'end')], { pass: 'secondary' }),
    ],
  },
  arrangements: [
    { id: 'shared', fills: [ // too small for rooms: one shared floor, no units
      fill('landing', ['bed.single'], [wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'S', 'end'), wall('room', 'E', 'start'), wall('room', 'N', 'start')], { count: 2 }),
      fill('landing', ['store.chest'], [wall('room', 'E', 'start'), wall('room', 'N', 'start')]),
      fill('landing', ['seat.chair'], [wall('room', 'S', 'end')], { pass: 'secondary' }),
    ] },
  ],
};
const APARTMENT_UNITS: FloorProgram = {
  id: 'lodging.dwellings',
  zones: [zone('landing', 'landing', 9, { minArea: 0, requires: [] })],
  units: {
    kind: 'dwelling', depth: { min: 4.2, max: 7.0 }, corridor: 1.5, maxWidth: 8.0, minUnits: 2, maxUnits: 6, zone: UNIT_ZONE,
    fills: [
      fill('unit', ['bed.double', 'bed.single'], [wall('unit', 'E', 'end'), wall('unit', 'N', 'end')]),
      fill('unit', ['store.wardrobe', 'store.chest'], [wall('unit', 'N', 'start'), wall('unit', 'E', 'start')]),
      fill('unit', ['table.pair', 'seat.chair'], [spot('unit', 0.3, 0.45), wall('unit', 'S', 'start')], { pass: 'secondary' }),
    ],
  },
  arrangements: [
    { id: 'shared', fills: [
      fill('landing', ['bed.single'], [wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'S', 'end'), wall('room', 'E', 'start'), wall('room', 'N', 'start')], { count: 2 }),
      fill('landing', ['store.wardrobe', 'store.chest'], [wall('room', 'E', 'start'), wall('room', 'N', 'start')]),
      fill('landing', ['table.pair'], [spot('main', 0.5, 0.45)], { pass: 'secondary' }),
    ] },
  ],
};
const HOTEL_LOBBY: FloorProgram = {
  id: 'hotel.lobby',
  zones: [
    zone('reception', 'reception', 0, { mandatory: true, minArea: 6, requires: ['service-staff', 'service-customer'] }),
    zone('lounge', 'lobby lounge', 1, { requires: ['seat'], adjacentTo: ['reception'], dropOrder: 1 }),
    zone('luggage', 'luggage', 2, { access: 'employee', requires: ['storage'], dropOrder: 2 }),
  ],
  arrangements: [
    { id: 'lobby', fills: [
      fill('reception', ['desk.reception'], [spot('main', 0.72, 0.15, 2), spot('main', 0.5, 0.3, 2), spot('main', 0.3, 0.15, 2)]),
      fill('lounge', ['lounge.set', 'seat.waiting', 'seat.reading'], [wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'S', 'end')]),
      fill('luggage', ['store.cabinet', 'store.chest'], [wall('room', 'N', 'start'), wall('room', 'E', 'center')]),
      fill('lounge', ['seat.waiting'], [wall('room', 'S', 'start'), wall('room', 'S', 'end')], { pass: 'secondary' }),
      fill('reception', ['detail.guestbook'], [wall('frontEast', 'S', 'start')], { pass: 'detail' }),
      fill('lounge', ['detail.plant', 'detail.lamp'], [wall('room', 'S', 'end'), wall('room', 'E', 'start')], { pass: 'detail', pick: 'seed' }),
    ] },
  ],
};

// ─── The catalog ─────────────────────────────────────────────────────────────────────────────────────────────────────

const PLANNED: InteriorArchetype[] = [
  // revision 2 (slice F, 2026-10-01): medium / large floors gain later-pass fills (`homeMore`, `SLEEP_MORE`, `STUDY_MORE`)
  { id: 'home', revision: 2, label: 'home', ground: HOME_GROUND, upper: [HOME_SLEEP, HOME_STUDY], maxFloors: 3, fallback: 'home.compact', legacyPurpose: 'residence' },
  { id: 'home.compact', revision: 2, label: 'small home', ground: HOME_COMPACT, upper: [HOME_SLEEP, HOME_STUDY], maxFloors: 3, fallback: null, legacyPurpose: 'residence' },
  { id: 'cantina', revision: 1, label: 'cantina', ground: CANTINA_GROUND, upper: [CANTINA_UPPER], maxFloors: 2, fallback: 'cantina.compact', legacyPurpose: 'hospitality' },
  { id: 'cantina.compact', revision: 1, label: 'small cantina', ground: CANTINA_COMPACT, upper: [CANTINA_UPPER], maxFloors: 2, fallback: null, legacyPurpose: 'hospitality' },
  // revision 2 (slice F): `CLINIC_MORE` grew for large floors; the ward (and so `clinic.compact`) is unchanged
  { id: 'clinic', revision: 2, label: 'clinic', ground: CLINIC_GROUND, upper: [CLINIC_WARD], maxFloors: 2, fallback: 'clinic.compact', legacyPurpose: 'clinic' },
  { id: 'clinic.compact', revision: 1, label: 'small clinic', ground: CLINIC_COMPACT, upper: [CLINIC_WARD], maxFloors: 2, fallback: null, legacyPurpose: 'clinic' },
  { id: 'shop', revision: 1, label: 'shop', ground: SHOP_GROUND, upper: [SHOP_UPPER], maxFloors: 2, fallback: 'shop.compact', legacyPurpose: 'retail' },
  { id: 'shop.compact', revision: 1, label: 'small shop', ground: SHOP_COMPACT, upper: [SHOP_UPPER], maxFloors: 2, fallback: null, legacyPurpose: 'retail' },
  { id: 'hotel', revision: 1, label: 'hotel', ground: HOTEL_LOBBY, upper: [HOTEL_UNITS], repeatUpper: true, maxFloors: 8, fallback: null, legacyPurpose: 'hospitality' },
  // revision 2 (slice F): its ground floor is the home's
  { id: 'apartment', revision: 2, label: 'apartments', ground: HOME_GROUND, upper: [APARTMENT_UNITS], repeatUpper: true, maxFloors: 8, fallback: 'home.compact', legacyPurpose: 'apartment' },
];

// ═══ THE BREADTH PROGRAMS (slice F, 2026-10-01) ═════════════════════════════════════════════════════════════════════
// A room for every building. Each program is the same schema as the wave-1 ones above; none of those was renumbered.
// Counts marked `per` scale with the floor (item 3: a big room earns more places, never a longer row of one thing).

const band = (side: Cut['side'], out: string, min: number, max = min, frac = 0, from = 'room'): Cut => ({ out, from, side, size: { min, max, frac } });
const ML: SizeBand[] = ['M', 'L'];
const SMALL: SizeBand[] = ['XS', 'S'];
/** An archetype and its compact fallback: the same upper floors, the last one repeating to the shell's eight. */
const family = (id: string, label: string, compactLabel: string, ground: FloorProgram, compact: FloorProgram, upper: FloorProgram[], legacyPurpose: InteriorPurpose): InteriorArchetype[] => [
  { id, revision: 1, label, ground, upper, repeatUpper: true, maxFloors: 8, fallback: `${id}.compact`, legacyPurpose },
  { id: `${id}.compact`, revision: 1, label: compactLabel, ground: compact, upper, repeatUpper: true, maxFloors: 8, fallback: null, legacyPurpose },
];
/** Rows either side of the centre aisle. A grid takes its whole count at the first region that has room, so each
 *  side is its own fill — the hall fills evenly instead of one half standing empty. */
const aisles = (z: string, kits: string[], turn: 0 | 1, gap: number, margin: number, o: Partial<Fill> = {}): Fill[] => [
  fill(z, kits, [grid('mainEast', turn, gap, margin)], o), fill(z, kits, [grid('mainWest', turn, gap, margin)], o),
];
/** The rows stop short of the focus: the back of the hall either side of the aisle (three tenths of its depth, 3.2 m
 *  in a small room, 5.4 m in a deep one — a stage's 2.1 m, up to 2.7 m off the wall where a console stands behind it,
 *  and the gap before the front row) is kept clear — never a row BESIDE a stage. */
const FORE: Cut[] = [band('back', 'foreE', 3.2, 5.4, 0.3, 'mainEast'), band('back', 'foreW', 3.2, 5.4, 0.3, 'mainWest')];
/** THE HOUSE IS A BLOCK, NOT TWO FILES. A grid takes the cells nearest the aisle first, so rows dealt into the whole
 *  depth of a hall stand in two single files with empty floor either side. The house is the back three quarters of
 *  what FORE left (6–12 m deep) either side of the aisle: its depth is what makes the block WIDE, and a hall too
 *  narrow for a second column simply runs on toward the door (the overflow goes to the rest of the side). */
const houseCuts = (fore: Cut[]): Cut[] => [...fore, band('back', 'houseE', 6, 12, 0.75, 'mainEast'), band('back', 'houseW', 6, 12, 0.75, 'mainWest')];
const HOUSE: Cut[] = houseCuts(FORE);
/** An offering table stands at the wall and is a metre deep: its pews need less clear floor before them than a stage's rows. */
const NAVE: Cut[] = houseCuts([band('back', 'foreE', 2.4, 3.4, 0.25, 'mainEast'), band('back', 'foreW', 2.4, 3.4, 0.25, 'mainWest')]);
const house = (z: string, kits: string[], gap: number, margin: number, o: Partial<Fill> = {}): Fill[] => [
  fill(z, kits, [grid('houseE', 0, gap, margin), grid('mainEast', 0, gap, margin)], o),
  fill(z, kits, [grid('houseW', 0, gap, margin), grid('mainWest', 0, gap, margin)], o),
];
/** The same kit on two walls, each its own count (a wall run takes its whole count at the first wall with room). */
const both = (z: string, kits: string[], a: Site[], b: Site[], o: Partial<Fill> = {}): Fill[] => [fill(z, kits, a, o), fill(z, kits, b, o)];
/** THE FOCUS IS CENTRED OR IT IS NOT THERE. On the back wall's centre — or, where a deed's structure console holds
 *  that ground, standing free 2.7 m before the wall, still centred, the console behind it (backstage; the route
 *  proof keeps it reached round the side). Never slid into the corner beside the console: rows would look past it.
 *  A room at least 1.5× as wide as deep also has a SIDE arrangement (the focus on the east wall, the rows turned). */
const BACK: Site[] = [
  { ...wall('room', 'N', 'center'), fixed: true, onAxis: true }, { ...row('room', 'N', 2.7, 'center'), fixed: true, onAxis: true },
  // … and where the stair strip leaves no room on the door's axis, the middle of the wall that is left
  { ...wall('room', 'N', 'center'), fixed: true }, { ...row('room', 'N', 2.7, 'center'), fixed: true },
];
const EAST: Site[] = [{ ...wall('room', 'E', 'center'), fixed: true }, wall('room', 'E', 'center')];

// ─── OFFICE (and every tower's office floors) ────────────────────────────────────────────────────────────────────────

/** An office floor: desk islands across the floor, records on a wall, a table to meet at, somewhere to sit down. */
const OFFICE_FLOOR: FloorProgram = {
  id: 'office.floor',
  zones: [
    zone('desks', 'desks', 0, { mandatory: true, minArea: 8, access: 'employee', requires: ['work'] }),
    zone('records', 'records', 1, { minArea: 2, access: 'employee', requires: ['storage'], adjacentTo: ['desks'], dropOrder: 2 }),
    zone('meeting', 'meeting table', 2, { minArea: 34, access: 'employee', requires: ['seat'], enclosure: 'screen', dropOrder: 4 }),
    zone('pause', 'tea point', 3, { minArea: 20, access: 'employee', requires: ['seat'], dropOrder: 3 }),
  ],
  arrangements: [
    { id: 'islands', fills: [
      ...aisles('desks', ['desk.pod'], 1, 1.2, 0.4, { per: 44, max: 4 }),
      fill('desks', ['desk.study'], [wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'S', 'end')], { count: 1, per: 90, max: 3 }),
      fill('records', ['store.bookcase', 'store.cabinet'], [wall('room', 'N', 'start'), wall('room', 'E', 'center')], { count: 2, per: 45, max: 5 }),
      fill('meeting', ['table.dining', 'table.pair'], [spot('frontEast', 0.55, 0.6, 1), spot('main', 0.5, 0.85)]),
      fill('pause', ['lounge.set', 'seat.reading'], [wall('room', 'S', 'end'), wall('frontWest', 'S', 'start'), wall('room', 'E', 'start')]),
      // a big floor repeats the bay: more islands, and a breakout cluster each side of the aisle
      ...aisles('desks', ['desk.pod'], 1, 1.2, 0.4, { count: 1, per: 250, max: 8, bands: ['L'] }),
      ...aisles('pause', ['lounge.island', 'lounge.pods'], 0, 4, 2.5, { count: 1, per: 900, max: 3, bands: ['L'] }),
      fill('meeting', ['screen.single'], [row('frontEast', 'S', 3.55, 'center')], { pass: 'secondary', bands: ML }),
      fill('records', ['sign.screen'], [wall('room', 'N', 'center'), wall('room', 'E', 'end')], { pass: 'secondary' }),
      fill('pause', ['detail.plant', 'detail.lamp'], [wall('room', 'S', 'start'), wall('room', 'N', 'end'), wall('room', 'E', 'start')], { pass: 'detail', pick: 'seed', count: 2 }),
      fill('desks', ['detail.column'], [wall('room', 'E', 'center'), wall('room', 'N', 'center')], { pass: 'detail', bands: ML }),
    ] },
  ],
};
const OFFICE_GROUND: FloorProgram = {
  id: 'office.ground',
  zones: [
    zone('reception', 'reception', 0, { mandatory: true, minArea: 6, requires: ['service-staff', 'service-customer'], adjacentTo: ['waiting'] }),
    zone('desks', 'desks', 1, { mandatory: true, minArea: 8, access: 'employee', requires: ['work'], separatedFrom: ['arrival'] }),
    zone('waiting', 'waiting', 2, { minArea: 3, requires: ['seat'], adjacentTo: ['reception'], dropOrder: 1 }),
    zone('records', 'records', 3, { minArea: 2, access: 'employee', requires: ['storage'], adjacentTo: ['desks'], dropOrder: 2 }),
    zone('meeting', 'meeting table', 4, { minArea: 60, access: 'employee', requires: ['seat'], enclosure: 'screen', dropOrder: 4 }),
    zone('arrival', 'arrival', 5, { minArea: 0 }),
  ],
  arrangements: (['east', 'west'] as const).map((side): Arrangement => ({
    id: `lobby-${side}`,
    cuts: [band('front', 'lobby', 3.0, 4.0, 0.3, 'main')],
    zoneRects: { reception: ['lobby'] },
    fills: [
      fill('reception', ['desk.reception'], side === 'east' ? [spot('lobby', 0.74, 0.35, 2), spot('lobby', 0.5, 0.5, 2), spot('main', 0.6, 0.1, 2)] : [spot('lobby', 0.3, 0.35, 2), spot('lobby', 0.5, 0.5, 2), spot('main', 0.4, 0.1, 2)]),
      fill('desks', ['desk.pod'], [grid('main', 1, 1.2, 0.25), grid('main', 0, 1.2, 0.25)], { per: 26, max: 8, bands: ['S', 'M', 'L'] }),
      fill('desks', ['desk.study'], [wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'N', 'start')], { count: 1, per: 80, max: 3 }),
      fill('waiting', ['seat.waiting'], side === 'east' ? [wall('frontWest', 'S', 'end'), wall('room', 'S', 'start'), wall('frontEast', 'S', 'end')] : [wall('frontEast', 'S', 'start'), wall('room', 'S', 'end')]),
      fill('records', ['store.cabinet', 'store.bookcase'], [wall('room', 'N', 'center'), wall('room', 'E', 'center'), wall('room', 'N', 'start')], { count: 2, per: 50, max: 5 }),
      fill('meeting', ['table.dining'], [spot('main', side === 'east' ? 0.22 : 0.78, 0.82), spot('main', 0.5, 0.85)]),
      // a pair of screens stands between the lobby and the desks — a way through in the middle
      fill('reception', ['screen.divider'], [row('main', 'S', 0.25, 'center')], { pass: 'secondary', bands: ML }),
      fill('reception', ['sign.screen'], side === 'east' ? [wall('frontEast', 'S', 'start'), wall('frontWest', 'S', 'end')] : [wall('frontWest', 'S', 'end'), wall('frontEast', 'S', 'start')], { pass: 'secondary' }),
      fill('waiting', ['detail.plant', 'detail.lamp'], [wall('room', 'S', 'end'), wall('room', 'S', 'start')], { pass: 'detail', pick: 'seed' }),
      fill('desks', ['detail.plant', 'detail.column'], [wall('room', 'E', 'start'), wall('room', 'N', 'end'), wall('room', 'W', 'end')], { pass: 'detail', pick: 'seed', count: 2, bands: ML }),
    ],
  })),
};
const OFFICE_COMPACT: FloorProgram = {
  id: 'office.compact',
  zones: [
    zone('desks', 'desk', 0, { mandatory: true, minArea: 3, access: 'employee', requires: ['work'] }),
    zone('records', 'records', 1, { requires: ['storage'], dropOrder: 1 }),
    zone('waiting', 'a seat', 2, { requires: ['seat'], dropOrder: 2 }),
  ],
  arrangements: [
    { id: 'bureau', fills: [
      fill('desks', ['desk.study'], [wall('room', 'E', 'end'), wall('room', 'N', 'end'), wall('room', 'W', 'end'), wall('room', 'S', 'end'), wall('room', 'N', 'start')], { count: 1, per: 22, max: 8 }),
      fill('records', ['store.cabinet', 'store.bookcase', 'store.chest'], [wall('room', 'N', 'start'), wall('room', 'E', 'start'), wall('room', 'S', 'end')], { count: 1, per: 45, max: 4 }),
      fill('waiting', ['seat.waiting', 'seat.chair'], [wall('room', 'S', 'end'), wall('room', 'S', 'start'), wall('room', 'E', 'start'), wall('room', 'N', 'center')], { count: 1, per: 70, max: 3 }),
      fill('waiting', ['detail.plant', 'detail.lamp'], [wall('room', 'S', 'start'), wall('room', 'W', 'start'), wall('room', 'E', 'center')], { pass: 'detail', pick: 'seed', count: 2 }),
    ] },
  ],
};

// ─── ACADEMY ─────────────────────────────────────────────────────────────────────────────────────────────────────────

const ACADEMY_HALL: FloorProgram = {
  id: 'academy.hall',
  zones: [
    zone('lecture', 'teaching dais', 0, { mandatory: true, minArea: 8, access: 'employee', requires: ['perform'], separatedFrom: ['arrival'] }),
    zone('seats', 'benches', 1, { mandatory: true, minArea: 8, requires: ['seat'], adjacentTo: ['lecture'] }),
    zone('library', 'library wall', 2, { minArea: 2, requires: ['storage'], dropOrder: 2 }),
    zone('desk', 'registrar', 3, { minArea: 40, requires: ['service-staff', 'service-customer'], dropOrder: 3 }),
    zone('arrival', 'arrival', 4, { minArea: 0 }),
  ],
  arrangements: [
    { id: 'lecture', cuts: HOUSE, fills: [
      fill('lecture', ['focus.dais'], BACK),
      ...house('seats', ['seat.row'], 1.0, 0.4, { count: 1, per: 22, max: 12 }),
      fill('seats', ['seat.row'], [grid('main', 0, 1.0, 0.4), wall('room', 'S', 'end')], { bands: SMALL, count: 2 }),
      ...both('library', ['store.bookcase'], [wall('room', 'E', 'end')], [wall('room', 'W', 'end')], { count: 1, per: 60, max: 4 }),
      fill('desk', ['desk.reception'], [spot('frontEast', 0.6, 0.5, 3), spot('frontWest', 0.4, 0.5, 1)]),
      fill('desk', ['sign.screen'], [wall('frontWest', 'S', 'end'), wall('frontEast', 'S', 'start')], { pass: 'secondary' }),
      fill('library', ['desk.study'], [wall('room', 'E', 'start'), wall('room', 'W', 'start')], { pass: 'secondary', bands: ML }),
      fill('seats', ['detail.plant', 'detail.lamp'], [wall('room', 'S', 'end'), wall('room', 'S', 'start')], { pass: 'detail', pick: 'seed' }),
    ] },
    { id: 'lecture-side', aspect: 'wide', fills: [
      fill('lecture', ['focus.dais'], EAST),
      fill('seats', ['seat.row'], [grid('main', 1, 1.0, 0.4)], { count: 2, per: 11, max: 16 }),
      fill('library', ['store.bookcase'], [wall('room', 'N', 'start'), wall('room', 'N', 'end')], { count: 2, per: 30, max: 6 }),
      fill('desk', ['desk.reception'], [spot('frontEast', 0.6, 0.5, 3), spot('frontWest', 0.4, 0.5, 1)]),
      fill('desk', ['sign.screen'], [wall('frontWest', 'S', 'end'), wall('frontEast', 'S', 'start')], { pass: 'secondary' }),
      fill('library', ['desk.study'], [wall('room', 'N', 'center'), wall('room', 'W', 'start')], { pass: 'secondary', bands: ML }),
      fill('seats', ['detail.plant', 'detail.lamp'], [wall('room', 'S', 'end'), wall('room', 'S', 'start')], { pass: 'detail', pick: 'seed' }),
    ] },
  ],
};
const ACADEMY_STUDY: FloorProgram = {
  id: 'academy.study',
  zones: [
    zone('study', 'study desks', 0, { mandatory: true, minArea: 6, requires: ['work'] }),
    zone('seminar', 'seminar table', 1, { minArea: 30, requires: ['seat'], dropOrder: 2 }),
    zone('stacks', 'stacks', 2, { requires: ['storage'], dropOrder: 3 }),
    zone('nook', 'reading nook', 3, { requires: ['seat'], dropOrder: 1 }),
  ],
  arrangements: [
    { id: 'study', fills: [
      fill('study', ['desk.study'], [wall('room', 'E', 'start'), wall('room', 'N', 'end')], { count: 2, per: 20, max: 6 }),
      fill('seminar', ['table.council', 'table.dining'], [spot('main', 0.5, 0.45), spot('main', 0.5, 0.6)]),
      fill('stacks', ['store.bookcase'], [wall('room', 'N', 'start'), wall('room', 'S', 'end')], { count: 2, per: 25, max: 6 }),
      fill('nook', ['seat.reading', 'seat.chair'], [wall('room', 'S', 'start'), wall('room', 'E', 'end')]),
      fill('nook', ['detail.lamp', 'detail.plant'], [wall('room', 'S', 'end'), wall('room', 'N', 'center')], { pass: 'detail', pick: 'seed' }),
    ] },
  ],
};

// ─── CIVIC / COMMAND — a different floor each: public hall · offices · council · archive ────────────────────────────

const CIVIC_HALL: FloorProgram = {
  id: 'civic.hall',
  zones: [
    zone('service', 'public service', 0, { mandatory: true, minArea: 8, requires: ['service-staff', 'service-customer'], adjacentTo: ['gallery'] }),
    zone('gallery', 'public benches', 1, { mandatory: true, minArea: 5, requires: ['seat'] }),
    zone('dais', 'assembly dais', 2, { minArea: 55, access: 'employee', requires: ['perform'], separatedFrom: ['arrival'], dropOrder: 3 }),
    zone('notices', 'notice wall', 3, { minArea: 0, requires: ['display'], adjacentTo: ['arrival'], dropOrder: 1 }),
    zone('records', 'records', 4, { minArea: 2, access: 'employee', requires: ['storage'], dropOrder: 2 }),
    zone('honours', 'honours', 5, { minArea: 70, requires: ['display'], dropOrder: 4 }),
    zone('arrival', 'arrival', 6, { minArea: 0 }),
  ],
  arrangements: (['east', 'west'] as const).map((side): Arrangement => ({
    id: `hall-${side}`,
    // the assembly: the back half of the hall either side of the aisle — the benches fill it as a BLOCK before the
    // dais (a grid takes the cells nearest the axis first, so an uncut hall would get two files of benches)
    cuts: [band('back', 'assemblyE', 6, 11, 0.5, 'mainEast'), band('back', 'assemblyW', 6, 11, 0.5, 'mainWest')],
    fills: [
      fill('service', ['desk.reception'], side === 'east' ? [spot('main', 0.76, 0.08, 2), spot('main', 0.6, 0.2, 2)] : [spot('main', 0.24, 0.08, 2), spot('main', 0.4, 0.2, 2)]),
      fill('service', ['desk.reception'], side === 'east' ? [spot('main', 0.24, 0.08, 2)] : [spot('main', 0.76, 0.08, 2)], { bands: ML }),
      fill('dais', ['focus.dais'], [...BACK, wall('room', 'E', 'center'), wall('room', 'N', 'end')]),
      fill('gallery', ['seat.row'], [grid('assemblyE', 0, 1.0, 0.8)], { count: 1, per: 40, max: 14, bands: ML }),
      fill('gallery', ['seat.row'], [grid('assemblyW', 0, 1.0, 0.8)], { count: 1, per: 40, max: 14, bands: ML }),
      fill('gallery', ['seat.waiting'], side === 'east' ? [wall('frontWest', 'S', 'end'), wall('room', 'W', 'start'), wall('room', 'S', 'start')] : [wall('frontEast', 'S', 'start'), wall('room', 'E', 'start'), wall('room', 'S', 'end')], { count: 1 }),
      fill('notices', ['sign.screen'], side === 'east' ? [wall('frontEast', 'S', 'start'), wall('room', 'E', 'start')] : [wall('frontWest', 'S', 'end'), wall('room', 'W', 'start')], { count: 2, per: 250, max: 5 }),
      fill('service', ['desk.reception'], [spot('main', 0.62, 0.05, 2), spot('main', 0.38, 0.05, 2)], { count: 2, bands: ['L'], pass: 'secondary' }),
      ...both('records', ['store.bookcase', 'store.cabinet'], [wall('room', 'E', 'end'), wall('room', 'N', 'end')], [wall('room', 'W', 'start'), wall('room', 'N', 'start')], { count: 1, per: 120, max: 3 }),
      fill('honours', ['exhibit.plinth'], [spot('frontEast', 0.12, 0.92), spot('frontWest', 0.88, 0.92)], { count: 2 }),
      ...both('honours', ['exhibit.wall'], [wall('room', 'E', 'start')], [wall('room', 'W', 'end')], { pass: 'secondary', count: 1, per: 300, max: 3, bands: ['L'] }),
      fill('gallery', ['lounge.island'], [spot('frontEast', 0.55, 0.6), spot('frontWest', 0.45, 0.6)], { pass: 'secondary', count: 2, bands: ['L'] }),
      ...both('gallery', ['seat.waiting'], [wall('room', 'E', 'center')], [wall('room', 'W', 'center')], { pass: 'secondary', count: 1, per: 200, max: 2, bands: ML }),
      fill('gallery', ['detail.plant'], [wall('room', 'S', 'start'), wall('room', 'S', 'end')], { pass: 'detail', count: 2 }),
      fill('dais', ['detail.column'], [wall('room', 'N', 'start'), wall('room', 'N', 'end')], { pass: 'detail', count: 2 }),
    ],
  })),
};
const CIVIC_COUNCIL: FloorProgram = {
  id: 'civic.council',
  zones: [
    zone('council', 'council table', 0, { mandatory: true, minArea: 14, access: 'employee', requires: ['seat'] }),
    zone('gallery', 'gallery', 1, { requires: ['seat'], dropOrder: 2 }),
    zone('clerk', 'clerk', 2, { access: 'employee', requires: ['work'], adjacentTo: ['council'], dropOrder: 3 }),
    zone('records', 'minutes', 3, { access: 'employee', requires: ['storage'], dropOrder: 1 }),
  ],
  arrangements: [
    { id: 'chamber', fills: [
      fill('council', ['table.council', 'table.dining', 'table.pair'], [spot('main', 0.55, 0.45), spot('main', 0.5, 0.55), spot('main', 0.6, 0.5, 1)]),
      fill('gallery', ['seat.row'], [wall('room', 'S', 'end'), wall('room', 'E', 'start')], { count: 2, per: 40, max: 4 }),
      fill('clerk', ['desk.study'], [wall('room', 'N', 'end'), wall('room', 'E', 'end')]),
      fill('records', ['store.bookcase', 'store.cabinet'], [wall('room', 'N', 'start'), wall('room', 'N', 'center')], { count: 2 }),
      // a big chamber: committee tables either side of the council's, and the public in rows on the door side facing it
      ...aisles('council', ['table.dining', 'table.pair'], 0, 4, 3, { count: 1, per: 300, max: 5, bands: ['L'] }),
      fill('gallery', ['seat.row'], [grid('frontEast', 0, 1.0, 0.6)], { count: 1, per: 150, max: 6, bands: ['L'] }),
      fill('gallery', ['seat.row'], [grid('frontWest', 0, 1.0, 0.6)], { count: 1, per: 150, max: 6, bands: ['L'] }),
      fill('council', ['sign.screen'], [wall('room', 'N', 'center'), wall('room', 'E', 'center')], { pass: 'secondary' }),
      fill('council', ['detail.rug'], [spot('main', 0.55, 0.45)], { pass: 'detail' }),
      fill('gallery', ['detail.column', 'detail.lamp'], [wall('room', 'S', 'start'), wall('room', 'E', 'center')], { pass: 'detail', pick: 'seed', count: 2 }),
    ] },
  ],
};
const CIVIC_ARCHIVE: FloorProgram = {
  id: 'civic.archive',
  zones: [
    zone('stacks', 'archive stacks', 0, { mandatory: true, minArea: 4, requires: ['storage'] }),
    zone('reading', 'reading desks', 1, { requires: ['work'], dropOrder: 2 }),
    zone('consult', 'consultation', 2, { minArea: 36, requires: ['stand'], dropOrder: 3 }),
    zone('nook', 'study niche', 3, { requires: ['seat'], dropOrder: 1 }),
  ],
  arrangements: [
    { id: 'spine', fills: [
      fill('stacks', ['store.bookcase'], [wall('room', 'N', 'start')], { count: 3, per: 14, max: 8 }),
      fill('stacks', ['store.bookcase', 'store.cabinet'], [wall('room', 'E', 'end')], { count: 1, per: 30, max: 5 }),
      fill('reading', ['desk.study'], [wall('room', 'S', 'end'), wall('room', 'E', 'start')], { count: 2, per: 40, max: 4 }),
      fill('consult', ['table.briefing'], [spot('main', 0.5, 0.5), spot('main', 0.5, 0.6, 1)]),
      fill('nook', ['seat.reading', 'seat.chair'], [wall('room', 'S', 'start'), wall('room', 'E', 'center')]),
      // a big archive: stacks standing free in rows either side of the aisle, reading islands among them
      ...aisles('stacks', ['store.bookcase'], 1, 1.5, 1.2, { count: 1, per: 100, max: 10, bands: ['L'] }),
      ...aisles('reading', ['desk.pod'], 1, 1.4, 1.0, { count: 1, per: 400, max: 3, bands: ['L'] }),
      fill('nook', ['detail.lamp'], [wall('room', 'S', 'end'), wall('room', 'N', 'end')], { pass: 'detail' }),
    ] },
  ],
};
const CIVIC_COMPACT: FloorProgram = {
  id: 'civic.compact',
  zones: [
    zone('service', 'public desk', 0, { mandatory: true, minArea: 3, requires: ['work'] }),
    zone('gallery', 'public bench', 1, { requires: ['seat'], dropOrder: 1 }),
    zone('notices', 'notices', 2, { requires: ['display'], dropOrder: 2 }),
  ],
  arrangements: [
    { id: 'office', fills: [
      fill('service', ['desk.study'], [wall('room', 'E', 'end'), wall('room', 'N', 'end'), wall('room', 'S', 'end'), wall('room', 'N', 'start')], { count: 1, per: 40, max: 4 }),
      fill('gallery', ['seat.waiting', 'seat.chair'], [wall('room', 'S', 'end'), wall('room', 'S', 'start'), wall('room', 'W', 'end'), wall('room', 'E', 'start'), wall('room', 'N', 'start')], { count: 1, per: 30, max: 6 }),
      fill('notices', ['sign.screen'], [wall('room', 'E', 'start'), wall('room', 'N', 'start'), wall('room', 'S', 'start')], { count: 1, per: 60, max: 3 }),
    ] },
  ],
};

// ─── FAITH — quiet and communal: a focus, benches before it, light; no idol ─────────────────────────────────────────

const FAITH_HALL: FloorProgram = {
  id: 'faith.hall',
  zones: [
    zone('focus', 'communal focus', 0, { mandatory: true, minArea: 5, access: 'employee', requires: ['perform'], separatedFrom: ['arrival'] }),
    zone('gathering', 'gathering', 1, { mandatory: true, minArea: 6, requires: ['seat'], adjacentTo: ['focus'] }),
    zone('reflection', 'quiet reflection', 2, { minArea: 2, requires: ['seat'], dropOrder: 1 }),
    zone('vestry', 'service storage', 3, { minArea: 2, access: 'employee', requires: ['storage'], dropOrder: 2 }),
    zone('glow', 'lights', 4, { minArea: 0, dropOrder: 3 }),
    zone('arrival', 'arrival', 5, { minArea: 0 }),
  ],
  arrangements: [
    { id: 'nave', cuts: NAVE, fills: [
      fill('focus', ['focus.offering'], BACK),
      ...house('gathering', ['seat.pew'], 1.1, 0.4, { count: 1, per: 30, max: 8 }),
      fill('gathering', ['seat.pew', 'seat.row'], [grid('main', 0, 1.1, 0.4), wall('room', 'S', 'end'), wall('room', 'E', 'start')], { bands: SMALL, count: 2 }),
      ...both('reflection', ['seat.waiting'], [wall('room', 'E', 'start')], [wall('room', 'W', 'start')], { count: 1, per: 120, max: 2 }),
      fill('vestry', ['store.cabinet', 'store.chest'], [wall('room', 'N', 'end'), wall('room', 'E', 'end')]),
      ...both('glow', ['detail.column'], [wall('room', 'E', 'end')], [wall('room', 'W', 'start')], { count: 1, per: 60, max: 4 }),
      fill('arrival', ['detail.rug'], [spot('main', 0.5, 0.12, 1)], { pass: 'detail' }),
      fill('reflection', ['detail.plant'], [wall('room', 'S', 'start'), wall('room', 'S', 'end')], { pass: 'detail' }),
    ] },
    { id: 'nave-side', aspect: 'wide', fills: [
      fill('focus', ['focus.offering'], EAST),
      fill('gathering', ['seat.pew', 'seat.row'], [grid('main', 1, 1.1, 0.4), wall('room', 'S', 'end')], { count: 2, per: 15, max: 12 }),
      fill('reflection', ['seat.waiting'], [wall('room', 'N', 'start'), wall('room', 'S', 'start')], { count: 1, per: 60, max: 3 }),
      fill('vestry', ['store.cabinet', 'store.chest'], [wall('room', 'N', 'end'), wall('room', 'E', 'end')]),
      fill('glow', ['detail.column'], [wall('room', 'N', 'center'), wall('room', 'W', 'center'), wall('room', 'N', 'start'), wall('room', 'W', 'end')], { count: 2, per: 30, max: 6 }),
      fill('reflection', ['detail.plant'], [wall('room', 'S', 'start'), wall('room', 'S', 'end')], { pass: 'detail' }),
    ] },
  ],
};
const FAITH_QUIET: FloorProgram = {
  id: 'faith.quiet',
  zones: [
    zone('reflection', 'quiet room', 0, { mandatory: true, requires: ['seat'] }),
    zone('study', 'reading', 1, { requires: ['work'], dropOrder: 1 }),
    zone('store', 'linen and lamps', 2, { access: 'employee', requires: ['storage'], dropOrder: 2 }),
  ],
  arrangements: [
    { id: 'quiet', fills: [
      fill('reflection', ['seat.waiting'], [wall('room', 'N', 'end'), wall('room', 'E', 'start')], { count: 2, per: 50, max: 5 }),
      fill('study', ['desk.study'], [wall('room', 'S', 'end'), wall('room', 'E', 'end')], { count: 1, per: 80, max: 3 }),
      fill('store', ['store.cabinet', 'store.chest'], [wall('room', 'N', 'start')], { count: 1, per: 100, max: 3 }),
      fill('reflection', ['detail.rug'], [spot('main', 0.5, 0.45)], { pass: 'detail' }),
      fill('reflection', ['detail.column', 'detail.lamp'], [wall('room', 'S', 'start'), wall('room', 'E', 'center')], { pass: 'detail', pick: 'seed', count: 2 }),
    ] },
  ],
};
const FAITH_COMPACT: FloorProgram = {
  id: 'faith.compact',
  zones: [
    zone('focus', 'focus', 0, { mandatory: true, minArea: 3, access: 'employee', requires: ['perform'] }),
    zone('gathering', 'a bench', 1, { requires: ['seat'], dropOrder: 1 }),
  ],
  arrangements: [
    { id: 'cell', fills: [
      fill('focus', ['focus.offering'], BACK),
      fill('gathering', ['seat.waiting', 'seat.chair'], [wall('room', 'S', 'end'), wall('room', 'S', 'start'), wall('room', 'E', 'start'), wall('room', 'W', 'end')], { count: 1, per: 25, max: 6 }),
    ] },
    { id: 'cell-side', fills: [
      fill('focus', ['focus.offering'], EAST),
      fill('gathering', ['seat.waiting', 'seat.chair'], [wall('room', 'S', 'end'), wall('room', 'S', 'start'), wall('room', 'W', 'end'), wall('room', 'N', 'start')], { count: 1, per: 25, max: 6 }),
    ] },
  ],
};

// ─── GUILD — hall · meeting · craft · quarters (the 8-floor hall) ───────────────────────────────────────────────────

const GUILD_HALL: FloorProgram = {
  id: 'guild.hall',
  zones: [
    zone('feast', 'hall tables', 0, { mandatory: true, minArea: 10, requires: ['seat'] }),
    zone('benches', 'wall benches', 1, { minArea: 2, requires: ['seat'], dropOrder: 1 }),
    zone('hearth', 'hearth', 2, { minArea: 30, requires: ['stand'], adjacentTo: ['feast'], dropOrder: 2 }),
    // a plain shell gets a speaker's dais; the guild hall's shell HAS one (the charter is signed there) and keeps it clear
    zone('dais', 'dais', 3, { minArea: 50, access: 'employee', requires: ['perform'], dropOrder: 5 }),
    zone('bar', 'hall counter', 4, { minArea: 60, requires: ['service-staff', 'service-customer'], dropOrder: 4 }),
    zone('trophies', 'trophies', 5, { minArea: 40, requires: ['display'], dropOrder: 3 }),
    zone('arrival', 'arrival', 6, { minArea: 0 }),
  ],
  arrangements: [
    // a hall wider than ~31 m has SIDE BAYS beyond its tables (a 14 m band off each side wall; a narrower hall is all
    // tables and the cut does not apply): the second social cluster — sofas between the columns, cases of the guild's work
    { id: 'nave', cuts: [band('east', 'bayE', 14, 14, 0, 'mainEast'), band('west', 'bayW', 14, 14, 0, 'mainWest')], fills: [
      ...aisles('feast', ['table.long', 'table.pair'], 1, 1.2, 0.5, { count: 1, per: 70, max: 18 }),
      fill('feast', ['table.long', 'table.pair'], [spot('main', 0.5, 0.45, 1), spot('main', 0.5, 0.5), spot('room', 0.6, 0.6)], { bands: SMALL }),
      ...both('benches', ['seat.waiting'], [wall('room', 'E', 'start')], [wall('room', 'W', 'end')], { count: 1, per: 160, max: 6 }),
      ...both('hearth', ['detail.hearth'], [wall('room', 'E', 'center')], [wall('room', 'W', 'center')], { count: 1, bands: ['L'] }),
      fill('hearth', ['detail.hearth'], [wall('room', 'E', 'center'), wall('room', 'W', 'center')], { bands: ['XS', 'S', 'M'] }),
      fill('dais', ['focus.dais'], BACK),
      fill('bar', ['counter.service.2', 'counter.kiosk'], [row('mainEast', 'E', 1.6, 'end'), row('room', 'E', 1.6, 'end')]),
      fill('trophies', ['exhibit.plinth'], [spot('frontEast', 0.14, 0.9), spot('frontWest', 0.86, 0.9), spot('frontEast', 0.5, 0.9), spot('frontWest', 0.5, 0.9)], { count: 2, per: 300, max: 4 }),
      fill('benches', ['lounge.island', 'lounge.pods'], [grid('bayE', 0, 4.5, 2.5)], { count: 3, bands: ['L'] }),
      fill('benches', ['lounge.island', 'lounge.pods'], [grid('bayW', 0, 4.5, 2.5)], { count: 3, bands: ['L'] }),
      fill('trophies', ['exhibit.case'], [grid('bayE', 1, 6, 4)], { count: 3, bands: ['L'] }),
      fill('trophies', ['exhibit.case'], [grid('bayW', 1, 6, 4)], { count: 3, bands: ['L'] }),
      ...both('trophies', ['exhibit.wall'], [wall('room', 'E', 'end')], [wall('room', 'W', 'start')], { pass: 'secondary', count: 1, per: 400, max: 4, bands: ['L'] }),
      fill('arrival', ['sign.screen'], [wall('frontEast', 'S', 'start'), wall('frontWest', 'S', 'end')], { pass: 'secondary' }),
      fill('benches', ['detail.column'], [wall('room', 'E', 'end'), wall('room', 'W', 'start')], { pass: 'detail', count: 2 }),
      fill('benches', ['detail.plant'], [wall('room', 'S', 'start'), wall('room', 'S', 'end')], { pass: 'detail', count: 2 }),
    ] },
  ],
};
const GUILD_MEETING: FloorProgram = {
  id: 'guild.meeting',
  zones: [
    zone('council', 'meeting table', 0, { mandatory: true, minArea: 12, access: 'employee', requires: ['seat'] }),
    zone('records', 'rolls and ledgers', 1, { access: 'employee', requires: ['storage'], dropOrder: 2 }),
    zone('benches', 'benches', 2, { requires: ['seat'], dropOrder: 1 }),
    zone('snug', 'snug', 3, { minArea: 30, requires: ['seat'], dropOrder: 3 }),
  ],
  arrangements: [
    { id: 'meeting', fills: [
      fill('council', ['table.council', 'table.dining', 'table.pair'], [spot('main', 0.55, 0.45), spot('main', 0.5, 0.55), spot('main', 0.6, 0.5, 1)]),
      fill('records', ['store.bookcase', 'store.cabinet'], [wall('room', 'N', 'start'), wall('room', 'N', 'end')], { count: 2, per: 60, max: 5 }),
      fill('benches', ['seat.waiting'], [wall('room', 'E', 'start'), wall('room', 'S', 'end')], { count: 1, per: 70, max: 4 }),
      fill('snug', ['lounge.set', 'seat.reading'], [wall('room', 'S', 'start'), wall('room', 'E', 'end')]),
      // a big floor: a committee table per trade either side of the court's, and snugs between them
      ...aisles('council', ['table.council', 'table.dining'], 0, 4, 3, { count: 1, per: 350, max: 6, bands: ['L'] }),
      ...aisles('snug', ['lounge.island', 'lounge.pods'], 0, 4.5, 2.5, { count: 1, per: 600, max: 4, bands: ['L'] }),
      fill('council', ['sign.screen'], [wall('room', 'N', 'center'), wall('room', 'E', 'center')], { pass: 'secondary' }),
      fill('snug', ['detail.lamp', 'detail.plant'], [wall('room', 'S', 'end'), wall('room', 'N', 'center')], { pass: 'detail', pick: 'seed', count: 2 }),
    ] },
  ],
};
const GUILD_CRAFT: FloorProgram = {
  id: 'guild.craft',
  zones: [
    zone('bench', 'benches', 0, { mandatory: true, minArea: 5, access: 'employee', requires: ['work'] }),
    zone('fab', 'fabricators', 1, { access: 'employee', requires: ['work'], dropOrder: 2 }),
    zone('parts', 'parts', 2, { access: 'employee', requires: ['storage'], adjacentTo: ['bench'], dropOrder: 1 }),
    zone('stock', 'finished work', 3, { access: 'employee', requires: ['storage'], dropOrder: 3 }),
    zone('table', 'planning table', 4, { minArea: 30, requires: ['seat'], dropOrder: 4 }),
  ],
  arrangements: [
    { id: 'craft', fills: [
      fill('bench', ['work.bench'], [wall('room', 'E', 'start')], { count: 1, per: 40, max: 5 }),
      fill('fab', ['work.fab'], [wall('room', 'N', 'end')], { count: 1, per: 70, max: 3 }),
      fill('parts', ['rack.parts'], [wall('room', 'N', 'start'), wall('room', 'E', 'end')], { count: 1, per: 60, max: 3 }),
      fill('stock', ['stock.crates'], [wall('room', 'S', 'end'), wall('room', 'S', 'start')], { count: 1, per: 80, max: 3 }),
      fill('table', ['table.pair'], [spot('main', 0.45, 0.5, 1), spot('main', 0.5, 0.6)]),
      // a big floor: bench islands in bays either side of the aisle, a process line down the west wall
      ...aisles('bench', ['work.island'], 0, 3, 3, { count: 1, per: 220, max: 8, bands: ['L'] }),
      fill('fab', ['work.line'], [wall('room', 'W', 'end'), wall('room', 'S', 'start')], { pass: 'secondary', bands: ['L'] }),
      fill('bench', ['detail.column'], [wall('room', 'N', 'center'), wall('room', 'E', 'center')], { pass: 'detail', count: 2 }),
    ] },
  ],
};
/** Quarters: bunks with footlockers, staff lockers, a table to sit at. Also the outpost's upper floors. */
const QUARTERS: FloorProgram = {
  id: 'quarters.bunks',
  zones: [
    zone('bunks', 'bunks', 0, { mandatory: true, minArea: 6, access: 'employee', requires: ['bed'] }),
    zone('lockers', 'lockers', 1, { access: 'employee', requires: ['storage'], dropOrder: 2 }),
    zone('common', 'common table', 2, { minArea: 24, requires: ['seat'], dropOrder: 1 }),
  ],
  arrangements: [
    { id: 'bunks', fills: [
      fill('bunks', ['bed.bunk'], [wall('room', 'E', 'start')], { count: 2, per: 30, max: 6 }),
      fill('bunks', ['bed.bunk'], [wall('room', 'N', 'end')], { count: 1, per: 60, max: 4 }),
      fill('lockers', ['store.lockers', 'store.wardrobe'], [wall('room', 'N', 'start'), wall('room', 'S', 'end')]),
      fill('common', ['table.pair', 'seat.reading'], [spot('main', 0.35, 0.5, 1), wall('room', 'S', 'start')]),
      // a big floor is a dormitory: bunks in rows either side of the aisle, a mess of long tables on the door side
      ...aisles('bunks', ['bed.bunk'], 1, 1.8, 2.0, { count: 1, per: 200, max: 10, bands: ['L'] }),
      fill('common', ['table.long', 'table.pair'], [grid('frontEast', 1, 1.6, 1.0)], { count: 1, per: 700, max: 4, bands: ['L'] }),
      fill('common', ['lounge.island', 'lounge.set'], [grid('frontWest', 0, 4, 2)], { count: 1, per: 900, max: 3, bands: ['L'] }),
      fill('common', ['detail.lamp', 'detail.rug'], [wall('room', 'S', 'start'), spot('main', 0.5, 0.5)], { pass: 'detail', pick: 'seed' }),
    ] },
  ],
};
const GUILD_COMPACT: FloorProgram = {
  id: 'guild.compact',
  zones: [
    zone('feast', 'the table', 0, { mandatory: true, minArea: 4, requires: ['seat'] }),
    zone('records', 'the roll', 1, { requires: ['storage'], dropOrder: 1 }),
  ],
  arrangements: [
    { id: 'lodge-room', fills: [
      fill('feast', ['table.long', 'table.pair', 'seat.waiting', 'seat.chair'], [grid('main', 1, 1.2, 0.5), spot('main', 0.5, 0.4), wall('room', 'E', 'center'), wall('room', 'S', 'end')], { count: 1, per: 40, max: 5 }),
      fill('records', ['store.shelf', 'store.chest'], [wall('room', 'E', 'end'), wall('room', 'N', 'start')], { count: 1, per: 60, max: 3 }),
      fill('feast', ['seat.waiting'], [wall('room', 'W', 'end'), wall('room', 'E', 'start'), wall('room', 'S', 'start')], { pass: 'secondary', count: 1, per: 50, max: 4 }),
      fill('records', ['detail.hearth', 'detail.column'], [wall('room', 'N', 'end'), wall('room', 'E', 'center')], { pass: 'detail' }),
    ] },
  ],
};

// ─── BANK — a public hall → the teller line with a staff side → the guarded vault ───────────────────────────────────

const BANK_HALL: FloorProgram = {
  id: 'bank.hall',
  zones: [
    zone('tellers', 'teller line', 0, { mandatory: true, minArea: 8, requires: ['service-staff', 'service-customer'], adjacentTo: ['vault'], separatedFrom: ['arrival'] }),
    zone('vault', 'vault', 1, { minArea: 12, access: 'guarded', requires: ['stand'], adjacentTo: ['tellers'], separatedFrom: ['arrival', 'hall'], enclosure: 'room', dropOrder: 2 }),
    zone('hall', 'public hall', 2, { minArea: 3, requires: ['seat'], dropOrder: 1 }),
    zone('consult', 'consultation', 3, { minArea: 50, requires: ['service-customer'], enclosure: 'screen', dropOrder: 3 }),
    zone('lounge', 'client lounge', 4, { minArea: 70, requires: ['seat'], dropOrder: 5 }),
    zone('backoffice', 'back office', 5, { minArea: 40, access: 'employee', requires: ['work'], separatedFrom: ['arrival', 'hall'], dropOrder: 4 }),
    zone('arrival', 'arrival', 6, { minArea: 0 }),
  ],
  arrangements: [
    // a branch: one two-position counter across the back, the strongroom behind its staff side
    { id: 'branch', bands: SMALL, fills: [
      fill('tellers', ['counter.teller.2', 'counter.kiosk'], [row('room', 'N', 2.6, 'center'), row('room', 'N', 2.2, 'center'), row('room', 'N', 1.6, 'center')]),
      fill('vault', ['vault.front', 'store.safes'], [wall('room', 'N', 'end'), wall('room', 'N', 'start')]),
      ...both('hall', ['seat.waiting', 'seat.chair'], [wall('room', 'E', 'start'), wall('frontEast', 'S', 'end')], [wall('room', 'W', 'end'), wall('frontWest', 'S', 'start')], { count: 1 }),
      fill('arrival', ['sign.screen'], [wall('frontWest', 'S', 'end'), wall('frontEast', 'S', 'start')], { pass: 'secondary' }),
      fill('hall', ['detail.plant'], [wall('room', 'S', 'start'), wall('room', 'S', 'end')], { pass: 'detail' }),
    ] },
    // the same branch with its counter on the east wall (a deed's console holds the back wall's centre)
    { id: 'branch-side', bands: SMALL, fills: [
      fill('tellers', ['counter.teller.2', 'counter.kiosk'], [row('room', 'E', 2.6, 'center'), row('room', 'E', 2.2, 'center'), row('room', 'E', 1.6, 'center'), row('room', 'E', 1.6, 'end')]),
      fill('vault', ['vault.front', 'store.safes'], [wall('room', 'E', 'end'), wall('room', 'N', 'end'), wall('room', 'E', 'start')]),
      ...both('hall', ['seat.waiting', 'seat.chair'], [wall('room', 'W', 'end'), wall('frontWest', 'S', 'start')], [wall('room', 'S', 'start'), wall('room', 'W', 'start')], { count: 1 }),
      fill('arrival', ['sign.screen'], [wall('frontWest', 'S', 'end'), wall('room', 'N', 'start')], { pass: 'secondary' }),
      fill('hall', ['detail.plant'], [wall('room', 'S', 'start'), wall('room', 'N', 'start')], { pass: 'detail' }),
    ] },
    { id: 'line', bands: ML,
      cuts: [band('back', 'secure', 4.7, 5.3, 0.36)],
      zoneRects: { tellers: ['secure'] },
      fills: [
        fill('tellers', ['counter.teller.3', 'counter.teller.2'], [row('secure', 'N', 3.6, 'center'), row('secure', 'N', 3.2, 'center')]),
        fill('tellers', ['counter.teller.2'], [row('secure', 'N', 3.6, 'start')], { bands: ['L'] }),
        fill('tellers', ['counter.teller.2'], [row('secure', 'N', 3.6, 'end')], { bands: ['L'] }),
        fill('vault', ['vault.front', 'store.safes'], [wall('secure', 'N', 'center'), wall('secure', 'N', 'end'), wall('secure', 'N', 'start')]),
        ...both('hall', ['seat.row'], [wall('room', 'E', 'start')], [wall('room', 'W', 'start')], { count: 1, per: 100, max: 3 }),
        fill('lounge', ['lounge.island'], [spot('main', 0.2, 0.2), spot('main', 0.8, 0.2)], { bands: ML }),
        fill('backoffice', ['desk.study'], [wall('secure', 'E', 'end'), wall('secure', 'W', 'end')], { count: 1, per: 150, max: 2, bands: ML }),
        fill('backoffice', ['store.safes', 'store.cabinet'], [wall('secure', 'N', 'start'), wall('secure', 'N', 'end')], { bands: ML }),
        fill('consult', ['desk.reception'], [spot('frontEast', 0.62, 0.5, 3), spot('frontWest', 0.38, 0.5, 1)]),
        fill('consult', ['screen.single'], [row('frontEast', 'S', 3.5, 'center')], { pass: 'secondary' }),
        fill('arrival', ['sign.screen'], [wall('frontWest', 'S', 'end'), wall('frontEast', 'S', 'start')], { pass: 'secondary' }),
        fill('hall', ['detail.plant'], [wall('room', 'S', 'start'), wall('room', 'S', 'end')], { pass: 'detail', count: 2 }),
        fill('tellers', ['detail.column'], [wall('secure', 'E', 'start'), wall('secure', 'W', 'start')], { pass: 'detail', count: 2 }),
      ] },
  ],
};
const BANK_COMPACT: FloorProgram = {
  id: 'bank.compact',
  zones: [
    // no `requires`: in a 6 m room the door's box leaves no counter a place (law 38) and a clerk's desk serves
    zone('tellers', 'teller', 0, { mandatory: true, minArea: 4 }),
    zone('vault', 'strongboxes', 1, { access: 'guarded', requires: ['storage'], dropOrder: 1 }),
    zone('hall', 'a bench', 2, { requires: ['seat'], dropOrder: 2 }),
  ],
  arrangements: [
    { id: 'kiosk', fills: [
      fill('tellers', ['counter.teller.2', 'counter.kiosk', 'counter.checkout'], [row('room', 'N', 2.3, 'center'), row('room', 'N', 1.6, 'center'), row('room', 'N', 1.15, 'center')]),
      fill('tellers', ['desk.study'], [wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'W', 'end')], { bands: ['XS'] }),
      fill('vault', ['vault.front', 'store.safes'], [wall('room', 'N', 'end'), wall('room', 'N', 'start'), wall('room', 'E', 'end')]),
      fill('hall', ['seat.waiting', 'seat.chair'], [wall('room', 'S', 'end'), wall('room', 'S', 'start'), wall('room', 'E', 'start'), wall('room', 'W', 'end')], { count: 1, per: 30, max: 5 }),
    ] },
    // the counter on the east wall: where a deed's console holds the back wall's centre
    { id: 'kiosk-side', fills: [
      fill('tellers', ['counter.teller.2', 'counter.kiosk', 'counter.checkout'], [row('room', 'E', 2.3, 'center'), row('room', 'E', 1.6, 'center'), row('room', 'E', 1.15, 'center'), row('room', 'E', 1.15, 'end')]),
      fill('vault', ['store.safes'], [wall('room', 'E', 'end'), wall('room', 'N', 'end'), wall('room', 'E', 'start')]),
      fill('hall', ['seat.waiting', 'seat.chair'], [wall('room', 'S', 'start'), wall('room', 'W', 'end'), wall('room', 'S', 'end')], { count: 1, per: 30, max: 5 }),
    ] },
  ],
};

// ─── THEATER — foyer → seating that faces a stage with a perform anchor ─────────────────────────────────────────────

const THEATER_HOUSE: FloorProgram = {
  id: 'theater.house',
  zones: [
    zone('stage', 'stage', 0, { mandatory: true, minArea: 8, access: 'employee', requires: ['perform'], separatedFrom: ['arrival'] }),
    zone('house', 'house', 1, { mandatory: true, minArea: 8, requires: ['seat'], adjacentTo: ['stage'] }),
    zone('foyer', 'foyer counter', 2, { minArea: 50, requires: ['service-staff', 'service-customer'], adjacentTo: ['arrival'], dropOrder: 2 }),
    zone('arrival', 'arrival', 3, { minArea: 0 }),
  ],
  arrangements: [
    { id: 'proscenium', cuts: HOUSE, fills: [
      fill('stage', ['perform.stage.wide'], BACK, { bands: ['L'] }),
      fill('stage', ['perform.stage', 'focus.dais'], BACK, { bands: ['XS', 'S', 'M'] }),
      ...house('house', ['seat.row'], 0.95, 0.4, { count: 1, per: 18, max: 12 }),
      fill('house', ['seat.row'], [grid('main', 0, 0.95, 0.4), wall('room', 'S', 'end')], { bands: SMALL, count: 2 }),
      fill('foyer', ['counter.kiosk'], [row('frontEast', 'S', 1.5, 'center'), row('frontWest', 'S', 1.5, 'center')]),
      fill('arrival', ['sign.screen'], [wall('frontWest', 'S', 'end'), wall('frontEast', 'S', 'start')], { pass: 'secondary' }),
      fill('house', ['seat.waiting'], [wall('room', 'E', 'start'), wall('room', 'W', 'start')], { pass: 'secondary', bands: ML }),
      fill('stage', ['detail.column'], [wall('room', 'E', 'end'), wall('room', 'W', 'end')], { pass: 'detail', count: 2 }),
      fill('house', ['detail.plant'], [wall('room', 'S', 'start'), wall('room', 'S', 'end')], { pass: 'detail' }),
    ] },
    // the stage on the east wall, the rows turned to face it (a deed's console holds the back wall's centre)
    { id: 'side-stage', aspect: 'wide', fills: [
      fill('stage', ['perform.stage.wide'], EAST, { bands: ['L'] }),
      fill('stage', ['perform.stage', 'focus.dais'], EAST, { bands: ['XS', 'S', 'M'] }),
      fill('house', ['seat.row'], [grid('main', 1, 0.95, 0.4), wall('room', 'S', 'end')], { count: 2, per: 9, max: 20 }),
      fill('foyer', ['counter.kiosk'], [row('frontWest', 'S', 1.5, 'center'), row('frontEast', 'S', 1.5, 'center')]),
      fill('arrival', ['sign.screen'], [wall('frontEast', 'S', 'start'), wall('frontWest', 'S', 'end')], { pass: 'secondary' }),
      ...both('house', ['seat.waiting'], [wall('room', 'W', 'end')], [wall('room', 'W', 'start')], { pass: 'secondary', count: 1, per: 200, max: 2, bands: ML }),
      fill('stage', ['detail.column'], [wall('room', 'N', 'end'), wall('room', 'N', 'start')], { pass: 'detail', count: 2 }),
      fill('house', ['detail.plant'], [wall('room', 'S', 'start'), wall('room', 'S', 'end')], { pass: 'detail' }),
    ] },
  ],
};
const THEATER_GREEN: FloorProgram = {
  id: 'theater.green',
  zones: [
    zone('green', 'green room', 0, { mandatory: true, access: 'employee', requires: ['seat'] }),
    zone('dress', 'dressing', 1, { access: 'employee', requires: ['storage'], dropOrder: 1 }),
    zone('table', 'table', 2, { requires: ['seat'], dropOrder: 2 }),
  ],
  arrangements: [
    { id: 'green', fills: [
      fill('green', ['lounge.set', 'seat.reading'], [wall('room', 'N', 'end'), wall('room', 'E', 'end')], { count: 1, per: 90, max: 3 }),
      fill('dress', ['store.wardrobe', 'store.chest'], [wall('room', 'N', 'start'), wall('room', 'E', 'start')], { count: 2, per: 50, max: 5 }),
      fill('table', ['table.pair'], [spot('main', 0.45, 0.5, 1), grid('mainEast', 1, 1.6, 1.2)], { count: 1, per: 110, max: 3 }),
      fill('dress', ['detail.mirror'], [wall('room', 'E', 'center'), wall('room', 'S', 'end')], { pass: 'detail', count: 2 }),
      fill('green', ['detail.lamp', 'detail.plant'], [wall('room', 'S', 'start')], { pass: 'detail', pick: 'seed' }),
    ] },
  ],
};
const THEATER_COMPACT: FloorProgram = {
  id: 'theater.compact',
  zones: [
    zone('stage', 'stage', 0, { mandatory: true, minArea: 5, access: 'employee', requires: ['perform'] }),
    zone('house', 'a bench', 1, { requires: ['seat'], dropOrder: 1 }),
  ],
  arrangements: [
    { id: 'studio', fills: [
      fill('stage', ['focus.dais', 'perform.corner'], BACK),
      fill('house', ['seat.row', 'seat.chair'], [grid('main', 0, 0.95, 0.4), wall('room', 'S', 'end'), wall('room', 'S', 'start'), wall('room', 'E', 'start')], { count: 1, per: 14, max: 10 }),
    ] },
    { id: 'studio-side', fills: [
      fill('stage', ['focus.dais', 'perform.corner'], EAST),
      fill('house', ['seat.row', 'seat.chair'], [grid('main', 1, 0.95, 0.4), wall('room', 'S', 'end'), wall('room', 'S', 'start'), wall('room', 'W', 'end')], { count: 1, per: 14, max: 10 }),
    ] },
  ],
};

// ─── MUSEUM — a visitor circuit past exhibits: along one wall, across the back, home down the other ────────────────

const museumFills = (foyer: boolean): Fill[] => [
  fill('east', ['exhibit.wall'], [wall('room', 'E', 'start')], { count: 1, per: 45, max: 4 }),
  fill('north', ['exhibit.wall'], [wall('room', 'N', 'end')], { count: 1, per: 60, max: 3 }),
  fill('west', ['exhibit.wall'], [wall('room', 'W', 'end'), wall('mainWest', 'W', 'end')], { count: 1, per: 60, max: 3 }),
  fill('islands', ['exhibit.case'], [grid('main', 0, 2.2, 2.6)], { count: 1, per: 45, max: 6 }),
  fill('heroes', ['exhibit.plinth'], foyer ? [spot('main', 0.5, 0.06), spot('frontEast', 0.2, 0.75)] : [spot('main', 0.5, 0.2), spot('main', 0.5, 0.75)], { count: 2 }),
  fill('rest', ['seat.row'], [spot('main', 0.5, 0.55), spot('main', 0.5, 0.7)]),
  ...(foyer ? [fill('foyer', ['desk.reception'], [spot('frontEast', 0.62, 0.5, 3), spot('frontWest', 0.38, 0.5, 1)])] : []),
  fill('east', ['sign.screen'], foyer ? [wall('frontWest', 'S', 'end'), wall('frontEast', 'S', 'start')] : [wall('room', 'S', 'end')], { pass: 'secondary' }),
  fill('rest', ['detail.column'], [wall('room', 'N', 'start'), wall('room', 'S', 'end'), wall('room', 'S', 'start')], { pass: 'detail', count: 2 }),
];
const museumZones = (foyer: boolean): ZoneSpec[] => [
  zone('east', 'first gallery wall', 0, { mandatory: true, minArea: 6, requires: ['display'] }),
  zone('north', 'far wall', 1, { minArea: 6, requires: ['display'], adjacentTo: ['east'], dropOrder: 2 }),
  zone('west', 'return wall', 2, { minArea: 6, requires: ['display'], adjacentTo: ['north'], dropOrder: 3 }),
  zone('islands', 'cases', 3, { minArea: 40, requires: ['display'], dropOrder: 4 }),
  zone('heroes', 'centrepieces', 4, { minArea: 20, requires: ['display'], dropOrder: 1 }),
  zone('rest', 'a bench to look from', 5, { minArea: 30, requires: ['seat'], dropOrder: 5 }),
  ...(foyer ? [zone('foyer', 'front desk', 6, { minArea: 50, requires: ['service-staff', 'service-customer'], adjacentTo: ['arrival'], dropOrder: 6 }), zone('arrival', 'arrival', 7, { minArea: 0 })] : []),
];
const MUSEUM_GALLERY: FloorProgram = { id: 'museum.gallery', zones: museumZones(true), arrangements: [{ id: 'circuit', fills: museumFills(true) }] };
const MUSEUM_UPPER: FloorProgram = { id: 'museum.upper', zones: museumZones(false), arrangements: [{ id: 'circuit', fills: museumFills(false) }] };
const MUSEUM_COMPACT: FloorProgram = {
  id: 'museum.compact',
  zones: [
    zone('east', 'exhibits', 0, { mandatory: true, minArea: 3, requires: ['display'] }),
    zone('rest', 'a bench', 1, { requires: ['seat'], dropOrder: 1 }),
  ],
  arrangements: [
    { id: 'cabinet', fills: [
      fill('east', ['exhibit.wall', 'display.wall'], [wall('room', 'E', 'center'), wall('room', 'N', 'end'), wall('room', 'W', 'end'), wall('room', 'N', 'start')], { count: 1, per: 25, max: 6 }),
      fill('east', ['exhibit.plinth'], [spot('main', 0.5, 0.4), spot('room', 0.6, 0.6)]),
      fill('rest', ['seat.waiting', 'seat.chair'], [wall('room', 'S', 'end'), wall('room', 'S', 'start'), wall('room', 'E', 'start')]),
    ] },
  ],
};

// ─── SALON — the work triangle: mirror, chair, tray; a wash unit; a place to wait ───────────────────────────────────

const SALON_FLOOR: FloorProgram = {
  id: 'salon.floor',
  zones: [
    zone('stations', 'styling stations', 0, { mandatory: true, minArea: 8, requires: ['seat', 'service-staff'] }),
    zone('waiting', 'waiting', 1, { minArea: 2, requires: ['seat'], adjacentTo: ['arrival'], dropOrder: 1 }),
    zone('wash', 'wash', 2, { minArea: 3, access: 'employee', requires: ['work'], adjacentTo: ['stations'], dropOrder: 2 }),
    zone('desk', 'front desk', 3, { minArea: 40, requires: ['service-staff', 'service-customer'], dropOrder: 4 }),
    zone('retail', 'retail shelf', 4, { minArea: 20, requires: ['display'], dropOrder: 3 }),
    zone('arrival', 'arrival', 5, { minArea: 0 }),
  ],
  arrangements: [
    { id: 'stations', fills: [
      fill('stations', ['salon.station'], [wall('room', 'E', 'end'), wall('room', 'N', 'end'), wall('room', 'E', 'start')], { count: 1, per: 22, max: 4 }),
      fill('stations', ['salon.station'], [wall('room', 'W', 'start')], { count: 1, per: 70, max: 3, bands: ML }),
      fill('waiting', ['seat.waiting', 'seat.chair'], [wall('frontWest', 'S', 'end'), wall('room', 'S', 'start'), wall('frontEast', 'S', 'start')]),
      fill('waiting', ['lounge.island', 'lounge.pods'], [spot('main', 0.5, 0.5), spot('main', 0.5, 0.65)], { bands: ML, pass: 'secondary' }),
      fill('wash', ['service.wash'], [wall('room', 'N', 'start'), wall('room', 'W', 'end'), wall('room', 'N', 'center')]),
      fill('desk', ['desk.reception'], [spot('main', 0.3, 0.1, 2), spot('main', 0.5, 0.2, 2)]),
      fill('retail', ['display.wall'], [wall('room', 'W', 'start'), wall('room', 'S', 'end')], { count: 2 }),
      fill('stations', ['screen.single'], [row('room', 'N', 3.9, 'end')], { pass: 'secondary', bands: ML }),
      fill('waiting', ['detail.plant', 'detail.lamp'], [wall('room', 'S', 'end'), wall('room', 'N', 'center')], { pass: 'detail', pick: 'seed' }),
    ] },
  ],
};
const SALON_COMPACT: FloorProgram = {
  id: 'salon.compact',
  zones: [
    zone('stations', 'the chair', 0, { mandatory: true, minArea: 4, requires: ['seat'] }),
    zone('waiting', 'a seat', 1, { requires: ['seat'], dropOrder: 1 }),
  ],
  arrangements: [
    { id: 'booth', fills: [
      fill('stations', ['salon.station', 'seat.chair'], [wall('room', 'E', 'end'), wall('room', 'N', 'end'), wall('room', 'W', 'end'), wall('room', 'S', 'end')], { count: 1, per: 25, max: 5 }),
      fill('waiting', ['seat.waiting', 'seat.chair'], [wall('room', 'S', 'end'), wall('room', 'S', 'start')]),
    ] },
  ],
};

// ─── GARAGE — the bay (a ride's footprint, kept clear), a bench, the parts ──────────────────────────────────────────

const GARAGE_FLOOR: FloorProgram = {
  id: 'garage.floor',
  zones: [
    zone('bay', 'vehicle bay', 0, { mandatory: true, minArea: 18, access: 'employee', requires: ['work'] }),
    zone('bench', 'service bench', 1, { minArea: 4, access: 'employee', requires: ['work'], adjacentTo: ['bay'], dropOrder: 3 }),
    zone('parts', 'parts', 2, { minArea: 2, access: 'employee', requires: ['storage'], adjacentTo: ['bench'], dropOrder: 2 }),
    zone('desk', 'work orders', 3, { minArea: 45, requires: ['service-staff', 'service-customer'], adjacentTo: ['arrival'], dropOrder: 4 }),
    zone('stock', 'stock', 4, { minArea: 2, access: 'employee', requires: ['storage'], dropOrder: 1 }),
    zone('arrival', 'arrival', 5, { minArea: 0 }),
  ],
  arrangements: [
    { id: 'bays', fills: [
      fill('bay', ['bay.vehicle'], [grid('mainEast', 0, 1.6, 1.3), grid('main', 1, 1.6, 0.9), grid('main', 0, 1.6, 0.9), grid('mainEast', 0, 1.6, 0.2), spot('main', 0.55, 0.6, 1), spot('room', 0.7, 0.55, 0), spot('room', 0.3, 0.55, 0)], { count: 1, per: 170, max: 3 }),
      fill('bay', ['bay.vehicle'], [grid('mainWest', 0, 1.6, 1.3)], { count: 1, per: 170, max: 3, bands: ['L'] }),
      // a one-bay garage has its bay against the east wall: the bench then stands on the west
      fill('bench', ['work.bench'], [wall('room', 'E', 'end'), wall('room', 'N', 'end'), wall('room', 'W', 'end'), wall('room', 'W', 'center')], { count: 1, per: 60, max: 4 }),
      fill('parts', ['rack.parts'], [wall('room', 'N', 'start'), wall('room', 'E', 'start')], { count: 1, per: 60, max: 4 }),
      fill('bench', ['work.island'], [spot('main', 0.5, 0.82), spot('main', 0.5, 0.5)], { bands: ['L'], pass: 'secondary' }),
      fill('desk', ['desk.reception'], [spot('frontEast', 0.62, 0.5, 3), spot('frontWest', 0.38, 0.5, 1)]),
      fill('stock', ['stock.crates', 'store.chest'], [wall('frontWest', 'S', 'end'), wall('room', 'S', 'end'), wall('room', 'W', 'end')]),
      fill('bench', ['store.lockers'], [wall('room', 'W', 'start'), wall('room', 'S', 'start')], { pass: 'secondary', bands: ML }),
      fill('bay', ['detail.column'], [wall('room', 'E', 'center'), wall('room', 'N', 'center')], { pass: 'detail', count: 2 }),
    ] },
  ],
};
const GARAGE_COMPACT: FloorProgram = {
  id: 'garage.compact',
  zones: [
    zone('bench', 'bench', 0, { mandatory: true, minArea: 3, access: 'employee', requires: ['work'] }),
    zone('parts', 'parts', 1, { requires: ['storage'], dropOrder: 1 }),
  ],
  arrangements: [
    { id: 'lockup', fills: [
      fill('bench', ['work.bench'], [wall('room', 'E', 'end'), wall('room', 'N', 'end'), wall('room', 'S', 'end'), wall('room', 'N', 'start')], { count: 1, per: 40, max: 4 }),
      fill('parts', ['rack.parts', 'store.chest'], [wall('room', 'N', 'start'), wall('room', 'E', 'start'), wall('room', 'S', 'start')], { count: 1, per: 40, max: 4 }),
      fill('parts', ['stock.crates'], [wall('room', 'W', 'end'), wall('room', 'S', 'end')], { pass: 'secondary' }),
    ] },
  ],
};

// ─── TRANSIT — the queue → the counter → the wait (and the gate) ────────────────────────────────────────────────────

const TRANSIT_CONCOURSE: FloorProgram = {
  id: 'transit.concourse',
  zones: [
    zone('ticketing', 'ticket counter', 0, { mandatory: true, minArea: 10, requires: ['service-staff', 'service-customer'], adjacentTo: ['arrival'] }),
    zone('waiting', 'waiting', 1, { mandatory: true, minArea: 6, requires: ['seat'], adjacentTo: ['boards'] }),
    zone('boards', 'departure boards', 2, { minArea: 0, requires: ['display'], dropOrder: 1 }),
    zone('gate', 'boarding gate', 3, { minArea: 50, access: 'guarded', requires: ['stand'], separatedFrom: ['arrival'], dropOrder: 3 }),
    zone('luggage', 'luggage', 4, { minArea: 3, access: 'employee', requires: ['storage'], dropOrder: 2 }),
    zone('arrival', 'arrival', 5, { minArea: 0 }),
  ],
  arrangements: [
    // the waiting rows are BLOCKS by the gates (the back half either side of the aisle), not two files down the hall:
    // a grid takes the cells nearest the axis first, so the block's depth is what makes it wide
    { id: 'concourse', cuts: [band('back', 'waitE', 6, 10, 0.5, 'mainEast'), band('back', 'waitW', 6, 10, 0.5, 'mainWest')], fills: [
      fill('ticketing', ['counter.ticket', 'counter.kiosk'], [row('mainEast', 'E', 1.6, 'start'), row('room', 'E', 1.6, 'center'), row('room', 'N', 2.4, 'center')]),
      fill('waiting', ['seat.row'], [grid('waitW', 0, 1.0, 0.8), grid('mainWest', 0, 1.0, 0.6)], { count: 1, per: 26, max: 10 }),
      fill('waiting', ['seat.row'], [grid('waitE', 0, 1.0, 0.8), grid('mainEast', 0, 1.0, 0.6)], { count: 1, per: 40, max: 8 }),
      fill('waiting', ['seat.row', 'seat.waiting'], [grid('main', 0, 1.0, 0.4), wall('room', 'S', 'start'), wall('room', 'W', 'end'), wall('room', 'S', 'end')], { bands: SMALL, count: 2 }),
      fill('boards', ['sign.screen'], [wall('room', 'N', 'center'), wall('room', 'N', 'start')], { count: 2 }),
      // never on the east wall: that is the staff side of the ticket counter
      fill('gate', ['gate.boarding'], [wall('room', 'N', 'end'), wall('room', 'N', 'start'), wall('room', 'W', 'end')]),
      fill('luggage', ['stock.crates', 'store.chest'], [wall('room', 'W', 'end'), wall('frontWest', 'S', 'end'), wall('room', 'S', 'start')]),
      fill('waiting', ['seat.waiting'], [wall('frontWest', 'S', 'start'), wall('room', 'W', 'start')], { pass: 'secondary' }),
      fill('arrival', ['sign.screen'], [wall('frontEast', 'S', 'start')], { pass: 'secondary' }),
      fill('waiting', ['detail.plant'], [wall('room', 'S', 'start'), wall('room', 'S', 'end')], { pass: 'detail', count: 2 }),
    ] },
  ],
};
const TRANSIT_LOUNGE: FloorProgram = {
  id: 'transit.lounge',
  zones: [
    zone('lounge', 'departure lounge', 0, { mandatory: true, minArea: 6, requires: ['seat'] }),
    zone('tables', 'tables', 1, { minArea: 20, requires: ['seat'], dropOrder: 1 }),
    zone('lockers', 'lockers', 2, { requires: ['storage'], dropOrder: 2 }),
  ],
  arrangements: [
    { id: 'lounge', fills: [
      fill('lounge', ['lounge.set', 'seat.waiting'], [wall('room', 'N', 'end'), wall('room', 'E', 'start')], { count: 2, per: 50, max: 4 }),
      fill('tables', ['table.pair'], [grid('mainEast', 1, 1.3, 1.2)], { count: 1, per: 60, max: 4 }),
      fill('lockers', ['store.lockers', 'store.cabinet'], [wall('room', 'N', 'start'), wall('room', 'S', 'end')]),
      fill('lounge', ['sign.screen'], [wall('room', 'E', 'center'), wall('room', 'S', 'start')], { pass: 'secondary' }),
      fill('lounge', ['detail.plant', 'detail.lamp'], [wall('room', 'S', 'end'), wall('room', 'E', 'end')], { pass: 'detail', pick: 'seed', count: 2 }),
    ] },
  ],
};
const TRANSIT_OPS: FloorProgram = {
  id: 'transit.operations',
  zones: [
    zone('control', 'control desks', 0, { mandatory: true, minArea: 6, access: 'employee', requires: ['work'] }),
    zone('briefing', 'briefing table', 1, { minArea: 30, access: 'employee', requires: ['stand'], dropOrder: 2 }),
    zone('boards', 'traffic boards', 2, { access: 'employee', requires: ['display'], dropOrder: 1 }),
    zone('lockers', 'crew lockers', 3, { access: 'employee', requires: ['storage'], dropOrder: 3 }),
  ],
  arrangements: [
    { id: 'control', fills: [
      fill('control', ['desk.pod', 'desk.study'], [grid('mainEast', 1, 1.2, 0.8), wall('room', 'N', 'end')], { count: 1, per: 50, max: 4 }),
      fill('briefing', ['table.briefing'], [spot('main', 0.3, 0.6), spot('main', 0.5, 0.5, 1)]),
      fill('boards', ['sign.screen'], [wall('room', 'N', 'center'), wall('room', 'E', 'center')], { count: 2 }),
      fill('lockers', ['store.lockers', 'store.cabinet'], [wall('room', 'S', 'end'), wall('room', 'N', 'start')]),
      fill('control', ['detail.column'], [wall('room', 'E', 'end'), wall('room', 'S', 'start')], { pass: 'detail', count: 2 }),
    ] },
  ],
};
const TRANSIT_COMPACT: FloorProgram = {
  id: 'transit.compact',
  zones: [
    zone('ticketing', 'counter', 0, { mandatory: true, minArea: 4 }),
    zone('waiting', 'a bench', 1, { requires: ['seat'], dropOrder: 1 }),
  ],
  arrangements: [
    { id: 'halt', fills: [
      fill('ticketing', ['counter.kiosk', 'counter.checkout'], [row('room', 'N', 1.5, 'center'), row('room', 'N', 1.15, 'center'), row('room', 'N', 1.15, 'end')]),
      fill('ticketing', ['desk.study'], [wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'W', 'end')], { bands: ['XS'] }),
      fill('waiting', ['seat.waiting', 'seat.chair'], [wall('room', 'S', 'end'), wall('room', 'S', 'start'), wall('room', 'E', 'start'), wall('room', 'W', 'end')], { count: 1, per: 22, max: 6 }),
    ] },
    { id: 'halt-side', fills: [
      fill('ticketing', ['counter.kiosk', 'counter.checkout'], [row('room', 'E', 1.5, 'center'), row('room', 'E', 1.15, 'center'), row('room', 'E', 1.15, 'end')]),
      fill('waiting', ['seat.waiting', 'seat.chair'], [wall('room', 'S', 'start'), wall('room', 'W', 'end'), wall('room', 'S', 'end'), wall('room', 'N', 'start')], { count: 1, per: 22, max: 6 }),
    ] },
  ],
};

// ─── WORKSHOP / FACTORY FLOOR — small: benches round the walls; large: the line, in the order the material moves ───

const WORKSHOP_FLOOR: FloorProgram = {
  id: 'workshop.floor',
  zones: [
    zone('bench', 'benches', 0, { mandatory: true, minArea: 5, access: 'employee', requires: ['work'] }),
    zone('line', 'process line', 1, { minArea: 60, access: 'employee', requires: ['work'], separatedFrom: ['arrival'], dropOrder: 3 }),
    zone('parts', 'inputs', 2, { minArea: 2, access: 'employee', requires: ['storage'], adjacentTo: ['bench'], dropOrder: 1 }),
    zone('stock', 'outputs', 3, { minArea: 3, access: 'employee', requires: ['storage'], dropOrder: 2 }),
    zone('order', 'orders and dispatch', 4, { minArea: 40, requires: ['service-staff', 'service-customer'], adjacentTo: ['arrival'], dropOrder: 5 }),
    zone('refuge', 'staff refuge', 5, { minArea: 50, access: 'employee', requires: ['seat'], separatedFrom: ['line'], enclosure: 'screen', dropOrder: 4 }),
    zone('arrival', 'arrival', 6, { minArea: 0 }),
  ],
  arrangements: [
    { id: 'benches', bands: ['XS', 'S', 'M'], fills: [
      fill('bench', ['work.bench'], [wall('room', 'E', 'start'), wall('room', 'N', 'end')], { count: 1, per: 22, max: 4 }),
      fill('line', ['work.fab'], [wall('room', 'N', 'end'), wall('room', 'E', 'end')], { count: 1, per: 45, max: 3 }),
      fill('parts', ['rack.parts'], [wall('room', 'N', 'start'), wall('room', 'E', 'end')]),
      fill('stock', ['stock.crates', 'store.chest'], [wall('frontEast', 'S', 'start'), wall('room', 'S', 'end'), wall('room', 'S', 'start')]),
      // a medium floor is not a ring of machines round a bare middle: bench islands stand in it
      fill('bench', ['work.island'], [grid('main', 0, 2.4, 3.2)], { count: 1, per: 80, max: 3, bands: ['M'] }),
      fill('order', ['desk.reception'], [spot('main', 0.3, 0.08, 2), spot('main', 0.5, 0.2, 2)]),
      fill('refuge', ['seat.reading', 'seat.chair'], [wall('room', 'W', 'end'), wall('room', 'S', 'start')]),
      fill('order', ['sign.screen'], [wall('frontWest', 'S', 'end'), wall('room', 'S', 'start')], { pass: 'secondary' }),
      fill('bench', ['detail.column'], [wall('room', 'E', 'center'), wall('room', 'N', 'center')], { pass: 'detail', count: 2 }),
    ] },
    { id: 'line', bands: ML, fills: [
      fill('line', ['work.line'], [wall('room', 'N', 'start')]),
      fill('line', ['work.line'], [wall('room', 'E', 'end')], { bands: ['L'] }),
      fill('bench', ['work.bench'], [wall('room', 'E', 'start'), wall('room', 'W', 'end')], { count: 1, per: 60, max: 3 }),
      fill('bench', ['work.island'], [grid('main', 0, 2.4, 3.2)], { count: 1, per: 110, max: 4 }),
      fill('parts', ['rack.parts'], [wall('room', 'W', 'end'), wall('room', 'S', 'end')], { count: 1, per: 80, max: 3 }),
      fill('stock', ['stock.crates'], [wall('frontEast', 'S', 'start'), wall('room', 'S', 'end')], { count: 1, per: 90, max: 3 }),
      fill('order', ['desk.reception'], [spot('main', 0.28, 0.06, 2), spot('main', 0.5, 0.15, 2)]),
      fill('refuge', ['store.lockers'], [wall('frontWest', 'S', 'end'), wall('room', 'W', 'start')]),
      fill('refuge', ['seat.reading'], [wall('room', 'W', 'start'), wall('room', 'S', 'start')], { pass: 'secondary' }),
      fill('order', ['sign.screen'], [wall('frontEast', 'S', 'end'), wall('room', 'S', 'start')], { pass: 'secondary' }),
      fill('line', ['detail.column'], [wall('room', 'E', 'center'), wall('room', 'N', 'end')], { pass: 'detail', count: 2 }),
    ] },
  ],
};
/** Upstairs of a workshop, a mine office or a garage: the stores — racks, crates, a desk for the ledger. */
const WORKS_STORE: FloorProgram = {
  id: 'works.store',
  zones: [
    zone('stock', 'stores', 0, { mandatory: true, minArea: 3, access: 'employee', requires: ['storage'] }),
    zone('crates', 'crates', 1, { access: 'employee', requires: ['storage'], dropOrder: 1 }),
    zone('office', 'ledger desk', 2, { access: 'employee', requires: ['work'], dropOrder: 2 }),
  ],
  arrangements: [
    { id: 'stores', fills: [
      fill('stock', ['rack.parts'], [wall('room', 'E', 'start'), wall('room', 'N', 'end')], { count: 2, per: 30, max: 6 }),
      fill('crates', ['stock.crates'], [wall('room', 'N', 'start'), wall('room', 'S', 'end')], { count: 1, per: 50, max: 4 }),
      // a bigger store stacks its floor too: pallets of crates in rows either side of the aisle
      ...aisles('crates', ['stock.crates'], 0, 1.8, 1.5, { count: 1, per: 90, max: 6, bands: ML }),
      fill('office', ['desk.study'], [wall('room', 'S', 'end'), wall('room', 'S', 'start'), wall('room', 'E', 'end')]),
      fill('office', ['detail.lamp'], [wall('room', 'S', 'start'), wall('room', 'E', 'center')], { pass: 'detail' }),
    ] },
  ],
};
const WORKSHOP_COMPACT: FloorProgram = {
  id: 'workshop.compact',
  zones: [
    zone('bench', 'bench', 0, { mandatory: true, minArea: 3, access: 'employee', requires: ['work'] }),
    zone('parts', 'parts', 1, { requires: ['storage'], dropOrder: 1 }),
  ],
  arrangements: [
    { id: 'bench', fills: [
      fill('bench', ['work.bench', 'work.fab'], [wall('room', 'E', 'end'), wall('room', 'N', 'end'), wall('room', 'S', 'end'), wall('room', 'N', 'start')], { count: 1, per: 35, max: 5 }),
      fill('parts', ['rack.parts', 'store.chest'], [wall('room', 'N', 'start'), wall('room', 'E', 'start'), wall('room', 'S', 'start')], { count: 1, per: 45, max: 4 }),
      fill('parts', ['stock.crates'], [wall('room', 'W', 'end'), wall('room', 'S', 'end')], { pass: 'secondary' }),
    ] },
  ],
};

// ─── MINING SUPPORT — dispatch → change room, maps, repair, samples (a support building; the mine is not in here) ───

const MINING_SUPPORT: FloorProgram = {
  id: 'mining.support',
  zones: [
    zone('dispatch', 'dispatch', 0, { mandatory: true, minArea: 6, requires: ['service-staff', 'service-customer'], adjacentTo: ['arrival'] }),
    zone('samples', 'sample handling', 1, { minArea: 2, access: 'employee', requires: ['storage'], dropOrder: 1 }),
    zone('change', 'change room', 2, { minArea: 6, access: 'employee', requires: ['storage'], enclosure: 'screen', dropOrder: 3 }),
    zone('repair', 'equipment repair', 3, { minArea: 5, access: 'employee', requires: ['work'], dropOrder: 2 }),
    zone('maps', 'map table', 4, { minArea: 45, access: 'employee', requires: ['stand'], dropOrder: 4 }),
    zone('arrival', 'arrival', 5, { minArea: 0 }),
  ],
  arrangements: [
    { id: 'shift', fills: [
      fill('dispatch', ['desk.reception'], [spot('main', 0.74, 0.08, 2), spot('main', 0.5, 0.2, 2), spot('main', 0.3, 0.08, 2)]),
      fill('samples', ['rack.parts'], [wall('room', 'N', 'start'), wall('room', 'N', 'center')], { count: 1, per: 50, max: 3 }),
      fill('change', ['store.lockers'], [wall('room', 'W', 'end'), wall('frontWest', 'S', 'end'), wall('room', 'S', 'start')], { count: 1, per: 70, max: 3 }),
      fill('repair', ['work.bench'], [wall('room', 'E', 'end'), wall('room', 'N', 'end')], { count: 1, per: 60, max: 3 }),
      fill('maps', ['table.briefing'], [spot('main', 0.42, 0.55), spot('main', 0.5, 0.6, 1)]),
      fill('samples', ['stock.crates'], [wall('room', 'N', 'end'), wall('room', 'E', 'start')], { pass: 'secondary' }),
      fill('dispatch', ['sign.screen'], [wall('frontEast', 'S', 'start'), wall('frontWest', 'S', 'end')], { pass: 'secondary' }),
      fill('change', ['screen.single'], [row('room', 'W', 3.4, 'end')], { pass: 'secondary', bands: ML }),
      fill('repair', ['detail.column'], [wall('room', 'E', 'center'), wall('room', 'S', 'end')], { pass: 'detail', count: 2 }),
    ] },
  ],
};

// ─── OUTPOST / BUNKER — a guarded threshold, a briefing table, the armoury, bunks ───────────────────────────────────

const OUTPOST_POST: FloorProgram = {
  id: 'outpost.post',
  zones: [
    zone('briefing', 'briefing', 0, { mandatory: true, minArea: 9, access: 'employee', requires: ['stand'] }),
    zone('stores', 'stores', 1, { minArea: 2, access: 'employee', requires: ['storage'], dropOrder: 1 }),
    zone('armory', 'armoury', 2, { minArea: 2, access: 'guarded', requires: ['storage'], separatedFrom: ['arrival'], dropOrder: 2 }),
    zone('quarters', 'bunks', 3, { minArea: 30, access: 'employee', requires: ['bed'], separatedFrom: ['arrival'], dropOrder: 4 }),
    zone('comms', 'comms desk', 4, { minArea: 20, access: 'employee', requires: ['work'], dropOrder: 3 }),
    zone('guard', 'guarded threshold', 5, { minArea: 34, access: 'guarded', requires: ['stand'], adjacentTo: ['arrival'], dropOrder: 5 }),
    zone('arrival', 'arrival', 6, { minArea: 0 }),
  ],
  arrangements: [
    { id: 'post', fills: [
      fill('briefing', ['table.briefing'], [spot('main', 0.5, 0.35), spot('main', 0.5, 0.5, 1), spot('room', 0.6, 0.6)]),
      fill('stores', ['stock.crates', 'store.chest'], [wall('room', 'N', 'end'), wall('room', 'E', 'end')], { count: 1, per: 60, max: 3 }),
      fill('armory', ['store.weapons'], [wall('room', 'N', 'start'), wall('room', 'N', 'center')]),
      fill('quarters', ['bed.bunk'], [wall('room', 'E', 'start'), wall('room', 'W', 'end')], { count: 1, per: 40, max: 4 }),
      fill('comms', ['desk.study'], [wall('room', 'W', 'end'), wall('room', 'S', 'end'), wall('room', 'E', 'center')]),
      fill('guard', ['guard.post'], [wall('frontEast', 'S', 'start'), wall('frontWest', 'S', 'end')]),
      fill('comms', ['sign.screen'], [wall('room', 'S', 'start'), wall('room', 'N', 'center')], { pass: 'secondary' }),
      fill('briefing', ['detail.column'], [wall('room', 'E', 'center'), wall('room', 'S', 'end')], { pass: 'detail' }),
    ] },
  ],
};
const OUTPOST_COMPACT: FloorProgram = {
  id: 'outpost.compact',
  zones: [
    zone('stores', 'stores', 0, { mandatory: true, minArea: 2, access: 'employee', requires: ['storage'] }),
    zone('quarters', 'a bunk', 1, { requires: ['bed'], dropOrder: 1 }),
    zone('comms', 'a desk', 2, { requires: ['work'], dropOrder: 2 }),
  ],
  arrangements: [
    { id: 'dugout', fills: [
      fill('stores', ['store.weapons', 'store.chest'], [wall('room', 'N', 'start'), wall('room', 'E', 'end'), wall('room', 'S', 'end')], { count: 1, per: 50, max: 4 }),
      fill('quarters', ['bed.bunk'], [wall('room', 'N', 'end'), wall('room', 'E', 'start'), wall('room', 'S', 'end'), wall('room', 'W', 'end')], { count: 1, per: 30, max: 6 }),
      fill('comms', ['desk.study'], [wall('room', 'S', 'end'), wall('room', 'E', 'center')]),
    ] },
  ],
};

// ─── LODGE / LOUNGE — somewhere to sit by a fire; never a bedroom ───────────────────────────────────────────────────

const LODGE_LOUNGE: FloorProgram = {
  id: 'lodge.lounge',
  zones: [
    zone('lounge', 'lounge', 0, { mandatory: true, minArea: 6, requires: ['seat'] }),
    zone('hearth', 'hearth', 1, { minArea: 2, requires: ['stand'], adjacentTo: ['lounge'], dropOrder: 1 }),
    zone('table', 'card table', 2, { minArea: 20, requires: ['seat'], dropOrder: 2 }),
    zone('bar', 'bar', 3, { minArea: 40, requires: ['service-staff', 'service-customer'], dropOrder: 4 }),
    zone('nook', 'window seat', 4, { minArea: 16, requires: ['seat'], dropOrder: 3 }),
    zone('arrival', 'arrival', 5, { minArea: 0 }),
  ],
  arrangements: [
    { id: 'fireside', fills: [
      fill('lounge', ['lounge.island'], [spot('main', 0.5, 0.45), spot('main', 0.55, 0.55)], { bands: ML }),
      // a big lounge is several places to sit, not one: more islands across the floor, sets along both long walls
      fill('lounge', ['lounge.island'], [grid('mainEast', 0, 2.2, 1.6)], { bands: ['L'], count: 1, per: 200, max: 3 }),
      fill('lounge', ['lounge.island'], [grid('mainWest', 0, 2.2, 1.6)], { bands: ['L'], count: 1, per: 200, max: 3 }),
      ...both('lounge', ['lounge.set', 'lounge.pods', 'seat.reading'], [wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'S', 'end')], [wall('room', 'W', 'start'), wall('room', 'N', 'start')], { count: 1, per: 220, max: 3 }),
      fill('hearth', ['detail.hearth'], [wall('room', 'N', 'center'), wall('room', 'E', 'center'), wall('room', 'N', 'start')]),
      fill('table', ['table.pair'], [spot('mainEast', 0.5, 0.75, 1), spot('main', 0.3, 0.5, 1), spot('frontEast', 0.55, 0.55, 1)]),
      fill('table', ['table.pair', 'table.stools'], [grid('mainEast', 1, 1.6, 1.2), grid('mainWest', 1, 1.6, 1.2)], { bands: ['L'], count: 1, per: 140, max: 4 }),
      fill('bar', ['counter.service.2', 'counter.kiosk'], [row('room', 'W', 1.6, 'end'), row('room', 'E', 1.6, 'start')]),
      fill('nook', ['lounge.pods', 'seat.reading'], [wall('room', 'E', 'start'), wall('room', 'S', 'start'), wall('room', 'W', 'end')], { count: 1, per: 180, max: 3 }),
      fill('lounge', ['detail.rug'], [spot('main', 0.5, 0.45)], { pass: 'detail', bands: SMALL }),
      fill('nook', ['detail.plant', 'detail.lamp'], [wall('room', 'S', 'start'), wall('room', 'S', 'end'), wall('room', 'N', 'start')], { pass: 'detail', pick: 'seed', count: 2 }),
    ] },
  ],
};
const LODGE_COMPACT: FloorProgram = {
  id: 'lodge.compact',
  zones: [zone('lounge', 'a seat by the stove', 0, { mandatory: true, minArea: 3, requires: ['seat'] }), zone('hearth', 'stove', 1, { requires: ['stand'], dropOrder: 1 })],
  arrangements: [
    { id: 'snug', fills: [
      fill('lounge', ['lounge.set', 'seat.reading', 'seat.chair'], [wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'S', 'end'), wall('room', 'W', 'end')], { count: 1, per: 30, max: 5 }),
      fill('hearth', ['detail.hearth', 'detail.lamp'], [wall('room', 'N', 'start'), wall('room', 'E', 'start'), wall('room', 'S', 'start')]),
    ] },
  ],
};

// ─── SHED — the garden workshop ──────────────────────────────────────────────────────────────────────────────────────

const SHED_WORKSHOP: FloorProgram = {
  id: 'shed.workshop',
  zones: [
    zone('bench', 'bench', 0, { mandatory: true, minArea: 4, requires: ['work'] }),
    zone('tools', 'tools', 1, { minArea: 2, requires: ['storage'], adjacentTo: ['bench'], dropOrder: 1 }),
    zone('potting', 'potting trough', 2, { minArea: 14, requires: ['work'], dropOrder: 2 }),
    zone('store', 'crates', 3, { minArea: 3, requires: ['storage'], dropOrder: 3 }),
    zone('seat', 'a stool', 4, { minArea: 18, requires: ['seat'], dropOrder: 4 }),
  ],
  arrangements: [
    { id: 'potting', fills: [
      fill('bench', ['work.bench'], [wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'E', 'start')]),
      fill('tools', ['rack.parts', 'store.shelf'], [wall('room', 'E', 'start'), wall('room', 'N', 'start'), wall('room', 'S', 'end')]),
      fill('potting', ['garden.planter'], [wall('room', 'W', 'end'), wall('room', 'N', 'start'), wall('room', 'E', 'center')]),
      fill('store', ['stock.crates', 'store.chest'], [wall('room', 'S', 'end'), wall('room', 'S', 'start')]),
      fill('seat', ['seat.stools', 'seat.chair'], [spot('main', 0.45, 0.45), wall('room', 'S', 'start')]),
      fill('potting', ['detail.plant'], [wall('room', 'S', 'start'), wall('room', 'W', 'start'), wall('room', 'N', 'center')], { pass: 'detail', count: 2 }),
      fill('bench', ['detail.lamp'], [wall('room', 'E', 'center'), wall('room', 'N', 'center')], { pass: 'detail' }),
    ] },
  ],
};
const SHED_COMPACT: FloorProgram = {
  id: 'shed.compact',
  zones: [zone('bench', 'bench', 0, { mandatory: true, minArea: 2, requires: ['storage'] }), zone('seat', 'a stool', 1, { requires: ['seat'], dropOrder: 1 })],
  arrangements: [
    { id: 'store', fills: [
      fill('bench', ['store.shelf', 'store.chest'], [wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'S', 'end'), wall('room', 'W', 'end')], { count: 1, per: 25, max: 6 }),
      fill('seat', ['seat.chair'], [wall('room', 'S', 'start'), wall('room', 'E', 'start')]),
    ] },
  ],
};

// ─── GARDEN ROOM / POOL ANNEX — loungers, green, something cold; not a bedroom ──────────────────────────────────────

const GARDEN_ROOM: FloorProgram = {
  id: 'garden.room',
  zones: [
    zone('lounge', 'loungers', 0, { mandatory: true, minArea: 5, requires: ['seat'] }),
    zone('green', 'planting', 1, { minArea: 0, dropOrder: 1 }),
    zone('refresh', 'refreshments', 2, { minArea: 6, requires: ['work'], dropOrder: 3 }),
    zone('table', 'table', 3, { minArea: 22, requires: ['seat'], dropOrder: 2 }),
    zone('potting', 'planter trough', 4, { minArea: 30, requires: ['work'], dropOrder: 4 }),
    zone('arrival', 'arrival', 5, { minArea: 0 }),
  ],
  arrangements: [
    { id: 'poolside', fills: [
      fill('lounge', ['lounge.pods', 'lounge.set', 'seat.reading'], [wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'S', 'end')]),
      fill('green', ['garden.green'], [wall('room', 'E', 'start'), wall('room', 'W', 'end'), wall('room', 'S', 'start'), wall('room', 'N', 'start')], { count: 2, per: 30, max: 5 }),
      fill('refresh', ['kitchen.galley'], [wall('room', 'N', 'start'), wall('room', 'E', 'center')]),
      fill('table', ['table.pair'], [spot('main', 0.42, 0.45, 1), spot('main', 0.5, 0.55)]),
      fill('potting', ['garden.planter'], [wall('room', 'W', 'start'), wall('room', 'S', 'end'), wall('room', 'E', 'center')]),
      fill('lounge', ['detail.rug'], [spot('main', 0.6, 0.6)], { pass: 'detail' }),
      fill('lounge', ['detail.lamp'], [wall('room', 'S', 'end'), wall('room', 'N', 'center')], { pass: 'detail' }),
    ] },
  ],
};
const GARDEN_COMPACT: FloorProgram = {
  id: 'garden.compact',
  zones: [zone('lounge', 'a seat', 0, { mandatory: true, minArea: 2, requires: ['seat'] }), zone('green', 'a planter', 1, { dropOrder: 1 })],
  arrangements: [
    { id: 'bower', fills: [
      fill('lounge', ['lounge.pods', 'seat.reading', 'seat.chair'], [wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'S', 'end'), wall('room', 'W', 'end')], { count: 1, per: 30, max: 5 }),
      fill('green', ['garden.green', 'detail.plant'], [wall('room', 'N', 'start'), wall('room', 'E', 'start'), wall('room', 'S', 'start')], { count: 1, per: 30, max: 5 }),
    ] },
  ],
};

// ─── GENERIC — the marker fallback: a clear entry and one flexible, sittable room ───────────────────────────────────

const GENERIC_HALL: FloorProgram = {
  id: 'generic.hall',
  zones: [
    zone('sit', 'table and seats', 0, { mandatory: true, minArea: 4, requires: ['seat'] }),
    zone('shelf', 'shelf', 1, { minArea: 2, requires: ['storage'], dropOrder: 1 }),
    zone('bench', 'bench by the door', 2, { minArea: 14, requires: ['seat'], adjacentTo: ['arrival'], dropOrder: 2 }),
    zone('corner', 'second corner', 3, { minArea: 60, requires: ['seat'], dropOrder: 3 }),
    zone('arrival', 'arrival', 4, { minArea: 0 }),
  ],
  arrangements: [
    { id: 'room', fills: [
      fill('sit', ['table.pair', 'seat.stools'], [spot('main', 0.5, 0.4), spot('room', 0.62, 0.62), spot('main', 0.5, 0.5, 1)]),
      fill('sit', ['table.pair'], [grid('mainEast', 1, 1.6, 1.4)], { count: 1, per: 120, max: 3, bands: ML }),
      fill('sit', ['table.pair'], [grid('mainWest', 1, 1.6, 1.4)], { count: 1, per: 120, max: 3, bands: ['L'] }),
      ...both('shelf', ['store.shelf', 'store.chest'], [wall('room', 'E', 'center'), wall('room', 'N', 'end')], [wall('room', 'W', 'end'), wall('room', 'N', 'start')], { count: 1, per: 90, max: 3 }),
      ...both('bench', ['seat.waiting', 'seat.chair'], [wall('frontWest', 'S', 'end'), wall('room', 'S', 'start')], [wall('frontEast', 'S', 'start'), wall('room', 'S', 'end')], { count: 1, per: 200, max: 2 }),
      fill('corner', ['lounge.set', 'seat.reading'], [wall('room', 'N', 'end'), wall('room', 'E', 'end')]),
      fill('corner', ['lounge.island'], [spot('main', 0.5, 0.78)], { bands: ['L'], pass: 'secondary' }),
      fill('sit', ['detail.rug'], [spot('main', 0.5, 0.4)], { pass: 'detail' }),
      fill('shelf', ['detail.lamp', 'detail.plant'], [wall('room', 'S', 'end'), wall('room', 'E', 'start'), wall('room', 'N', 'center')], { pass: 'detail', pick: 'seed', count: 2 }),
    ] },
  ],
};
const GENERIC_UPPER: FloorProgram = {
  id: 'generic.upper',
  zones: [
    zone('sit', 'sitting', 0, { mandatory: true, minArea: 3, requires: ['seat'] }),
    zone('store', 'storage', 1, { requires: ['storage'], dropOrder: 1 }),
    zone('desk', 'a desk', 2, { minArea: 16, requires: ['work'], dropOrder: 2 }),
    zone('table', 'a table', 3, { minArea: 40, requires: ['seat'], dropOrder: 3 }),
  ],
  arrangements: [
    { id: 'loft', fills: [
      fill('sit', ['lounge.set', 'seat.reading', 'seat.chair'], [wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'S', 'end')], { count: 1, per: 70, max: 4 }),
      ...both('store', ['store.cabinet', 'store.chest'], [wall('room', 'N', 'start')], [wall('room', 'E', 'start')], { count: 1, per: 90, max: 3 }),
      fill('desk', ['desk.study'], [wall('room', 'E', 'center'), wall('room', 'S', 'end')], { count: 1, per: 60, max: 4 }),
      fill('table', ['table.pair'], [grid('mainEast', 1, 1.6, 1.2), spot('main', 0.5, 0.5, 1)], { count: 1, per: 90, max: 6 }),
      fill('sit', ['lounge.island', 'lounge.pods'], [grid('mainWest', 0, 4, 2.5)], { count: 1, per: 350, max: 5, bands: ['L'] }),
      fill('sit', ['detail.lamp', 'detail.rug'], [wall('room', 'S', 'start'), spot('main', 0.5, 0.5)], { pass: 'detail', pick: 'seed' }),
      fill('store', ['detail.plant', 'detail.column'], [wall('room', 'S', 'end'), wall('room', 'N', 'center')], { pass: 'detail', pick: 'seed', count: 2 }),
    ] },
  ],
};
/** The smallest room that is still a room: one seat with its side table against a wall. */
const GENERIC_COMPACT: FloorProgram = {
  id: 'generic.compact',
  zones: [zone('sit', 'a seat', 0, { mandatory: true, minArea: 1, requires: ['seat'] }), zone('shelf', 'a chest', 1, { requires: ['storage'], dropOrder: 1 })],
  arrangements: [
    { id: 'nook', fills: [
      fill('sit', ['seat.waiting', 'seat.chair', 'seat.stools'], [wall('room', 'N', 'end'), wall('room', 'E', 'center'), wall('room', 'N', 'center'), wall('room', 'S', 'end'), wall('room', 'E', 'end'), wall('room', 'W', 'end'), spot('room', 0.75, 0.75)], { count: 1, per: 30, max: 6 }),
      fill('shelf', ['store.shelf', 'store.chest'], [wall('room', 'N', 'start'), wall('room', 'E', 'start'), wall('room', 'S', 'start')], { count: 1, per: 50, max: 4 }),
    ] },
  ],
};

// ─── THE MERCHANT HIVE — the shop program at three sizes, round the casework the SHELL builds ──────────────────────
// `interiorSpec(…, 'hive:store')` already draws perimeter shelving (render-only, 0.9 m off each side wall), twin
// pedestal aisles and an L counter 2.1 m before the back wall. The program adds what the shell cannot: the staff and
// customer sides of that counter, heroes in the window, cases on the open floor of the bigger tiers, back stock.

const HIVE_STORE: FloorProgram = {
  id: 'hive.store',
  zones: [
    zone('till', 'till', 0, { mandatory: true, minArea: 4, requires: ['service-staff', 'service-customer'], separatedFrom: ['arrival'] }),
    zone('window', 'window heroes', 1, { minArea: 2, requires: ['display'], adjacentTo: ['arrival'], dropOrder: 1 }),
    zone('backstock', 'back stock', 2, { minArea: 2, access: 'employee', requires: ['storage'], adjacentTo: ['till'], dropOrder: 2 }),
    zone('browse', 'cases', 3, { minArea: 110, requires: ['display'], dropOrder: 3 }),
    zone('rest', 'a bench', 4, { minArea: 150, requires: ['seat'], dropOrder: 4 }),
    zone('arrival', 'arrival', 5, { minArea: 0 }),
  ],
  arrangements: [
    { id: 'tiers',
      // the shell's shelving strips are not ours to furnish; the staff aisle behind its counter is
      cuts: [band('east', 'caseE', 1.0), band('west', 'caseW', 1.0), band('back', 'staff', 1.7)],
      zoneRects: { till: ['staff'] },
      fills: [
        fill('till', ['till.hive'], [spot('staff', 0.42, 0.3, 2), spot('staff', 0.3, 0.3, 2), spot('staff', 0.55, 0.3, 2)]),
        fill('window', ['display.hero'], [spot('frontEast', 0.3, 0.45), spot('frontWest', 0.7, 0.45)], { count: 2 }),
        fill('backstock', ['store.shelf', 'store.chest'], [wall('staff', 'N', 'end'), wall('staff', 'N', 'start')], { count: 1, per: 120, max: 3 }),
        // either side of the shell's runner (the walk from the door to the counter), never on it
        fill('browse', ['exhibit.case', 'exhibit.plinth'], [grid('mainEast', 1, 2.4, 0.5)], { count: 1, per: 130, max: 3 }),
        fill('browse', ['exhibit.case', 'exhibit.plinth'], [grid('mainWest', 1, 2.4, 0.5)], { count: 1, per: 130, max: 3 }),
        fill('rest', ['seat.waiting'], [wall('frontEast', 'S', 'end'), wall('frontWest', 'S', 'start')]),
        fill('arrival', ['sign.screen'], [wall('frontWest', 'S', 'end'), wall('frontEast', 'S', 'start')], { pass: 'secondary' }),
        fill('window', ['detail.plant', 'detail.lamp'], [wall('frontEast', 'S', 'start'), wall('frontWest', 'S', 'end')], { pass: 'detail', pick: 'seed' }),
      ] },
  ],
};
/** The emporium's upper floor (the grand tier has two): a second department — cases, a wall of stock, somewhere to sit. */
const HIVE_GALLERY: FloorProgram = {
  id: 'hive.gallery',
  zones: [
    zone('gallery', 'cases', 0, { mandatory: true, minArea: 8, requires: ['display'] }),
    zone('wall', 'wall displays', 1, { requires: ['display'], dropOrder: 2 }),
    zone('lounge', 'lounge', 2, { minArea: 40, requires: ['seat'], dropOrder: 1 }),
    zone('stock', 'stock', 3, { access: 'employee', requires: ['storage'], dropOrder: 3 }),
  ],
  arrangements: [
    { id: 'department', fills: [
      fill('gallery', ['exhibit.case', 'display.island'], [grid('mainEast', 0, 2.0, 1.2), grid('mainWest', 0, 2.0, 1.2)], { count: 2, per: 45, max: 8 }),
      fill('wall', ['display.wall'], [wall('room', 'E', 'start')], { count: 2, per: 40, max: 6 }),
      fill('lounge', ['lounge.island', 'lounge.set'], [spot('main', 0.5, 0.8), wall('room', 'S', 'end')]),
      fill('stock', ['stock.back', 'store.shelf'], [wall('room', 'N', 'end'), wall('room', 'N', 'start')]),
      fill('gallery', ['detail.column', 'detail.plant'], [wall('room', 'S', 'start'), wall('room', 'N', 'center')], { pass: 'detail', pick: 'seed', count: 2 }),
    ] },
  ],
};
const HIVE_COMPACT: FloorProgram = {
  id: 'hive.compact',
  zones: [zone('till', 'till', 0, { mandatory: true, minArea: 2, requires: ['service-staff', 'service-customer'] })],
  arrangements: [
    { id: 'till', cuts: [band('back', 'staff', 1.7)], zoneRects: { till: ['staff'] },
      fills: [fill('till', ['till.hive'], [spot('staff', 0.42, 0.3, 2), spot('staff', 0.3, 0.3, 2), spot('staff', 0.55, 0.3, 2), spot('staff', 0.7, 0.3, 2)])] },
  ],
};

const BREADTH: InteriorArchetype[] = [
  // Every archetype's LAST upper program repeats to the shell's eight floors: a tower is never left with bare floors.
  ...family('office', 'offices', 'small office', OFFICE_GROUND, OFFICE_COMPACT, [OFFICE_FLOOR], 'office'),
  { id: 'academy', revision: 1, label: 'academy', ground: ACADEMY_HALL, upper: [ACADEMY_STUDY, OFFICE_FLOOR], repeatUpper: true, maxFloors: 8, fallback: 'office.compact', legacyPurpose: 'office' },
  ...family('civic', 'civic hall', 'small civic office', CIVIC_HALL, CIVIC_COMPACT, [OFFICE_FLOOR, CIVIC_COUNCIL, CIVIC_ARCHIVE, OFFICE_FLOOR], 'civic'),
  ...family('faith', 'shrine', 'small shrine', FAITH_HALL, FAITH_COMPACT, [FAITH_QUIET], 'civic'),
  // the 8-floor hall: the hall · the court's meeting floor · the craft floor · the rolls · the members' lounge · quarters above
  ...family('guild', 'guild hall', 'guild room', GUILD_HALL, GUILD_COMPACT, [GUILD_MEETING, GUILD_CRAFT, CIVIC_ARCHIVE, GENERIC_UPPER, QUARTERS], 'guild'),
  ...family('bank', 'bank', 'small bank', BANK_HALL, BANK_COMPACT, [OFFICE_FLOOR], 'bank'),
  ...family('theater', 'theater', 'small theater', THEATER_HOUSE, THEATER_COMPACT, [THEATER_GREEN, GENERIC_UPPER], 'theater'),
  ...family('museum', 'museum', 'small museum', MUSEUM_GALLERY, MUSEUM_COMPACT, [MUSEUM_UPPER], 'civic'),
  ...family('salon', 'salon', 'small salon', SALON_FLOOR, SALON_COMPACT, [THEATER_GREEN, GENERIC_UPPER], 'retail'),
  ...family('garage', 'garage', 'small garage', GARAGE_FLOOR, GARAGE_COMPACT, [WORKS_STORE], 'garage'),
  ...family('transit', 'concourse', 'small shuttleport', TRANSIT_CONCOURSE, TRANSIT_COMPACT, [TRANSIT_LOUNGE, TRANSIT_OPS, OFFICE_FLOOR], 'transit'),
  ...family('workshop', 'workshop', 'small workshop', WORKSHOP_FLOOR, WORKSHOP_COMPACT, [WORKS_STORE], 'workshop'),
  { id: 'mining', revision: 1, label: 'mining support', ground: MINING_SUPPORT, upper: [WORKS_STORE, OFFICE_FLOOR], repeatUpper: true, maxFloors: 8, fallback: 'workshop.compact', legacyPurpose: 'workshop' },
  ...family('outpost', 'outpost', 'bunker', OUTPOST_POST, OUTPOST_COMPACT, [QUARTERS], 'civic'),
  ...family('lodge', 'lounge', 'snug', LODGE_LOUNGE, LODGE_COMPACT, [CANTINA_UPPER, GENERIC_UPPER], 'lodge'),
  ...family('shed', 'garden workshop', 'small shed', SHED_WORKSHOP, SHED_COMPACT, [GENERIC_UPPER], 'shed'),
  ...family('garden', 'garden room', 'bower', GARDEN_ROOM, GARDEN_COMPACT, [GENERIC_UPPER], 'generic'),
  ...family('generic', 'hall', 'small room', GENERIC_HALL, GENERIC_COMPACT, [GENERIC_UPPER], 'generic'),
  ...family('hive', 'Hive store', 'Hive stall', HIVE_STORE, HIVE_COMPACT, [HIVE_GALLERY], 'retail'),
];

const ALL_PURPOSES: InteriorPurpose[] = ['residence', 'apartment', 'civic', 'office', 'retail', 'hospitality', 'workshop', 'clinic', 'bank', 'garage', 'guild', 'theater', 'transit', 'lodge', 'shed', 'generic'];
const LEGACY: InteriorArchetype[] = ALL_PURPOSES.map((p) => ({
  id: `legacy.${p}`, revision: 1, label: `${p} (today's plan)`, ground: null, upper: [], maxFloors: 3, fallback: null, legacy: p, legacyPurpose: p,
}));

/** Every kit an archetype's programs name (ground, every upper floor, the units' own fills). */
export function kitsNamedBy(a: Pick<InteriorArchetype, 'ground' | 'upper'>): Set<string> {
  const out = new Set<string>();
  const prog = (p: FloorProgram | null): void => {
    if (!p) return;
    for (const arr of p.arrangements) for (const f of arr.fills) for (const k of f.kits) out.add(k);
    for (const f of p.units?.fills ?? []) for (const k of f.kits) out.add(k);
  };
  prog(a.ground);
  for (const u of a.upper) prog(u);
  return out;
}
/** The revision each archetype was AUTHORED at (the number written in its table row above). */
export const AUTHORED_REVISION: Readonly<Record<string, number>> = Object.fromEntries([...PLANNED, ...LEGACY, ...BREADTH].map((a) => [a.id, a.revision]));
/**
 * THE KIT RESEATS (slice N, 2026-10-01). A kit whose members changed at a kit version (interior_kits.ts
 * `KIT_VERSION_KEYS` — kits-2: the generated counter, desk, board, screen, seats, plinth, case, table, rack, lockers,
 * wash unit and vault door took their stand-ins' places) seats different pieces of different sizes, so every archetype
 * whose programs NAME such a kit is a different room: its revision is its authored one + 1 per such version, and with
 * it its seed namespace and every digest. DERIVED from the programs, so the list cannot miss an archetype; the
 * `legacy.*` archetypes name no kit and never move (a layout-0 room is today's plan, piece for piece).
 * kits-3 (round 2, the same day): the eight remaining stand-ins went, the counter was re-cooked taller and the
 * performance corner seats the riser (`KIT_VERSION_KITS`) — an archetype naming a kit reseated at BOTH versions is + 2.
 */
const reseated = (a: InteriorArchetype): InteriorArchetype => {
  const versions = new Set<string>();
  for (const k of kitsNamedBy(a)) for (const v of kitReseatVersions(k)) versions.add(v);
  return versions.size > 0 ? { ...a, revision: a.revision + versions.size } : a;
};

// Wave-1 archetypes first, then the legacy ones, then slice F's: an id's place in this record never moves.
export const ARCHETYPES: Readonly<Record<string, InteriorArchetype>> = Object.fromEntries([...PLANNED, ...LEGACY, ...BREADTH].map((a) => [a.id, reseated(a)]));
export const PLANNED_ARCHETYPES: readonly string[] = [...PLANNED, ...BREADTH].map((a) => a.id);
/** The archetypes wave 1 authored (slice B) and the ones slice F added. */
export const WAVE1_ARCHETYPES: readonly string[] = PLANNED.map((a) => a.id);
export const BREADTH_ARCHETYPES: readonly string[] = BREADTH.map((a) => a.id);

/** KEY FIRST. A key listed here outranks its purpose; `legacy.*` keeps a type on today's plan until its slice authors it. */
export const ARCHETYPE_BY_KEY: Readonly<Record<string, string>> = {
  'deed:small_house': 'home', 'deed:medium_house': 'home', 'deed:large_house': 'home', 'deed:xlarge_house': 'home',
  'deed:cantina': 'cantina',
  'deed:hotel': 'hotel',                      // hospitality by purpose, lodging by key
  'deed:hospital': 'clinic', 'deed:cloning': 'clinic',
  'deed:store': 'shop', 'deed:market': 'shop', 'deed:merchant_tent': 'shop',
  // slice F (2026-10-01): every deed type that is a kind of ROOM of its own, whatever purpose its label shares
  'deed:salon': 'salon',                      // a service room, not a shop floor
  'deed:guild_hall': 'guild',                 // hall · meeting · craft · quarters, round the shell's own columns and dais
  'hive:store': 'hive',                       // the shop program round the Hive shell's own casework
  'deed:bank': 'bank', 'deed:theater': 'theater', 'deed:garage': 'garage',
  'deed:museum': 'museum',                    // civic by purpose, a gallery circuit by key
  'deed:city_hall': 'civic', 'deed:civic_decoration': 'civic',
  'deed:factory': 'workshop', 'deed:mining_facility': 'mining',
  'deed:shuttleport': 'transit', 'deed:spaceport': 'transit',
  'deed:bunker': 'outpost', 'deed:outpost': 'outpost',   // civic by purpose, a guarded post by key
  'deed:garden': 'garden', 'deed:pool': 'garden',
};
/** BY THE BUILDING'S OWN ID: the three personal presets register every volume under ONE key (`preset:home`), so the
 *  pool annex — a `workshop` by its purpose label — is told apart by its id. The sky lounge (`lodge`) and the garden
 *  workshop (`shed`) arrive at theirs by purpose. */
export const ARCHETYPE_BY_ID: Readonly<Record<string, string>> = {
  'preset:annex': 'garden',
};
/** KEY VOCABULARY, most specific first — for keys no table names (a future shrine, an academy, a gallery). None of
 *  today's settlement keys (`civa_*`, `civb_*`) matches one: they resolve by their PROGRAM's purpose. */
export const ARCHETYPE_KEY_RULES: ReadonlyArray<readonly [RegExp, string]> = [
  [/shrine|sanctum|temple|chapel/, 'faith'],
  [/academy|school|lecture|library/, 'academy'],
  [/museum|gallery/, 'museum'],
  [/bunker|barracks|watchspire|warden_post/, 'outpost'],
];
/** PURPOSE SECOND. */
export const ARCHETYPE_BY_PURPOSE: Readonly<Record<InteriorPurpose, string>> = {
  residence: 'home', apartment: 'apartment', hospitality: 'cantina', clinic: 'clinic', retail: 'shop',
  // slice F (2026-10-01): every purpose has a program; `legacy.<purpose>` stays in the catalog as the baseline the
  // ladder falls to and `algorithm: 'legacy'` returns
  civic: 'civic', office: 'office', workshop: 'workshop', bank: 'bank', garage: 'garage',
  guild: 'guild', theater: 'theater', transit: 'transit', lodge: 'lodge', shed: 'shed', generic: 'generic',
};

/** The archetype a building takes: its own id (the presets), its key, its key's vocabulary, then its purpose. */
export function archetypeFor(key: string, purpose: InteriorPurpose, buildingId?: string): InteriorArchetype {
  let id: string | undefined = (buildingId !== undefined ? ARCHETYPE_BY_ID[buildingId] : undefined) ?? ARCHETYPE_BY_KEY[key];
  if (!id) { const k = key.toLowerCase(); for (const [re, a] of ARCHETYPE_KEY_RULES) if (re.test(k)) { id = a; break; } }
  return ARCHETYPES[id ?? ARCHETYPE_BY_PURPOSE[purpose] ?? `legacy.${purpose}`] ?? ARCHETYPES['legacy.generic'];
}

/** The floor program of floor `f` (0 = ground), or null when the archetype leaves it unprogrammed. */
export function programFor(a: InteriorArchetype, f: number): FloorProgram | null {
  if (f === 0) return a.ground;
  if (f >= a.maxFloors) return null;
  if (f - 1 < a.upper.length) return a.upper[f - 1];
  return a.repeatUpper && a.upper.length > 0 ? a.upper[a.upper.length - 1] : null;
}

// ═══ THE STOREYS (slice U, 2026-10-02) — read ONLY at layout ≥ STOREYS_LAYOUT_REVISION ═════════════════════════════════
// Slice L measured it: a lift ran in half the towers and three upper storeys in four held nothing a resident could use.
// Two causes: (1) the route raster could not see the landing's way past a back-wall shaft, so every program of every
// upper storey was refused there (interior_planner.ts THE LANDING GATE), and (2) an archetype named one or two upper
// floors and stopped — a home tower's storeys 3–7 had no program at all. This table is (2): what an archetype's upper
// storeys ARE, by the building's purpose and the storey's ROLE.
//
//   lower    floors 1 … lower.length, in order — the storeys nearest the ground have fixed purposes (the bedrooms over
//            a home, the lounge over a cantina, the ward over a clinic, the stockroom over a shop);
//   middle   the storeys between: SIBLINGS dealt in rotation from the building's own start (its topology seed), so
//            storeys 3 and 5 of a tower are two rooms of one house, never two copies; each also takes its own
//            arrangement and its own details by its floor's seed;
//   top      the top storey of a building of three floors or more, where it is not one of the lower ones;
//   compact  what a storey too small for its program holds instead (`simpler` on the ladder) — before the legacy plan.
//
// NOTHING HERE IS PART OF AN ARCHETYPE'S `upper` LIST: `kitsNamedBy`, the revisions and every plan at layouts 0–2 are
// what they were (THE LAYOUT LAW — a pinned room never moves). Unit floors keep their units (rentals key on their ids).

export type StoreyRole = 'lower' | 'middle' | 'top';
export interface StoreyPlan { lower: FloorProgram[]; middle: FloorProgram[]; top: FloorProgram | null; compact: FloorProgram }

/**
 * THE SOFT STOREY: a seat, a second thing to do (a bed, a desk), somewhere to put things, a lamp. It is two things:
 *   · the family's COMPACT program — what a storey too small for its own program holds instead (`simpler`);
 *   · THE SOFT TOP-UP — a storey whose program was seated but leaves a resident fewer than three places of two kinds
 *     (an archive is all stacks, a small office floor is one desk) takes these pieces as LATER-PASS fills, each kept
 *     only if everything proven before it stays proven (interior_planner.ts `dress`).
 * Its zone ids are its own (`soft…`), so its slots never collide with the program's it tops up. Three arrangements —
 * the same pieces tried round the room three ways: a small plate that refuses one takes another.
 */
function softStorey(id: string, o: { seats: string[]; second: { zone: 'softbed' | 'softdesk'; role: string; kits: string[]; access?: AccessRole; requires: AnchorKind }; store: string[]; storeAccess?: AccessRole }): FloorProgram {
  const R: Site[][] = [
    [wall('room', 'S', 'end'), wall('room', 'E', 'start'), wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'S', 'start'), wall('room', 'N', 'start'), wall('room', 'E', 'center')],
    [wall('room', 'E', 'end'), wall('room', 'N', 'end'), wall('room', 'S', 'start'), wall('room', 'E', 'start'), wall('room', 'N', 'start'), wall('room', 'S', 'end'), wall('room', 'N', 'center')],
    [wall('room', 'N', 'start'), wall('room', 'E', 'center'), wall('room', 'S', 'end'), wall('room', 'N', 'end'), wall('room', 'E', 'start'), wall('room', 'S', 'start'), wall('room', 'E', 'end')],
  ];
  const turn = (k: number, by: number): Site[] => [...R[k].slice(by), ...R[k].slice(0, by)];
  return {
    id,
    zones: [
      zone('softseat', 'a seat', 90, { mandatory: true, minArea: 1, requires: ['seat'] }),
      zone(o.second.zone, o.second.role, 91, { minArea: 1, access: o.second.access ?? 'public', requires: [o.second.requires], dropOrder: 2 }),
      zone('softstore', 'somewhere to put things', 92, { minArea: 1, access: o.storeAccess ?? 'public', requires: ['storage'], dropOrder: 3 }),
    ],
    arrangements: [0, 1, 2].map((k) => ({ id: ['south', 'east', 'north'][k], fills: [
      // (siblings, not copies) every fill with a choice takes its kits in the storey's own seeded order
      fill('softseat', o.seats, turn(k, 0), { pick: 'seed' }),
      fill(o.second.zone, o.second.kits, turn(k, 2), { pick: 'seed' }),
      fill('softstore', o.store, turn(k, 4), { pick: 'seed' }),
      fill('softseat', ['detail.lamp'], turn(k, 1), { pass: 'secondary' }),
      fill('softseat', [...o.seats].reverse(), turn(k, 3), { pass: 'secondary', pick: 'seed' }),
      fill('softseat', ['detail.plant', 'detail.mirror'], turn(k, 5), { pass: 'detail', pick: 'seed' }),
    ] })),
  };
}
const HOME_STOREY = softStorey('home.storey', { seats: ['seat.reading', 'seat.chair'], second: { zone: 'softbed', role: 'a bed', kits: ['bed.single'], requires: 'bed' }, store: ['store.chest', 'store.cabinet'] });
const LODGING_STOREY = softStorey('lodging.storey', { seats: ['seat.chair', 'seat.reading'], second: { zone: 'softbed', role: 'a bed', kits: ['bed.single'], requires: 'bed' }, store: ['store.chest', 'store.cabinet'] });
const CLINIC_STOREY = softStorey('clinic.storey', { seats: ['seat.waiting', 'seat.chair'], second: { zone: 'softbed', role: 'a cot', kits: ['bed.single'], requires: 'bed' }, store: ['store.cabinet', 'store.shelf'], storeAccess: 'employee' });
const WORK_STOREY = softStorey('work.storey', { seats: ['seat.chair', 'seat.reading', 'seat.waiting'], second: { zone: 'softdesk', role: 'a desk', kits: ['desk.study'], access: 'employee', requires: 'work' }, store: ['store.cabinet', 'store.bookcase', 'store.shelf'], storeAccess: 'employee' });
const QUARTERS_STOREY = softStorey('quarters.storey', { seats: ['seat.chair', 'seat.waiting'], second: { zone: 'softbed', role: 'a bunk', kits: ['bed.bunk', 'bed.single'], access: 'employee', requires: 'bed' }, store: ['store.chest', 'store.cabinet'], storeAccess: 'employee' });
const GENERIC_STOREY = softStorey('generic.storey', { seats: ['seat.reading', 'seat.chair', 'seat.waiting'], second: { zone: 'softdesk', role: 'a desk', kits: ['desk.study'], requires: 'work' }, store: ['store.cabinet', 'store.chest'] });

/** A lamp somebody can stand by: `secondary`, so it is not one of the detail fills a seeded quarter of rooms go without. */
const lampBy = (z: string, sites: Site[]): Fill => fill(z, ['detail.lamp'], sites, { pass: 'secondary' });
const ANY_WALL: Site[] = [wall('room', 'E', 'start'), wall('room', 'S', 'end'), wall('room', 'N', 'center'), wall('room', 'E', 'end'), wall('room', 'S', 'start'), wall('room', 'N', 'start')];
const ANY_WALL_B: Site[] = [wall('room', 'N', 'end'), wall('room', 'E', 'center'), wall('room', 'S', 'start'), wall('room', 'N', 'start'), wall('room', 'E', 'start'), wall('room', 'S', 'end')];

// ─── HOME: a family's floors between the bedrooms and the roof ───────────────────────────────────────────────────────

/** A sitting room upstairs: the sofa, the family table, a writing desk, books — where a household is by day. */
const HOME_FAMILY: FloorProgram = {
  id: 'home.family',
  zones: [
    zone('sitting', 'sitting room', 0, { mandatory: true, minArea: 3, requires: ['seat'] }),
    zone('table', 'family table', 1, { minArea: 16, requires: ['seat'], dropOrder: 3 }),
    zone('desk', 'writing desk', 2, { minArea: 10, access: 'owner', requires: ['work'], dropOrder: 2 }),
    zone('books', 'books and keepsakes', 3, { minArea: 2, requires: ['storage'], dropOrder: 1 }),
  ],
  arrangements: [
    { id: 'east-sofa', fills: [
      fill('sitting', ['lounge.set', 'seat.reading', 'seat.chair'], [wall('room', 'E', 'end'), wall('room', 'N', 'end'), wall('room', 'E', 'start'), wall('room', 'S', 'end')]),
      fill('table', ['table.dining', 'table.pair'], [spot('main', 0.45, 0.4, 1), spot('main', 0.5, 0.55), spot('frontEast', 0.5, 0.5, 1)]),
      fill('desk', ['desk.study'], [wall('room', 'S', 'end'), wall('room', 'N', 'start'), wall('room', 'E', 'start')]),
      fill('books', ['store.bookcase', 'store.cabinet'], [wall('room', 'N', 'start'), wall('room', 'E', 'center'), wall('room', 'S', 'start')], { count: 1, per: 45, max: 4 }),
      fill('sitting', ['seat.reading', 'seat.chair'], [wall('room', 'S', 'start'), wall('room', 'N', 'center'), wall('room', 'E', 'center')], { pass: 'secondary' }),
      fill('sitting', ['lounge.island'], [spot('main', 0.5, 0.6), spot('main', 0.4, 0.5)], { pass: 'secondary', bands: ['L'] }),
      lampBy('sitting', ANY_WALL),
      fill('books', ['store.chest', 'detail.sidecase'], ANY_WALL_B, { pass: 'detail', pick: 'seed' }),
      fill('sitting', ['detail.rug'], [spot('main', 0.55, 0.5)], { pass: 'detail' }),
      fill('table', ['detail.plant', 'detail.hearth', 'detail.mirror'], ANY_WALL, { pass: 'detail', pick: 'seed', count: 2 }),
    ] },
    { id: 'north-sofa', fills: [
      fill('sitting', ['lounge.set', 'seat.reading', 'seat.chair'], [wall('room', 'N', 'end'), wall('room', 'E', 'start'), wall('room', 'S', 'end'), wall('room', 'E', 'end')]),
      fill('table', ['table.dining', 'table.pair'], [spot('main', 0.55, 0.35), spot('main', 0.5, 0.5, 1), spot('frontEast', 0.5, 0.5)]),
      fill('desk', ['desk.study'], [wall('room', 'E', 'end'), wall('room', 'S', 'end'), wall('room', 'N', 'start')]),
      fill('books', ['store.bookcase', 'store.cabinet'], [wall('room', 'E', 'center'), wall('room', 'S', 'start'), wall('room', 'N', 'start')], { count: 1, per: 45, max: 4 }),
      fill('sitting', ['seat.reading', 'seat.chair'], [wall('room', 'S', 'start'), wall('room', 'E', 'center'), wall('room', 'N', 'start')], { pass: 'secondary' }),
      fill('sitting', ['lounge.island'], [spot('main', 0.45, 0.55), spot('main', 0.55, 0.45)], { pass: 'secondary', bands: ['L'] }),
      lampBy('sitting', ANY_WALL_B),
      fill('books', ['detail.sidecase', 'store.chest'], ANY_WALL, { pass: 'detail', pick: 'seed' }),
      fill('sitting', ['detail.rug'], [spot('main', 0.5, 0.45)], { pass: 'detail' }),
      fill('table', ['detail.mirror', 'detail.plant', 'detail.hearth'], ANY_WALL_B, { pass: 'detail', pick: 'seed', count: 2 }),
    ] },
  ],
};
/** A guest's floor: a bed or two, a wardrobe, a chair by the window, a small desk. */
const HOME_GUEST: FloorProgram = {
  id: 'home.guest',
  zones: [
    zone('guest', 'guest bed', 0, { mandatory: true, minArea: 5, requires: ['bed'] }),
    zone('nook', 'a chair by the window', 1, { minArea: 2, requires: ['seat'], dropOrder: 1 }),
    zone('dress', 'wardrobe', 2, { minArea: 2, requires: ['storage'], adjacentTo: ['guest'], dropOrder: 2 }),
    zone('desk', 'small desk', 3, { minArea: 12, requires: ['work'], dropOrder: 3 }),
  ],
  arrangements: [
    { id: 'north-bed', fills: [
      fill('guest', ['bed.double', 'bed.single'], [wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'E', 'start')], { count: 1, per: 70, max: 3 }),
      fill('nook', ['seat.reading', 'seat.chair'], [wall('room', 'E', 'start'), wall('room', 'S', 'end'), wall('room', 'N', 'start')]),
      fill('dress', ['store.wardrobe', 'store.chest'], [wall('room', 'N', 'start'), wall('room', 'E', 'center'), wall('room', 'S', 'start')]),
      fill('desk', ['desk.study'], [wall('room', 'S', 'end'), wall('room', 'E', 'center'), wall('room', 'S', 'start')]),
      fill('nook', ['table.pair'], [spot('main', 0.45, 0.5, 1), spot('main', 0.5, 0.6)], { pass: 'secondary', bands: ML }),
      lampBy('nook', ANY_WALL),
      fill('guest', ['detail.rug'], [spot('main', 0.55, 0.55)], { pass: 'detail' }),
      fill('dress', ['detail.mirror', 'detail.plant'], ANY_WALL_B, { pass: 'detail', pick: 'seed' }),
    ] },
    { id: 'east-bed', fills: [
      fill('guest', ['bed.double', 'bed.single'], [wall('room', 'E', 'end'), wall('room', 'N', 'end'), wall('room', 'E', 'start')], { count: 1, per: 70, max: 3 }),
      fill('nook', ['seat.reading', 'seat.chair'], [wall('room', 'S', 'end'), wall('room', 'N', 'start'), wall('room', 'E', 'start')]),
      fill('dress', ['store.wardrobe', 'store.chest'], [wall('room', 'N', 'center'), wall('room', 'N', 'start'), wall('room', 'S', 'start')]),
      fill('desk', ['desk.study'], [wall('room', 'S', 'start'), wall('room', 'N', 'start'), wall('room', 'E', 'start')]),
      fill('nook', ['table.pair'], [spot('main', 0.5, 0.45), spot('main', 0.4, 0.55, 1)], { pass: 'secondary', bands: ML }),
      lampBy('nook', ANY_WALL_B),
      fill('guest', ['detail.rug'], [spot('main', 0.5, 0.5)], { pass: 'detail' }),
      fill('dress', ['detail.plant', 'detail.mirror'], ANY_WALL, { pass: 'detail', pick: 'seed' }),
    ] },
  ],
};
/** Under the roof: a lounge among planters — the house's quiet room, and the one with the view. */
const HOME_LOFT: FloorProgram = {
  id: 'home.loft',
  zones: [
    zone('lounge', 'roof lounge', 0, { mandatory: true, minArea: 3, requires: ['seat'] }),
    zone('green', 'planters', 1, { minArea: 6, requires: ['work'], dropOrder: 2 }),
    zone('table', 'a table for two', 2, { minArea: 18, requires: ['seat'], dropOrder: 3 }),
    zone('store', 'trunks', 3, { minArea: 2, requires: ['storage'], dropOrder: 1 }),
  ],
  arrangements: [
    { id: 'loft', fills: [
      fill('lounge', ['lounge.set', 'seat.reading', 'seat.chair'], [wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'S', 'end'), wall('room', 'E', 'start')]),
      fill('green', ['garden.planter'], [wall('room', 'E', 'start'), wall('room', 'S', 'end'), wall('room', 'N', 'start')], { count: 1, per: 60, max: 4 }),
      fill('table', ['table.stools', 'table.pair'], [spot('main', 0.45, 0.5), spot('main', 0.55, 0.4, 1), spot('frontEast', 0.5, 0.5)]),
      fill('store', ['store.chest', 'store.cabinet'], [wall('room', 'N', 'start'), wall('room', 'S', 'start'), wall('room', 'E', 'center')]),
      fill('lounge', ['seat.reading', 'seat.chair'], [wall('room', 'S', 'start'), wall('room', 'E', 'center'), wall('room', 'N', 'center')], { pass: 'secondary' }),
      fill('lounge', ['lounge.island', 'lounge.pods'], [spot('main', 0.5, 0.6), spot('main', 0.4, 0.45)], { pass: 'secondary', bands: ['L'] }),
      lampBy('lounge', ANY_WALL),
      fill('green', ['garden.green', 'detail.plant'], ANY_WALL_B, { pass: 'detail', pick: 'seed', count: 2, per: 50, max: 5 }),
      fill('lounge', ['detail.rug'], [spot('main', 0.5, 0.5)], { pass: 'detail' }),
    ] },
  ],
};
// ─── LODGING: the unit floors, with the soft layer inside every unit and a corridor that is somewhere ────────────────

/** A storey too small to cut into units is ONE small flat (the `shared` arrangement): the bed, somewhere to sit as a
 *  PRIMARY piece, the wardrobe, a second seat, a lamp — two ways round the room, and a second bed only on a floor
 *  with ground for it. */
const SHARED_FLAT = (beds: string[], seats: string[], stores: string[]): Arrangement[] => [
  { id: 'shared', fills: [
    fill('landing', beds, [wall('room', 'E', 'end'), wall('room', 'N', 'end'), wall('room', 'E', 'start'), wall('room', 'S', 'end')], { count: 1, per: 40, max: 3, pick: 'seed' }),
    fill('landing', seats, [spot('main', 0.4, 0.45), wall('room', 'S', 'start'), wall('room', 'N', 'start'), wall('room', 'S', 'end'), wall('room', 'E', 'start')], { pick: 'seed' }),
    fill('landing', stores, [wall('room', 'N', 'start'), wall('room', 'E', 'start'), wall('room', 'S', 'end')], { pick: 'seed' }),
    fill('landing', ['seat.reading', 'seat.chair'], [wall('room', 'S', 'end'), wall('room', 'E', 'center'), wall('room', 'N', 'center')], { pass: 'secondary', pick: 'seed' }),
    lampBy('landing', ANY_WALL),
    fill('landing', ['detail.plant', 'detail.mirror', 'detail.sidecase'], ANY_WALL_B, { pass: 'detail', pick: 'seed' }),
  ] },
  { id: 'shared-b', fills: [
    fill('landing', beds, [wall('room', 'N', 'end'), wall('room', 'E', 'start'), wall('room', 'S', 'end'), wall('room', 'E', 'end')], { count: 1, per: 40, max: 3, pick: 'seed' }),
    fill('landing', seats, [wall('room', 'S', 'start'), spot('main', 0.5, 0.55, 1), wall('room', 'E', 'end'), wall('room', 'N', 'start')], { pick: 'seed' }),
    fill('landing', stores, [wall('room', 'E', 'start'), wall('room', 'N', 'start'), wall('room', 'S', 'start')], { pick: 'seed' }),
    fill('landing', ['seat.reading', 'seat.chair'], [wall('room', 'E', 'center'), wall('room', 'S', 'end'), wall('room', 'N', 'center')], { pass: 'secondary', pick: 'seed' }),
    lampBy('landing', ANY_WALL_B),
    fill('landing', ['detail.mirror', 'detail.plant', 'detail.sidecase'], ANY_WALL, { pass: 'detail', pick: 'seed' }),
  ] },
];
/** What a corridor holds besides doors: a bench, a lamp, something green — on whatever wall the doors leave free. */
const CORRIDOR: Fill[] = [
  fill('landing', ['seat.waiting', 'seat.chair'], [wall('room', 'S', 'start'), wall('room', 'N', 'start'), wall('room', 'W', 'start'), wall('room', 'W', 'end')], { pass: 'secondary', pick: 'seed' }),
  fill('landing', ['detail.lamp'], [wall('room', 'N', 'start'), wall('room', 'S', 'start'), wall('room', 'W', 'end'), wall('room', 'W', 'start')], { pass: 'secondary' }),
  fill('landing', ['detail.plant', 'detail.column', 'detail.mirror'], [wall('room', 'W', 'end'), wall('room', 'S', 'start'), wall('room', 'N', 'start'), wall('room', 'W', 'center')], { pass: 'detail', pick: 'seed' }),
  // THE RESIDENTS' LOUNGE: where the units leave a hall (a 26 m tower's floor is a band of rooms down one wall and an
  // empty hall beside it), the hall is somewhere to sit together and a table to eat at — later passes, kept only if
  // every unit's door and every anchor stays reached; a corridor too narrow for them simply takes none
  fill('landing', ['lounge.set', 'lounge.island'], [spot('room', 0.32, 0.5), spot('room', 0.32, 0.22), spot('room', 0.32, 0.78), spot('room', 0.5, 0.5)], { pass: 'secondary', per: 160, max: 3, pick: 'seed' }),
  fill('landing', ['table.dining', 'table.pair'], [spot('room', 0.48, 0.32), spot('room', 0.48, 0.68), spot('room', 0.6, 0.5)], { pass: 'secondary', per: 240, max: 2, pick: 'seed' }),
];
/** The same units as `HOTEL_UNITS` (the same cut, the same ids), each one a room you could stay in: a bed, a chest, a
 *  chair with its side table as a PRIMARY piece, a lamp, and one detail by the storey's own seed. */
const HOTEL_UNITS_SOFT: FloorProgram = {
  id: HOTEL_UNITS.id,
  zones: HOTEL_UNITS.zones,
  units: {
    ...HOTEL_UNITS.units!, common: CORRIDOR,
    fills: [
      fill('unit', ['bed.double', 'bed.single'], [wall('unit', 'E', 'end'), wall('unit', 'E', 'start'), wall('unit', 'N', 'end'), wall('unit', 'S', 'end'), wall('unit', 'N', 'start')], { pick: 'seed' }),
      // the bed and its STORE are the primary pieces, as at layout 2 (a let room is a bed and somewhere the tenant
      // keeps things: rentals key the tenant's storage on it); a chair that crowds the bedside costs the unit its bed,
      // so the seat and everything after it is a later pass, kept only if the bed and the store stay reachable
      fill('unit', ['store.chest', 'store.cabinet'], [wall('unit', 'E', 'start'), wall('unit', 'N', 'start'), wall('unit', 'S', 'end')], { pick: 'seed' }),
      fill('unit', ['seat.chair', 'seat.reading'], [wall('unit', 'N', 'start'), wall('unit', 'S', 'start'), wall('unit', 'S', 'end'), wall('unit', 'E', 'start'), wall('unit', 'N', 'end'), wall('unit', 'E', 'end')], { pass: 'secondary', pick: 'seed' }),
      fill('unit', ['detail.lamp', 'detail.mirror'], [wall('unit', 'S', 'end'), wall('unit', 'N', 'start'), wall('unit', 'E', 'start')], { pass: 'secondary', pick: 'seed' }),
      fill('unit', ['detail.plant', 'detail.sidecase'], [wall('unit', 'S', 'start'), wall('unit', 'E', 'start'), wall('unit', 'N', 'end')], { pass: 'detail', pick: 'seed' }),
    ],
  },
  arrangements: SHARED_FLAT(['bed.double', 'bed.single'], ['seat.chair', 'seat.reading'], ['store.chest', 'store.cabinet']),
};
/** The same dwellings as `APARTMENT_UNITS`, each one lived in: the bed and the wardrobe, a table for two (a chair where
 *  the unit is small), a reading chair, a lamp, books or a cabinet by the storey's own seed. */
const APARTMENT_UNITS_SOFT: FloorProgram = {
  id: APARTMENT_UNITS.id,
  zones: APARTMENT_UNITS.zones,
  units: {
    ...APARTMENT_UNITS.units!, common: CORRIDOR,
    fills: [
      fill('unit', ['bed.double', 'bed.single'], [wall('unit', 'E', 'end'), wall('unit', 'N', 'end'), wall('unit', 'E', 'start'), wall('unit', 'S', 'end'), wall('unit', 'N', 'start')], { pick: 'seed' }),
      fill('unit', ['store.wardrobe', 'store.chest'], [wall('unit', 'N', 'start'), wall('unit', 'E', 'start'), wall('unit', 'S', 'end')], { pick: 'seed' }),
      // (the bed and the wardrobe are proven first — a table that crowds the bedside would cost the dwelling its bed)
      fill('unit', ['table.pair', 'seat.chair', 'seat.reading'], [spot('unit', 0.3, 0.45), wall('unit', 'S', 'start'), wall('unit', 'S', 'end'), wall('unit', 'N', 'start'), wall('unit', 'N', 'end'), wall('unit', 'E', 'start'), wall('unit', 'E', 'end')], { pass: 'secondary' }),
      fill('unit', ['seat.chair', 'seat.reading'], [wall('unit', 'S', 'end'), wall('unit', 'E', 'start'), wall('unit', 'N', 'start'), wall('unit', 'S', 'start'), wall('unit', 'N', 'end')], { pass: 'secondary', pick: 'seed' }),
      fill('unit', ['store.bookcase', 'store.cabinet', 'detail.sidecase'], [wall('unit', 'E', 'start'), wall('unit', 'S', 'start'), wall('unit', 'N', 'start')], { pass: 'secondary', pick: 'seed' }),
      fill('unit', ['detail.lamp'], [wall('unit', 'S', 'start'), wall('unit', 'E', 'start'), wall('unit', 'N', 'start')], { pass: 'secondary' }),
      fill('unit', ['detail.plant', 'detail.mirror'], [wall('unit', 'S', 'end'), wall('unit', 'N', 'end'), wall('unit', 'E', 'start')], { pass: 'detail', pick: 'seed' }),
    ],
  },
  arrangements: SHARED_FLAT(['bed.double', 'bed.single'], ['table.pair', 'seat.chair', 'seat.reading'], ['store.wardrobe', 'store.chest']),
};

// ─── OVER A CANTINA: guest rooms (never let — the inn's own), the staff's bunks, the keeper's rooms on top ───────────

const INN_ROOMS: FloorProgram = {
  id: 'inn.rooms',
  zones: [
    zone('beds', 'guest beds', 0, { mandatory: true, minArea: 5, requires: ['bed'] }),
    zone('chairs', 'a chair each', 1, { minArea: 2, requires: ['seat'], dropOrder: 1 }),
    zone('trunks', 'trunks', 2, { minArea: 2, requires: ['storage'], dropOrder: 2 }),
    zone('wash', 'wash stand', 3, { minArea: 16, access: 'employee', requires: ['storage'], dropOrder: 3 }),
  ],
  arrangements: [
    { id: 'rooms', fills: [
      fill('beds', ['bed.double.screen', 'bed.double', 'bed.single'], [wall('room', 'E', 'end'), wall('room', 'N', 'end')], { count: 1, per: 28, max: 5 }),
      fill('beds', ['bed.single'], [wall('room', 'E', 'start'), wall('room', 'S', 'end')], { count: 1, per: 60, max: 3 }),
      fill('chairs', ['seat.chair', 'seat.reading'], [wall('room', 'S', 'start'), wall('room', 'N', 'start'), wall('room', 'E', 'center')], { count: 1, per: 30, max: 4 }),
      fill('trunks', ['store.chest', 'store.wardrobe'], [wall('room', 'N', 'start'), wall('room', 'E', 'center'), wall('room', 'S', 'end')], { count: 1, per: 35, max: 4 }),
      fill('wash', ['service.wash', 'store.cabinet'], [wall('room', 'S', 'start'), wall('room', 'N', 'center')]),
      fill('chairs', ['table.pair'], [spot('main', 0.4, 0.5, 1), spot('main', 0.5, 0.6)], { pass: 'secondary', bands: ML }),
      lampBy('chairs', ANY_WALL),
      fill('beds', ['screen.single'], [row('room', 'E', 3.4, 'center')], { pass: 'secondary', bands: ML }),
      fill('chairs', ['detail.plant', 'detail.mirror'], ANY_WALL_B, { pass: 'detail', pick: 'seed' }),
    ] },
  ],
};

// ─── OVER A CLINIC: the dispensary and the consulting rooms; the staff's rest on top ─────────────────────────────────

const CLINIC_DISPENSARY: FloorProgram = {
  id: 'clinic.dispensary',
  zones: [
    zone('stores', 'dispensary', 0, { mandatory: true, minArea: 3, access: 'employee', requires: ['storage'] }),
    zone('consult', 'consulting desk', 1, { minArea: 6, access: 'employee', requires: ['work'], dropOrder: 2 }),
    zone('waiting', 'waiting', 2, { minArea: 2, requires: ['seat'], dropOrder: 1 }),
    zone('couch', 'examination couch', 3, { minArea: 14, access: 'guarded', requires: ['bed', 'bedside'], dropOrder: 3 }),
  ],
  arrangements: [
    { id: 'dispensary', fills: [
      fill('stores', ['supply.wall', 'store.cabinet', 'store.shelf'], [wall('room', 'N', 'end'), wall('room', 'E', 'end')], { count: 1, per: 40, max: 4 }),
      fill('consult', ['desk.study'], [wall('room', 'E', 'start'), wall('room', 'S', 'end'), wall('room', 'N', 'start')], { count: 1, per: 60, max: 3 }),
      fill('waiting', ['seat.waiting', 'seat.chair'], [wall('room', 'S', 'start'), wall('room', 'S', 'end'), wall('room', 'N', 'start')], { count: 1, per: 60, max: 3 }),
      fill('couch', ['bed.treatment.screen', 'bed.treatment'], [wall('room', 'E', 'center'), wall('room', 'N', 'start')]),
      fill('stores', ['service.wash', 'store.shelf'], [wall('room', 'N', 'start'), wall('room', 'E', 'center')], { pass: 'secondary' }),
      lampBy('waiting', ANY_WALL),
      fill('waiting', ['sign.screen'], [wall('room', 'S', 'end'), wall('room', 'N', 'center')], { pass: 'secondary', bands: ML }),
      fill('waiting', ['detail.plant', 'detail.column'], ANY_WALL_B, { pass: 'detail', pick: 'seed' }),
    ] },
  ],
};
// ─── OVER A SHOP, A WORKSHOP, A GARAGE: the office, the workroom ─────────────────────────────────────────────────────

/** A small office over the trade: two or three writing desks, the ledgers, a table to talk terms at. */
const TRADE_OFFICE: FloorProgram = {
  id: 'trade.office',
  zones: [
    zone('desks', 'desks', 0, { mandatory: true, minArea: 3, access: 'employee', requires: ['work'] }),
    zone('ledgers', 'ledgers', 1, { minArea: 2, access: 'employee', requires: ['storage'], dropOrder: 2 }),
    zone('terms', 'a table to talk terms', 2, { minArea: 14, requires: ['seat'], dropOrder: 3 }),
    zone('wait', 'a seat for a caller', 3, { minArea: 2, requires: ['seat'], dropOrder: 1 }),
  ],
  arrangements: [
    { id: 'office', fills: [
      fill('desks', ['desk.study'], [wall('room', 'E', 'end'), wall('room', 'N', 'end'), wall('room', 'E', 'start')], { count: 1, per: 30, max: 4 }),
      fill('ledgers', ['store.bookcase', 'store.cabinet'], [wall('room', 'N', 'start'), wall('room', 'E', 'center'), wall('room', 'S', 'end')], { count: 1, per: 40, max: 4 }),
      fill('terms', ['table.pair', 'table.dining'], [spot('main', 0.45, 0.45, 1), spot('main', 0.5, 0.6), spot('frontEast', 0.5, 0.5, 1)]),
      fill('wait', ['seat.reading', 'seat.chair', 'seat.waiting'], [wall('room', 'S', 'end'), wall('room', 'S', 'start'), wall('room', 'N', 'start')]),
      ...aisles('desks', ['desk.pod'], 1, 1.2, 0.6, { count: 1, per: 120, max: 4, bands: ML }),
      lampBy('wait', ANY_WALL),
      fill('ledgers', ['sign.screen'], [wall('room', 'N', 'center'), wall('room', 'E', 'end')], { pass: 'secondary', bands: ML }),
      fill('wait', ['detail.plant', 'detail.column'], ANY_WALL_B, { pass: 'detail', pick: 'seed' }),
    ] },
  ],
};
/** A workroom: benches along a wall, the parts beside them, a table to lay work out on. */
const WORKS_ROOM: FloorProgram = {
  id: 'works.room',
  zones: [
    zone('bench', 'benches', 0, { mandatory: true, minArea: 4, access: 'employee', requires: ['work'] }),
    zone('parts', 'parts', 1, { minArea: 2, access: 'employee', requires: ['storage'], adjacentTo: ['bench'], dropOrder: 2 }),
    zone('table', 'laying-out table', 2, { minArea: 16, requires: ['seat'], dropOrder: 3 }),
    zone('rest', 'a seat', 3, { minArea: 2, requires: ['seat'], dropOrder: 1 }),
  ],
  arrangements: [
    { id: 'workroom', fills: [
      fill('bench', ['work.bench'], [wall('room', 'E', 'start'), wall('room', 'N', 'end')], { count: 1, per: 35, max: 5 }),
      fill('parts', ['rack.parts', 'stock.crates'], [wall('room', 'N', 'start'), wall('room', 'E', 'end'), wall('room', 'S', 'end')], { count: 1, per: 50, max: 4 }),
      fill('table', ['table.pair', 'table.long'], [spot('main', 0.45, 0.5, 1), spot('main', 0.5, 0.6)]),
      fill('rest', ['seat.waiting', 'seat.chair'], [wall('room', 'S', 'start'), wall('room', 'S', 'end'), wall('room', 'N', 'center')]),
      ...aisles('bench', ['work.island'], 0, 3, 3, { count: 1, per: 220, max: 6, bands: ['L'] }),
      lampBy('rest', ANY_WALL),
      fill('bench', ['detail.column'], ANY_WALL_B, { pass: 'detail' }),
    ] },
  ],
};
// ─── OFFICES AND CIVIC TOWERS: a studio floor between the desk floors, the board's room on top ───────────────────────

/** A studio floor: the long table in the middle, desks along the walls, somewhere soft to think. */
const OFFICE_STUDIO: FloorProgram = {
  id: 'office.studio',
  zones: [
    zone('table', 'the long table', 0, { mandatory: true, minArea: 8, access: 'employee', requires: ['seat'] }),
    zone('desks', 'wall desks', 1, { minArea: 4, access: 'employee', requires: ['work'], dropOrder: 1 }),
    zone('records', 'drawings and files', 2, { minArea: 2, access: 'employee', requires: ['storage'], dropOrder: 2 }),
    zone('pause', 'soft seats', 3, { minArea: 14, access: 'employee', requires: ['seat'], dropOrder: 3 }),
  ],
  arrangements: [
    { id: 'studio', fills: [
      fill('table', ['table.dining', 'table.pair'], [spot('main', 0.5, 0.45, 1), spot('main', 0.5, 0.55), spot('main', 0.4, 0.5, 1)]),
      fill('desks', ['desk.study'], [wall('room', 'E', 'end'), wall('room', 'N', 'end'), wall('room', 'E', 'start')], { count: 1, per: 28, max: 6 }),
      fill('records', ['store.bookcase', 'store.cabinet'], [wall('room', 'N', 'start'), wall('room', 'S', 'end'), wall('room', 'E', 'center')], { count: 1, per: 40, max: 5 }),
      fill('pause', ['lounge.set', 'seat.reading'], [wall('room', 'S', 'start'), wall('room', 'S', 'end'), wall('room', 'N', 'center')]),
      ...aisles('table', ['table.dining', 'table.pair'], 1, 2.2, 1.6, { count: 1, per: 180, max: 6, bands: ['L'] }),
      ...aisles('pause', ['lounge.island', 'lounge.pods'], 0, 4, 2.5, { count: 1, per: 700, max: 3, bands: ['L'] }),
      lampBy('pause', ANY_WALL),
      fill('records', ['sign.screen'], [wall('room', 'N', 'center'), wall('room', 'E', 'end')], { pass: 'secondary' }),
      fill('pause', ['detail.plant', 'detail.column'], ANY_WALL_B, { pass: 'detail', pick: 'seed', count: 2 }),
    ] },
  ],
};
/** The top of an office or a civic tower: the board's table, a desk of rank, sofas, the long view. */
const OFFICE_BOARD: FloorProgram = {
  id: 'office.board',
  zones: [
    zone('board', 'the board\'s table', 0, { mandatory: true, minArea: 8, access: 'employee', requires: ['seat'] }),
    zone('desk', 'the principal\'s desk', 1, { minArea: 4, access: 'employee', requires: ['work'], dropOrder: 1 }),
    zone('lounge', 'reception sofas', 2, { minArea: 6, requires: ['seat'], dropOrder: 2 }),
    zone('records', 'the bound volumes', 3, { minArea: 2, access: 'employee', requires: ['storage'], dropOrder: 3 }),
  ],
  arrangements: [
    { id: 'board', fills: [
      fill('board', ['table.council', 'table.dining', 'table.pair'], [spot('main', 0.5, 0.5), spot('main', 0.5, 0.45, 1), spot('main', 0.45, 0.55)]),
      fill('desk', ['desk.study'], [wall('room', 'N', 'end'), wall('room', 'E', 'end'), wall('room', 'E', 'start')], { count: 1, per: 90, max: 3 }),
      fill('lounge', ['lounge.set', 'seat.reading'], [wall('room', 'S', 'end'), wall('room', 'E', 'start'), wall('room', 'S', 'start')], { count: 1, per: 120, max: 3 }),
      fill('records', ['store.bookcase', 'store.cabinet'], [wall('room', 'N', 'start'), wall('room', 'E', 'center')], { count: 1, per: 50, max: 4 }),
      ...aisles('lounge', ['lounge.island', 'lounge.pods'], 0, 4, 2.5, { count: 1, per: 500, max: 4, bands: ['L'] }),
      lampBy('lounge', ANY_WALL),
      fill('board', ['sign.screen'], [wall('room', 'N', 'center'), wall('room', 'E', 'center')], { pass: 'secondary' }),
      fill('lounge', ['detail.plant', 'detail.column', 'detail.mirror'], ANY_WALL_B, { pass: 'detail', pick: 'seed', count: 2, per: 80, max: 5 }),
      fill('board', ['detail.rug'], [spot('main', 0.5, 0.5)], { pass: 'detail' }),
    ] },
  ],
};

// ─── OVER A BUNKER: the armoury between the bunk rooms ───────────────────────────────────────────────────────────────

const OUTPOST_ARMOURY: FloorProgram = {
  id: 'outpost.armoury',
  zones: [
    zone('lockers', 'kit lockers', 0, { mandatory: true, minArea: 3, access: 'employee', requires: ['storage'] }),
    zone('racks', 'weapon racks', 1, { minArea: 2, access: 'guarded', requires: ['storage'], dropOrder: 3 }),
    zone('bench', 'the armourer\'s bench', 2, { minArea: 5, access: 'employee', requires: ['work'], dropOrder: 2 }),
    zone('brief', 'briefing table', 3, { minArea: 24, access: 'employee', requires: ['stand'], dropOrder: 4 }),
    zone('rest', 'a bench', 4, { minArea: 2, requires: ['seat'], dropOrder: 1 }),
  ],
  arrangements: [
    { id: 'armoury', fills: [
      fill('lockers', ['store.lockers', 'store.cabinet'], [wall('room', 'E', 'start'), wall('room', 'S', 'end')], { count: 1, per: 45, max: 4 }),
      fill('racks', ['store.weapons'], [wall('room', 'N', 'end'), wall('room', 'N', 'start')], { count: 1, per: 50, max: 4 }),
      fill('bench', ['work.bench'], [wall('room', 'E', 'end'), wall('room', 'N', 'start'), wall('room', 'S', 'start')]),
      fill('brief', ['table.briefing'], [spot('main', 0.45, 0.5), spot('main', 0.5, 0.6, 1)]),
      fill('rest', ['seat.waiting', 'seat.chair'], [wall('room', 'S', 'start'), wall('room', 'S', 'end'), wall('room', 'N', 'center')]),
      fill('lockers', ['stock.crates'], [wall('room', 'S', 'end'), wall('room', 'N', 'center')], { pass: 'secondary', count: 1, per: 80, max: 3 }),
      lampBy('rest', ANY_WALL),
      fill('bench', ['detail.column'], ANY_WALL_B, { pass: 'detail' }),
    ] },
  ],
};
// ─── THE SPACEPORT'S UPPER FLOORS, FURNISHED ON BOTH SIDES OF THE AISLE (the QA log: half the floor stood empty) ──────

const TRANSIT_LOUNGE_WIDE: FloorProgram = {
  id: TRANSIT_LOUNGE.id,
  zones: TRANSIT_LOUNGE.zones,
  arrangements: [
    { id: 'lounge', fills: [
      fill('lounge', ['lounge.set', 'seat.waiting'], [wall('room', 'N', 'end'), wall('room', 'E', 'start')], { count: 2, per: 50, max: 4 }),
      // a grid takes its whole count at the first region with room — each side of the aisle is its own fill
      ...aisles('tables', ['table.pair'], 1, 1.3, 1.2, { count: 1, per: 110, max: 3 }),
      fill('lockers', ['store.lockers', 'store.cabinet'], [wall('room', 'N', 'start'), wall('room', 'S', 'end')]),
      fill('lounge', ['seat.waiting', 'lounge.pods'], [wall('room', 'W', 'end'), wall('room', 'S', 'start'), wall('room', 'W', 'start')], { count: 1, per: 70, max: 3 }),
      fill('lounge', ['sign.screen'], [wall('room', 'E', 'center'), wall('room', 'S', 'start')], { pass: 'secondary' }),
      lampBy('lounge', ANY_WALL_B),
      fill('lounge', ['detail.plant', 'detail.lamp'], [wall('room', 'S', 'end'), wall('room', 'E', 'end'), wall('room', 'W', 'center')], { pass: 'detail', pick: 'seed', count: 2 }),
    ] },
  ],
};
const TRANSIT_OPS_WIDE: FloorProgram = {
  id: TRANSIT_OPS.id,
  zones: TRANSIT_OPS.zones,
  arrangements: [
    { id: 'control', fills: [
      ...aisles('control', ['desk.pod', 'desk.study'], 1, 1.2, 0.8, { count: 1, per: 100, max: 3 }),
      fill('control', ['desk.study'], [wall('room', 'N', 'end'), wall('room', 'E', 'end')], { count: 1, per: 90, max: 2 }),
      fill('briefing', ['table.briefing'], [spot('main', 0.3, 0.6), spot('main', 0.5, 0.5, 1)]),
      fill('boards', ['sign.screen'], [wall('room', 'N', 'center'), wall('room', 'E', 'center')], { count: 2 }),
      fill('lockers', ['store.lockers', 'store.cabinet'], [wall('room', 'S', 'end'), wall('room', 'N', 'start')]),
      fill('control', ['seat.waiting', 'seat.chair'], [wall('room', 'W', 'end'), wall('room', 'S', 'start')], { pass: 'secondary' }),
      lampBy('control', ANY_WALL_B),
      fill('control', ['detail.column'], [wall('room', 'E', 'end'), wall('room', 'S', 'start')], { pass: 'detail', count: 2 }),
    ] },
  ],
};

/** By FAMILY (an archetype and its `.compact` fallback share their storeys). */
const STOREY_PLANS: Readonly<Record<string, StoreyPlan>> = {
  home: { lower: [HOME_SLEEP, HOME_STUDY], middle: [HOME_FAMILY, HOME_GUEST, HOME_SLEEP, HOME_STUDY], top: HOME_LOFT, compact: HOME_STOREY },
  apartment: { lower: [APARTMENT_UNITS_SOFT], middle: [APARTMENT_UNITS_SOFT], top: null, compact: LODGING_STOREY },
  hotel: { lower: [HOTEL_UNITS_SOFT], middle: [HOTEL_UNITS_SOFT], top: null, compact: LODGING_STOREY },
  cantina: { lower: [CANTINA_UPPER], middle: [INN_ROOMS, QUARTERS, CANTINA_UPPER], top: HOME_GUEST, compact: LODGING_STOREY },
  clinic: { lower: [CLINIC_WARD], middle: [CLINIC_DISPENSARY, CLINIC_WARD, TRADE_OFFICE], top: QUARTERS, compact: CLINIC_STOREY },
  shop: { lower: [SHOP_UPPER], middle: [TRADE_OFFICE, WORKS_STORE, HOME_GUEST], top: HOME_FAMILY, compact: WORK_STOREY },
  office: { lower: [OFFICE_FLOOR], middle: [OFFICE_STUDIO, OFFICE_FLOOR, TRADE_OFFICE], top: OFFICE_BOARD, compact: WORK_STOREY },
  academy: { lower: [ACADEMY_STUDY, OFFICE_FLOOR], middle: [OFFICE_STUDIO, ACADEMY_STUDY, CIVIC_ARCHIVE], top: OFFICE_BOARD, compact: WORK_STOREY },
  civic: { lower: [OFFICE_FLOOR, CIVIC_COUNCIL, CIVIC_ARCHIVE], middle: [OFFICE_STUDIO, OFFICE_FLOOR, CIVIC_ARCHIVE], top: OFFICE_BOARD, compact: WORK_STOREY },
  faith: { lower: [FAITH_QUIET], middle: [FAITH_QUIET, QUARTERS, CIVIC_ARCHIVE], top: HOME_GUEST, compact: GENERIC_STOREY },
  guild: { lower: [GUILD_MEETING, GUILD_CRAFT, CIVIC_ARCHIVE, GENERIC_UPPER], middle: [QUARTERS, WORKS_ROOM, GENERIC_UPPER], top: QUARTERS, compact: QUARTERS_STOREY },
  bank: { lower: [OFFICE_FLOOR], middle: [OFFICE_STUDIO, CIVIC_ARCHIVE, OFFICE_FLOOR], top: OFFICE_BOARD, compact: WORK_STOREY },
  theater: { lower: [THEATER_GREEN, GENERIC_UPPER], middle: [WORKS_ROOM, GENERIC_UPPER, THEATER_GREEN], top: OFFICE_STUDIO, compact: GENERIC_STOREY },
  museum: { lower: [MUSEUM_UPPER], middle: [MUSEUM_UPPER, CIVIC_ARCHIVE], top: OFFICE_STUDIO, compact: GENERIC_STOREY },
  salon: { lower: [THEATER_GREEN, GENERIC_UPPER], middle: [TRADE_OFFICE, GENERIC_UPPER, HOME_GUEST], top: HOME_FAMILY, compact: GENERIC_STOREY },
  garage: { lower: [WORKS_STORE], middle: [WORKS_ROOM, WORKS_STORE, TRADE_OFFICE], top: TRADE_OFFICE, compact: WORK_STOREY },
  transit: { lower: [TRANSIT_LOUNGE_WIDE, TRANSIT_OPS_WIDE, OFFICE_FLOOR], middle: [OFFICE_STUDIO, OFFICE_FLOOR, QUARTERS], top: OFFICE_BOARD, compact: WORK_STOREY },
  workshop: { lower: [WORKS_STORE], middle: [WORKS_ROOM, WORKS_STORE, TRADE_OFFICE], top: TRADE_OFFICE, compact: WORK_STOREY },
  mining: { lower: [WORKS_STORE, OFFICE_FLOOR], middle: [WORKS_ROOM, QUARTERS, WORKS_STORE], top: TRADE_OFFICE, compact: WORK_STOREY },
  outpost: { lower: [QUARTERS], middle: [OUTPOST_ARMOURY, QUARTERS], top: QUARTERS, compact: QUARTERS_STOREY },
  lodge: { lower: [CANTINA_UPPER, GENERIC_UPPER], middle: [INN_ROOMS, GENERIC_UPPER, CANTINA_UPPER], top: HOME_LOFT, compact: GENERIC_STOREY },
  shed: { lower: [GENERIC_UPPER], middle: [WORKS_ROOM, GENERIC_UPPER], top: HOME_LOFT, compact: GENERIC_STOREY },
  garden: { lower: [GENERIC_UPPER], middle: [HOME_LOFT, GENERIC_UPPER], top: HOME_LOFT, compact: GENERIC_STOREY },
  generic: { lower: [GENERIC_UPPER], middle: [OFFICE_STUDIO, GENERIC_UPPER, HOME_FAMILY], top: HOME_LOFT, compact: GENERIC_STOREY },
  hive: { lower: [HIVE_GALLERY], middle: [HIVE_GALLERY, WORKS_STORE, TRADE_OFFICE], top: TRADE_OFFICE, compact: WORK_STOREY },
};
/** The archetype's FAMILY: its id less a `.compact`. */
export const storeyFamily = (id: string): string => (id.endsWith('.compact') ? id.slice(0, -'.compact'.length) : id);
export function storeyPlanOf(a: Pick<InteriorArchetype, 'id' | 'legacy'>): StoreyPlan | null {
  return a.legacy ? null : STOREY_PLANS[storeyFamily(a.id)] ?? null;
}
/**
 * THE STOREY PROGRAM (layout ≥ STOREYS_LAYOUT_REVISION) of floor `f` ≥ 1 in a building of `floors`: its role, its
 * program and the compact program a small storey falls to. `start` = the building's own rotation start (any integer —
 * the planner passes its topology seed); a middle storey takes `middle[(start + k) mod n]`, k counting middle storeys
 * from the lowest, so two neighbours are never the same program where the family has two to deal.
 */
export function storeyProgramFor(a: InteriorArchetype, f: number, floors: number, start: number): { role: StoreyRole; program: FloorProgram; compact: FloorProgram } | null {
  const sp = storeyPlanOf(a);
  if (!sp || f < 1 || f >= floors) return null;
  if (f <= sp.lower.length) return { role: 'lower', program: sp.lower[f - 1], compact: sp.compact };
  if (f === floors - 1 && sp.top) return { role: 'top', program: sp.top, compact: sp.compact };
  const n = sp.middle.length;
  if (n === 0) return null;
  // THE BALANCED DEAL: each middle storey takes, of the programs that are not the one under it (where there are two to
  // deal), the one the building has used LEAST so far — counting its lower storeys and its top — ties broken in the
  // rotation the building's own seed starts. A program the ground floors already use comes round last, so a tower is
  // not one program stamped on three storeys of a narrow plate (where a program has a single legal layout).
  const s0 = ((start % n) + n) % n;
  const used = new Map<FloorProgram, number>();
  for (const p of sp.lower.slice(0, Math.min(sp.lower.length, floors - 1))) used.set(p, (used.get(p) ?? 0) + 1);
  if (sp.top && floors - 1 > sp.lower.length) used.set(sp.top, (used.get(sp.top) ?? 0) + 1);
  let below: FloorProgram | undefined = sp.lower[sp.lower.length - 1];
  let pick: FloorProgram = sp.middle[s0];
  for (let g = sp.lower.length + 1; g <= f; g++) {
    let best: FloorProgram | null = null, bestUse = Infinity;
    for (let i = 0; i < n; i++) {
      const p = sp.middle[(s0 + (g - sp.lower.length - 1) + i) % n];
      if (n >= 2 && p === below) continue;
      const u = used.get(p) ?? 0;
      if (u < bestUse) { best = p; bestUse = u; }
    }
    pick = best ?? sp.middle[s0];
    used.set(pick, (used.get(pick) ?? 0) + 1);
    below = pick;
  }
  return { role: 'middle', program: pick, compact: sp.compact };
}
/** Every program the storeys revision can seat (the gate walks them; `kitsNamedBy` never does). */
export function storeyPrograms(): FloorProgram[] {
  const out = new Map<FloorProgram, true>();
  for (const sp of Object.values(STOREY_PLANS)) for (const p of [...sp.lower, ...sp.middle, ...(sp.top ? [sp.top] : []), sp.compact]) out.set(p, true);
  return [...out.keys()];
}

/** The construction tradition a key implies when the caller knows no better: the two settlement packs name theirs;
 *  everything else is `neutral`. Slice C passes the look's resolved tradition instead (interior_style.ts). */
export function defaultTradition(key: string): Tradition {
  const k = key.toLowerCase();
  if (k.includes('civa_')) return 'aurelian';
  if (k.includes('civb_')) return 'borean';
  return 'neutral';
}
